// ============================================================================
// `@torre/work-hours` — contrato compartido del registro de horas trabajadas
// por commits (#528 / #532).
// ============================================================================
// Regla de conteo (#783): las horas que se pagan y facturan son los MINUTOS
// registrados en entradas de tiempo (`work_time_entries`, sin anuladas). Las
// franjas por commit quedan como EVIDENCIA. Un día-persona sin ninguna entrada
// vigente se ESTIMA con bloques de tiempo real (#960, misma regla que la skill
// cc-hours: pausas de más de 30 min no cuentan, 15 min antes del primer
// commit), marcado `estimated: true` / `source: "commits"`. Las secciones de
// abajo describen cómo se calcula esa evidencia.
//
//   • FRANJA     — hora de reloj en America/Mexico_City con al menos un commit
//                  (o evento de soporte). Es evidencia de actividad (mapa de
//                  calor, pico); ya NO vale 1 h: las horas salen de los bloques.
//   • DEDUPLICADO — por SHA: el mismo commit visto en varios repos (forks) o
//                  ramas cuenta una vez. Un rebase genera otro SHA pero conserva
//                  la fecha de autor, así que cae en la misma franja.
//   • AGREGADOS  — heatmap 7×24, series por día/semana/mes, repos y PRs.
//   • REPORTES   — mensaje armado una sola vez y entregado por un canal
//                  intercambiable (email hoy, WhatsApp después).
//
// Paquete puro: sin Firebase, sin red, sin `import.meta.env`.
// ============================================================================

// ---------------------------------------------------------------------------
// CONSTANTES
// ---------------------------------------------------------------------------

/** Zona horaria en la que se cortan las franjas y se programan los reportes. */
export const WORK_HOURS_TIMEZONE = "America/Mexico_City";

/** Commits crudos ingeridos (uno por SHA y empresa). */
export const WORK_COMMITS_COLLECTION = "work_commits";
/** Horas trabajadas: una por usuario + fecha local + hora local. */
export const WORK_HOURS_COLLECTION = "work_hours";
/** Identidad GitHub de cada usuario de la plataforma. */
export const WORK_HOURS_IDENTITIES_COLLECTION = "work_hours_identities";
/** Clientes del reporte semanal y los repos que les pertenecen. */
export const WORK_HOURS_CLIENTS_COLLECTION = "work_hours_clients";
/** Bitácora de corridas del agente nocturno. */
export const WORK_HOURS_RUNS_COLLECTION = "work_hours_runs";
/** Bitácora de reportes entregados (o vistas previas). */
export const WORK_HOURS_DELIVERIES_COLLECTION = "work_hours_deliveries";

/** Quién subió los commits: la skill del dev o el agente nocturno del equipo. */
export const COMMIT_SOURCES = ["dev", "team"] as const;
export type CommitSource = (typeof COMMIT_SOURCES)[number];

/** Canales de entrega de reportes. WhatsApp está registrado pero sin proveedor. */
export const DELIVERY_CHANNELS = ["email", "whatsapp"] as const;
export type DeliveryChannel = (typeof DELIVERY_CHANNELS)[number];

/**
 * Estado de una entrega:
 *   • `sent`           — el proveedor aceptó el mensaje.
 *   • `preview`        — no hay credenciales: se guardó el mensaje para revisarlo.
 *   • `not_configured` — el canal existe pero no tiene proveedor (WhatsApp hoy).
 *   • `failed`         — el proveedor respondió con error.
 */
export const DELIVERY_STATUSES = ["sent", "preview", "not_configured", "failed"] as const;
export type DeliveryStatus = (typeof DELIVERY_STATUSES)[number];

export const REPORT_TYPES = ["daily_admin", "weekly_client"] as const;
export type ReportType = (typeof REPORT_TYPES)[number];

export const RUN_STATUSES = ["running", "succeeded", "failed"] as const;
export type RunStatus = (typeof RUN_STATUSES)[number];

/** Etiquetas de día (lunes = 0) para el heatmap. */
export const WEEKDAY_LABELS_ES = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"] as const;

// ---------------------------------------------------------------------------
// TIPOS
// ---------------------------------------------------------------------------

export interface PullRequestRef {
  /** `owner/repo`. */
  repo: string;
  number: number;
  title?: string | null;
  url?: string | null;
  state?: string | null;
}

/** Commit tal como lo sube la skill o el agente nocturno. */
export interface CommitInput {
  sha: string;
  /** `owner/repo`. */
  repo: string;
  branch?: string | null;
  branches?: string[];
  authorLogin?: string | null;
  authorEmail?: string | null;
  authorName?: string | null;
  /** Fecha de AUTOR en ISO 8601 (no la de committer: un rebase no mueve horas). */
  authoredAt: string;
  committedAt?: string | null;
  /** Primera línea del mensaje. */
  message?: string | null;
  url?: string | null;
  pullRequests?: PullRequestRef[];
}

/** Franja local de un instante. `weekday`: lunes = 0 … domingo = 6. */
export interface LocalSlot {
  date: string;
  hour: number;
  weekday: number;
}

/** Commit persistido (uno por SHA dentro de la empresa). */
export interface WorkCommit extends LocalSlot {
  id: string;
  company_id: string;
  sha: string;
  repos: string[];
  branches: string[];
  authorLogin: string | null;
  authorEmail: string | null;
  authorName: string | null;
  authoredAt: string;
  message: string | null;
  url: string | null;
  pullRequests: PullRequestRef[];
  /** Usuario de la plataforma resuelto por su identidad GitHub; `null` = sin usuario. */
  userId: string | null;
  userName: string | null;
  source: CommitSource;
}

/** Una hora trabajada: existe solo si hay al menos un commit que la respalde. */
export interface HourBucket extends LocalSlot {
  id: string;
  userId: string;
  userName: string | null;
  commitShas: string[];
  commitCount: number;
  repos: string[];
  /** Claves `owner/repo#123`. */
  pullRequests: string[];
  /**
   * Información mínima (título y dirección) de los PRs de `pullRequests`, copiada
   * en la franja durante la ingesta para que el dashboard NO tenga que leer miles
   * de commits solo para poner títulos. Opcional: las franjas escritas antes de
   * este campo no lo tienen y se resuelven leyendo sus commits (respaldo).
   */
  pullRequestRefs?: PullRequestRef[];
}

/** Identidad GitHub de un usuario de la plataforma. */
export interface WorkHoursIdentity {
  userId: string;
  userName?: string | null;
  email?: string | null;
  githubLogin?: string | null;
  gitEmails?: string[];
  /** Colaborador del módulo Equipo al que alimenta la asistencia (#536). */
  teamMemberId?: string | null;
  /** Pre-vínculo (#841): correo con el que la identidad se reclamará sola. */
  pendingEmail?: string | null;
  /** `false` = contratista externo: sus horas no se registran (#841). */
  trackHours?: boolean;
  exclusionReason?: string | null;
  excludedAt?: string | null;
  excludedBy?: string | null;
  /** Id sintético del que se reclamó esta identidad (#841). */
  claimedFrom?: string | null;
  claimedAt?: string | null;
  claimedBy?: string | null;
  /** Vinculada con el inicio de sesión en GitHub (#842). */
  githubVerifiedAt?: string | null;
  githubId?: string | null;
}

export interface WorkHoursClient {
  id: string;
  name: string;
  emails: string[];
  /** `owner/repo` que pertenecen al cliente. */
  repos: string[];
  active?: boolean;
}

export interface UserHours {
  userId: string;
  userName: string | null;
  hours: number;
  days: number;
  commits: number;
  /** Primera y última hora local trabajada en el rango. */
  firstHour: number | null;
  lastHour: number | null;
  /** Minutos que cuentan (entradas + estimado por bloques). Solo en `summarizeRecordedHours`. */
  minutes?: number;
  /** Horas de entradas de tiempo registradas. Solo en `summarizeRecordedHours`. */
  recordedHours?: number;
  /** Horas estimadas por commits (días sin entradas). Solo en `summarizeRecordedHours`. */
  estimatedHours?: number;
  /** Días-persona estimados. Solo en `summarizeRecordedHours`. */
  estimatedDays?: number;
  /** Minutos fuera de asignación (#843), incluidos en `minutes`. Solo en `summarizeRecordedHours`. */
  outOfAllocationMinutes?: number;
}

export interface PullRequestHours {
  key: string;
  repo: string;
  number: number;
  title: string | null;
  url: string | null;
  /** Horas atribuidas: una franja con varios PRs se reparte en partes iguales. */
  hours: number;
  commits: number;
  users: string[];
}

export interface WorkHoursSummary {
  from: string | null;
  to: string | null;
  totalHours: number;
  totalCommits: number;
  activeUsers: number;
  byUser: UserHours[];
  byDay: { date: string; hours: number }[];
  /** `heatmap[weekday][hour]` = horas trabajadas en esa celda (lunes = 0). */
  heatmap: number[][];
  peak: { weekday: number; hour: number; hours: number } | null;
  weekly: { weekStart: string; hours: number }[];
  monthly: { month: string; hours: number }[];
  /** Horas por repo: una franja que tocó varios repos se reparte en partes iguales entre TODOS ellos. */
  byRepo: { repo: string; hours: number }[];
  pullRequests: PullRequestHours[];
  /** Horas cuyas franjas no tocan ningún PR. */
  hoursWithoutPullRequest: number;
}

export interface ReportMessage {
  subject: string;
  text: string;
  html: string;
  /** Versión corta para canales de mensajería (WhatsApp). */
  shortText: string;
}

// ---------------------------------------------------------------------------
// FECHAS LOCALES
// ---------------------------------------------------------------------------

const WEEKDAY_INDEX: Record<string, number> = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };
const formatters = new Map<string, Intl.DateTimeFormat>();

function formatterFor(timeZone: string): Intl.DateTimeFormat {
  let f = formatters.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      hourCycle: "h23",
      weekday: "short",
    } as Intl.DateTimeFormatOptions);
    formatters.set(timeZone, f);
  }
  return f;
}

/** Fecha, hora y día de la semana locales de un instante ISO. `null` si no es válido. */
export function localSlot(iso: string | null | undefined, timeZone = WORK_HOURS_TIMEZONE): LocalSlot | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const parts: Record<string, string> = {};
  for (const p of formatterFor(timeZone).formatToParts(d)) parts[p.type] = p.value;
  const hour = Number(parts.hour) % 24;
  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    hour,
    weekday: WEEKDAY_INDEX[parts.weekday] ?? 0,
  };
}

/** Suma días a una fecha `YYYY-MM-DD`. */
export function addDays(date: string, days: number): string {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Lunes de la semana de una fecha `YYYY-MM-DD`. */
export function weekStartOf(date: string): string {
  const d = new Date(`${date}T12:00:00Z`);
  const weekday = (d.getUTCDay() + 6) % 7;
  return addDays(date, -weekday);
}

/** Día de la semana (lunes = 0) de una fecha `YYYY-MM-DD`. */
export function weekdayOf(date: string): number {
  return (new Date(`${date}T12:00:00Z`).getUTCDay() + 6) % 7;
}

// ---------------------------------------------------------------------------
// IDENTIDAD Y DEDUPLICADO
// ---------------------------------------------------------------------------

export const normalizeLogin = (login: string | null | undefined): string =>
  String(login ?? "").trim().toLowerCase();

export const normalizeEmail = (email: string | null | undefined): string =>
  String(email ?? "").trim().toLowerCase();

/**
 * Resuelve el usuario de la plataforma autor de un commit: primero por usuario
 * de GitHub, luego por email de git (los registrados o el de la plataforma).
 */
export function matchIdentity<T extends WorkHoursIdentity>(
  commit: Pick<CommitInput, "authorLogin" | "authorEmail">,
  identities: T[],
): T | null {
  const login = normalizeLogin(commit.authorLogin);
  if (login) {
    const byLogin = identities.find((i) => normalizeLogin(i.githubLogin) === login);
    if (byLogin) return byLogin;
  }
  const email = normalizeEmail(commit.authorEmail);
  if (email) {
    const byEmail = identities.find(
      (i) => (i.gitEmails ?? []).some((e) => normalizeEmail(e) === email) || normalizeEmail(i.email) === email,
    );
    if (byEmail) return byEmail;
  }
  return null;
}

const uniq = <T>(list: T[]): T[] => [...new Set(list)];

// ---------------------------------------------------------------------------
// COINCIDENCIA DE REPOS (con comodín de organización, #841)
// ---------------------------------------------------------------------------

/**
 * ¿El patrón de repo cubre este repo? Un patrón es `owner/repo` (exacto) o
 * `owner/*` (cualquier repo de esa organización o usuario). GitHub no distingue
 * mayúsculas en owner ni en nombre, así que la comparación tampoco.
 * `Coding-Cloud-Online/*` cubre `coding-cloud-online/NUVEX`, pero NO
 * `Coding-Cloud-Online-Labs/x` ni `otro/Coding-Cloud-Online`.
 */
export function repoMatches(pattern: string | null | undefined, repo: string | null | undefined): boolean {
  const p = String(pattern ?? "").trim().toLowerCase();
  const r = String(repo ?? "").trim().toLowerCase();
  if (!p || !r) return false;
  if (p.endsWith("/*")) {
    const owner = p.slice(0, -2);
    return Boolean(owner) && !owner.includes("/") && r.startsWith(`${owner}/`) && r.length > owner.length + 1;
  }
  return p === r;
}

/** ¿Algún patrón de la lista cubre el repo? */
export const repoMatchesAny = (patterns: (string | null | undefined)[] | null | undefined, repo: string | null | undefined): boolean =>
  (patterns ?? []).some((p) => repoMatches(p, repo));

/** Clave estable de un PR. */
export const prKey = (pr: Pick<PullRequestRef, "repo" | "number">): string => `${pr.repo}#${pr.number}`;

/**
 * Deduplica por SHA uniendo repos, ramas y PRs. Un commit que aparece en tres
 * forks (CC-CONSOLE, NUVEX, WAYAK) es UN commit.
 */
export function mergeCommits(commits: CommitInput[]): (CommitInput & { repos: string[] })[] {
  const bySha = new Map<string, CommitInput & { repos: string[] }>();
  for (const c of commits) {
    if (!c || !c.sha) continue;
    const branches = uniq([...(c.branches ?? []), ...(c.branch ? [c.branch] : [])]);
    const existing = bySha.get(c.sha);
    if (!existing) {
      bySha.set(c.sha, { ...c, repos: [c.repo], branches, pullRequests: [...(c.pullRequests ?? [])] });
      continue;
    }
    existing.repos = uniq([...existing.repos, c.repo]);
    existing.branches = uniq([...(existing.branches ?? []), ...branches]);
    const prs = new Map((existing.pullRequests ?? []).map((p) => [prKey(p), p]));
    for (const p of c.pullRequests ?? []) if (!prs.has(prKey(p))) prs.set(prKey(p), p);
    existing.pullRequests = [...prs.values()];
    existing.authorLogin = existing.authorLogin || c.authorLogin;
    existing.authorEmail = existing.authorEmail || c.authorEmail;
    existing.message = existing.message || c.message;
  }
  return [...bySha.values()];
}

// ---------------------------------------------------------------------------
// FRANJAS (la regla de negocio)
// ---------------------------------------------------------------------------

export const hourBucketId = (userId: string, date: string, hour: number): string =>
  `${userId}_${date}_${String(hour).padStart(2, "0")}`;

export interface BucketableCommit {
  sha: string;
  userId: string | null;
  userName?: string | null;
  authoredAt: string;
  repos?: string[];
  repo?: string;
  pullRequests?: PullRequestRef[];
}

/**
 * Convierte commits en horas trabajadas. Cada franja (usuario, fecha, hora local)
 * con al menos un commit es exactamente 1 h. Los commits sin usuario no generan
 * horas: se reportan aparte para que un admin los ligue.
 */
export function buildHourBuckets(commits: BucketableCommit[], timeZone = WORK_HOURS_TIMEZONE): HourBucket[] {
  const buckets = new Map<string, HourBucket>();
  const refs = new Map<string, Map<string, PullRequestRef>>();
  const seen = new Set<string>();
  for (const c of commits) {
    if (!c.userId || !c.sha) continue;
    const slot = localSlot(c.authoredAt, timeZone);
    if (!slot) continue;
    const id = hourBucketId(c.userId, slot.date, slot.hour);
    let b = buckets.get(id);
    if (!b) {
      b = { id, userId: c.userId, userName: c.userName ?? null, ...slot, commitShas: [], commitCount: 0, repos: [], pullRequests: [], pullRequestRefs: [] };
      buckets.set(id, b);
      refs.set(id, new Map());
    }
    const dedupeKey = `${id}|${c.sha}`;
    if (seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);
    b.commitShas.push(c.sha);
    b.commitCount += 1;
    b.repos = uniq([...b.repos, ...(c.repos ?? (c.repo ? [c.repo] : []))]);
    b.pullRequests = uniq([...b.pullRequests, ...(c.pullRequests ?? []).map(prKey)]);
    // Título y dirección del PR viajan con la franja (nunca `undefined`: Firestore
    // lo rechaza). El último commit que aporte datos gana, igual que en la ingesta.
    const bucketRefs = refs.get(id)!;
    for (const p of c.pullRequests ?? []) {
      const key = prKey(p);
      const prev = bucketRefs.get(key);
      bucketRefs.set(key, {
        repo: p.repo,
        number: p.number,
        title: p.title ?? prev?.title ?? null,
        url: p.url ?? prev?.url ?? null,
      });
    }
  }
  for (const [id, b] of buckets) b.pullRequestRefs = [...(refs.get(id) ?? new Map<string, PullRequestRef>()).values()];
  return [...buckets.values()].sort((a, b) => (a.date === b.date ? a.hour - b.hour : a.date < b.date ? -1 : 1));
}

/**
 * Metadatos de PR tomados de las propias franjas (`pullRequestRefs`). Devuelve
 * también las franjas que citan un PR del que nadie trajo información: solo para
 * esas hace falta leer commits (respaldo de franjas anteriores a este campo).
 */
export function pullRequestInfoFromBuckets(buckets: HourBucket[]): {
  info: Record<string, PullRequestRef>;
  bucketsMissingInfo: HourBucket[];
} {
  const info: Record<string, PullRequestRef> = {};
  for (const b of buckets) {
    for (const p of b.pullRequestRefs ?? []) {
      const key = prKey(p);
      const prev = info[key];
      if (!prev || (!prev.title && p.title) || (!prev.url && p.url)) {
        info[key] = { ...prev, ...p, title: p.title ?? prev?.title ?? null, url: p.url ?? prev?.url ?? null };
      }
    }
  }
  const bucketsMissingInfo = buckets.filter((b) => b.pullRequests.some((k) => !info[k] || (!info[k].title && !info[k].url)));
  return { info, bucketsMissingInfo };
}

// ---------------------------------------------------------------------------
// AGREGADOS
// ---------------------------------------------------------------------------

const round2 = (n: number): number => Math.round(n * 100) / 100;
const emptyHeatmap = (): number[][] => Array.from({ length: 7 }, () => new Array<number>(24).fill(0));

export interface SummarizeOptions {
  from?: string | null;
  to?: string | null;
  /**
   * Solo franjas que tocan alguno de estos repos (reporte a clientes). El filtro
   * decide qué se muestra, no cuánto vale: el reparto de la hora entre repos y
   * PRs se sigue calculando sobre TODO lo que tocó la franja.
   */
  repos?: string[] | null;
  userId?: string | null;
  /** Metadatos de PR (título, url) indexados por `owner/repo#n`. */
  pullRequestInfo?: Record<string, PullRequestRef>;
}

/** Filtra franjas por rango de fechas, usuario y repos. */
export function filterBuckets(buckets: HourBucket[], opts: SummarizeOptions = {}): HourBucket[] {
  const repos = opts.repos && opts.repos.length ? opts.repos : null;
  return buckets.filter(
    (b) =>
      (!opts.from || b.date >= opts.from) &&
      (!opts.to || b.date <= opts.to) &&
      (!opts.userId || b.userId === opts.userId) &&
      (!repos || b.repos.some((r) => repoMatchesAny(repos, r))),
  );
}

/** Resumen para el dashboard y los reportes. */
export function summarizeWorkHours(allBuckets: HourBucket[], opts: SummarizeOptions = {}): WorkHoursSummary {
  const buckets = filterBuckets(allBuckets, opts);
  const repoPatterns = opts.repos && opts.repos.length ? opts.repos : null;
  const repoFilter = repoPatterns ? { has: (repo: string) => repoMatchesAny(repoPatterns, repo) } : null;
  const heatmap = emptyHeatmap();
  const users = new Map<string, UserHours & { dates: Set<string> }>();
  const byDay = new Map<string, number>();
  const weekly = new Map<string, number>();
  const monthly = new Map<string, number>();
  const byRepo = new Map<string, number>();
  const prs = new Map<string, PullRequestHours & { userSet: Set<string> }>();
  let totalCommits = 0;
  let withoutPr = 0;

  for (const b of buckets) {
    totalCommits += b.commitCount;
    heatmap[b.weekday][b.hour] += 1;
    byDay.set(b.date, (byDay.get(b.date) ?? 0) + 1);
    weekly.set(weekStartOf(b.date), (weekly.get(weekStartOf(b.date)) ?? 0) + 1);
    monthly.set(b.date.slice(0, 7), (monthly.get(b.date.slice(0, 7)) ?? 0) + 1);

    let u = users.get(b.userId);
    if (!u) {
      u = { userId: b.userId, userName: b.userName, hours: 0, days: 0, commits: 0, firstHour: null, lastHour: null, dates: new Set() };
      users.set(b.userId, u);
    }
    u.hours += 1;
    u.commits += b.commitCount;
    u.dates.add(b.date);
    u.firstHour = u.firstHour === null ? b.hour : Math.min(u.firstHour, b.hour);
    u.lastHour = u.lastHour === null ? b.hour : Math.max(u.lastHour, b.hour);

    // El reparto de la hora se calcula SIEMPRE sobre todo lo que tocó la franja,
    // no sobre la lista ya filtrada: si no, una hora que tocó repos de dos
    // clientes se facturaría completa a cada uno (mismo criterio que
    // `computePlannedVsActual`). El filtro decide QUÉ se muestra, nunca cuánto vale.
    const repoShare = 1 / Math.max(1, b.repos.length);
    const repos = repoFilter ? b.repos.filter((r) => repoFilter.has(r)) : b.repos;
    for (const r of repos) byRepo.set(r, (byRepo.get(r) ?? 0) + repoShare);

    const prShare = 1 / Math.max(1, b.pullRequests.length);
    const bucketPrs = repoFilter ? b.pullRequests.filter((k) => repoFilter.has(k.split("#")[0])) : b.pullRequests;
    if (bucketPrs.length === 0) withoutPr += 1;
    for (const key of bucketPrs) {
      const [repo, num] = [key.slice(0, key.lastIndexOf("#")), Number(key.slice(key.lastIndexOf("#") + 1))];
      let p = prs.get(key);
      if (!p) {
        const info = opts.pullRequestInfo?.[key];
        p = { key, repo, number: num, title: info?.title ?? null, url: info?.url ?? null, hours: 0, commits: 0, users: [], userSet: new Set() };
        prs.set(key, p);
      }
      p.hours += prShare;
      p.commits += b.commitCount;
      p.userSet.add(b.userName ?? b.userId);
    }
  }

  let peak: WorkHoursSummary["peak"] = null;
  heatmap.forEach((row, weekday) =>
    row.forEach((hours, hour) => {
      if (hours > 0 && (!peak || hours > peak.hours)) peak = { weekday, hour, hours };
    }),
  );

  const sortByKey = <V>(m: Map<string, V>) => [...m.entries()].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));

  return {
    from: opts.from ?? null,
    to: opts.to ?? null,
    totalHours: buckets.length,
    totalCommits,
    activeUsers: users.size,
    byUser: [...users.values()]
      .map(({ dates, ...u }) => ({ ...u, days: dates.size }))
      .sort((a, b) => b.hours - a.hours),
    byDay: sortByKey(byDay).map(([date, hours]) => ({ date, hours })),
    heatmap,
    peak,
    weekly: sortByKey(weekly).map(([weekStart, hours]) => ({ weekStart, hours })),
    monthly: sortByKey(monthly).map(([month, hours]) => ({ month, hours })),
    byRepo: [...byRepo.entries()].map(([repo, hours]) => ({ repo, hours: round2(hours) })).sort((a, b) => b.hours - a.hours),
    pullRequests: [...prs.values()]
      .map(({ userSet, ...p }) => ({ ...p, hours: round2(p.hours), users: [...userSet].sort() }))
      .sort((a, b) => b.hours - a.hours),
    hoursWithoutPullRequest: withoutPr,
  };
}

// ---------------------------------------------------------------------------
// PLANEADO VS REAL POR PROYECTO (#573)
// ---------------------------------------------------------------------------

export interface AllocationInput {
  projectId: string;
  projectName: string;
  percentage: number;
  repos?: string[];
  startDate?: string | null;
  endDate?: string | null;
}

export interface PlannedVsActual {
  projectId: string;
  projectName: string;
  percentage: number;
  repos: string[];
  /** Horas esperadas en el rango: capacidad semanal × % × días hábiles vigentes / 5. */
  plannedHours: number;
  /**
   * Horas reales: minutos registrados en entradas con ese `projectId` (#783) más,
   * en los días sin entradas, los minutos de los bloques de tiempo real (#960)
   * repartidos a los repos del proyecto por densidad de commits.
   */
  actualHours: number;
  /** Parte de `actualHours` que viene de entradas de tiempo. */
  recordedHours: number;
  /** Parte de `actualHours` estimada por commits (días sin entradas). */
  estimatedHours: number;
  /** real / planeado (`null` si no hay planeado). */
  ratio: number | null;
  /** Horas de entradas que esperan aprobación del tech lead (#1281): NO están en `actualHours`. */
  pendingHours: number;
}

/**
 * Línea «Fuera de asignación» de plan vs real (#843): horas que SÍ se trabajaron
 * y cuentan, pero en proyectos que no están en la asignación de la persona (o
 * en repos sin proyecto). Se separan por proyecto real con sus motivos.
 */
export interface OutOfAllocationLine {
  label: typeof OUT_OF_ALLOCATION_LABEL;
  hours: number;
  recordedHours: number;
  estimatedHours: number;
  projects: {
    /** Proyecto real (o `repo:owner/repo` si el repo de una franja estimada no pertenece a ninguno). */
    projectId: string;
    projectLabel: string;
    hours: number;
    recordedHours: number;
    estimatedHours: number;
    /** Motivo del sistema + justificación de la persona (o «sin justificación»). */
    reasons: string[];
  }[];
}

type OutAcc = Map<string, { projectId: string; projectLabel: string; recorded: number; estimated: number; reasons: Set<string> }>;

/** Suma horas a la línea fuera de asignación (una entrada o la parte de una franja). */
function addOutOfAllocation(acc: OutAcc, key: string, label: string, hours: number, kind: "recorded" | "estimated", reasons: string[]): void {
  const prev = acc.get(key) ?? { projectId: key, projectLabel: label, recorded: 0, estimated: 0, reasons: new Set<string>() };
  prev[kind] += hours;
  for (const r of reasons) if (r) prev.reasons.add(r);
  acc.set(key, prev);
}

/** Prefijo del «proyecto» de un repo fuera de asignación cuya franja no trae su proyecto real. */
export const REPO_PROJECT_PREFIX = "repo:";

/** Parte fuera de asignación de un repo de un día estimado, con su proyecto real si lo hay. */
function outOfAllocationOfRepo(acc: OutAcc, found: OutOfAllocationRepo | undefined, repo: string, share: number): void {
  const key = found?.projectId || (repo ? `${REPO_PROJECT_PREFIX}${repo}` : "sin-repo");
  const label = found?.projectLabel || repo || "Sin repo (soporte)";
  addOutOfAllocation(acc, key, label, share, "estimated", found ? [flagText(found)] : []);
}

function outOfAllocationLine(acc: OutAcc): OutOfAllocationLine {
  const projects = [...acc.values()]
    .map((p) => ({
      projectId: p.projectId,
      projectLabel: p.projectLabel,
      hours: round2(p.recorded + p.estimated),
      recordedHours: round2(p.recorded),
      estimatedHours: round2(p.estimated),
      reasons: [...p.reasons],
    }))
    .sort((a, b) => b.hours - a.hours || a.projectId.localeCompare(b.projectId));
  const recorded = [...acc.values()].reduce((n, p) => n + p.recorded, 0);
  const estimated = [...acc.values()].reduce((n, p) => n + p.estimated, 0);
  return { label: OUT_OF_ALLOCATION_LABEL, hours: round2(recorded + estimated), recordedHours: round2(recorded), estimatedHours: round2(estimated), projects };
}

/** Días hábiles (lunes a viernes) entre dos fechas, inclusive. */
export function businessDays(from: string, to: string): number {
  let n = 0;
  for (let d = from; d <= to; d = addDays(d, 1)) if (weekdayOf(d) < 5) n += 1;
  return n;
}

/** Id de proyecto de una asignación, con la misma normalización que las entradas. */
export const allocationProjectId = (a: Pick<AllocationInput, "projectId" | "projectName">): string =>
  normalizeProjectId(a.projectId || a.projectName);

/**
 * Compara lo planeado (capacidad semanal × % asignado) con las horas reales.
 *
 * Real (#783): las entradas de tiempo vigentes cuentan por su `projectId`
 * (minutos / 60). Un día-persona SIN entradas se estima con bloques de tiempo
 * real (#960): los minutos de cada repo van a los proyectos que lo tienen. Las
 * franjas de un día que sí tiene entradas son solo evidencia y no suman.
 *
 * `entries` omitido ⇒ comportamiento previo (todo es estimado por commits).
 * Entradas de proyectos sin asignación y horas en repos sin proyecto van a
 * `unassignedHours`.
 */
export function computePlannedVsActual(
  buckets: HourBucket[],
  allocations: AllocationInput[],
  weeklyCapacityHours: number | null | undefined,
  from: string,
  to: string,
  entries?: CountableTimeEntry[] | null,
  estimateOpts: Pick<ResolveDaysOptions, "events" | "estimate"> = {},
): {
  projects: PlannedVsActual[];
  unassignedHours: number;
  /**
   * Línea «Fuera de asignación» (#843): el detalle de `unassignedHours` por
   * proyecto real y con motivo. Esas horas ya están en `totalHours`.
   */
  outOfAllocation: OutOfAllocationLine;
  totalHours: number;
  capacityHours: number;
  recordedHours: number;
  estimatedHours: number;
  /** Días-persona (`userId|fecha`) que se estimaron por bloques. */
  estimatedDays: string[];
  estimateMethod: EstimateMethod;
  /**
   * Horas de entradas que esperan aprobación del tech lead (#1281). NO están en
   * `totalHours`: se muestran aparte como «Horas por aprobar». Incluye las de
   * proyectos sin asignación (que no caen en ninguna fila de `projects`).
   */
  pendingHours: number;
} {
  const capacity = Number(weeklyCapacityHours) > 0 ? Number(weeklyCapacityHours) : 40;
  const inRange = (entries ?? []).filter((e) => e.date >= from && e.date <= to);
  const counted = inRange.filter((e) => isCountedEntry(e));
  const daysWithEntries = new Set(counted.map((e) => `${e.userId}|${e.date}`));
  const estimatedBuckets = buckets.filter((b) => b.date >= from && b.date <= to && !daysWithEntries.has(`${b.userId}|${b.date}`));
  // #960: minutos reales por repo de cada día estimado.
  const estimates = estimateBucketDays(estimatedBuckets, estimateOpts.events ?? [], estimateOpts.estimate ?? {});
  const outRepos = outOfAllocationReposByDay(estimatedBuckets);

  const recorded = new Map<string, number>();
  const estimated = new Map<string, number>();
  let unassigned = 0;
  const outAcc: OutAcc = new Map();

  const byAllocationId = new Map<string, AllocationInput[]>();
  for (const a of allocations) {
    for (const id of new Set([String(a.projectId ?? ""), allocationProjectId(a)])) {
      if (!id) continue;
      byAllocationId.set(id, [...(byAllocationId.get(id) ?? []), a]);
    }
  }
  // #1281: pendientes de aprobación, aparte. Un día con SOLO entradas pendientes
  // sigue estimándose por commits (no tiene entradas que cuenten). Las de un
  // proyecto sin asignación suman al total pero no caen en ninguna fila.
  const pending = new Map<string, number>();
  let pendingTotal = 0;
  for (const e of inRange) {
    if (!isPendingEntry(e)) continue;
    const hours = (Number(e.minutes) || 0) / 60;
    pendingTotal += hours;
    const owners = byAllocationId.get(e.projectId) ?? byAllocationId.get(normalizeProjectId(e.projectId)) ?? [];
    for (const a of owners) pending.set(a.projectId, (pending.get(a.projectId) ?? 0) + hours / owners.length);
  }
  let recordedTotal = 0;
  for (const e of counted) {
    const hours = (Number(e.minutes) || 0) / 60;
    recordedTotal += hours;
    const owners = byAllocationId.get(e.projectId) ?? byAllocationId.get(normalizeProjectId(e.projectId)) ?? [];
    if (!owners.length) {
      unassigned += hours;
      addOutOfAllocation(outAcc, e.projectId, e.projectLabel || e.projectId, hours, "recorded", (e.flags ?? []).map(flagText));
      continue;
    }
    // El mismo proyecto no puede estar dos veces, pero por si acaso se reparte.
    for (const a of owners) recorded.set(a.projectId, (recorded.get(a.projectId) ?? 0) + hours / owners.length);
  }

  let estimatedMinutes = 0;
  const estimatedDays: string[] = [];
  for (const [key, est] of estimates) {
    if (est.minutes <= 0) continue;
    estimatedMinutes += est.minutes;
    estimatedDays.push(key);
    const date = key.slice(key.lastIndexOf("|") + 1);
    for (const [repo, minutes] of Object.entries(est.byRepo)) {
      const share = minutes / 60;
      const owners = allocations.filter(
        (a) => repoMatchesAny(a.repos, repo) && (!a.startDate || a.startDate <= date) && (!a.endDate || a.endDate >= date),
      );
      if (!owners.length) {
        unassigned += share;
        outOfAllocationOfRepo(outAcc, outRepos.get(key)?.get(repo), repo, share);
        continue;
      }
      for (const a of owners) estimated.set(a.projectId, (estimated.get(a.projectId) ?? 0) + share / owners.length);
    }
  }
  const projects = allocations.map((a) => {
    const start = a.startDate && a.startDate > from ? a.startDate : from;
    const end = a.endDate && a.endDate < to ? a.endDate : to;
    const days = start <= end ? businessDays(start, end) : 0;
    const plannedHours = round2(((capacity * (Number(a.percentage) || 0)) / 100) * (days / 5));
    const recordedHours = round2(recorded.get(a.projectId) ?? 0);
    const estimatedHours = round2(estimated.get(a.projectId) ?? 0);
    const actualHours = round2((recorded.get(a.projectId) ?? 0) + (estimated.get(a.projectId) ?? 0));
    return {
      projectId: a.projectId,
      projectName: a.projectName,
      percentage: Number(a.percentage) || 0,
      repos: a.repos ?? [],
      plannedHours,
      actualHours,
      recordedHours,
      estimatedHours,
      ratio: plannedHours > 0 ? round2(actualHours / plannedHours) : null,
      pendingHours: round2(pending.get(a.projectId) ?? 0),
    };
  });
  return {
    projects,
    unassignedHours: round2(unassigned),
    outOfAllocation: outOfAllocationLine(outAcc),
    totalHours: round2(recordedTotal + estimatedMinutes / 60),
    capacityHours: round2(capacity * (businessDays(from, to) / 5)),
    recordedHours: round2(recordedTotal),
    estimatedHours: round2(estimatedMinutes / 60),
    estimatedDays: estimatedDays.sort(),
    estimateMethod: ESTIMATE_METHOD_BLOCKS,
    pendingHours: round2(pendingTotal),
  };
}

// ---------------------------------------------------------------------------
// ESPERADO A LA FECHA VS REAL (#969)
// ---------------------------------------------------------------------------
// Cuántas horas DEBERÍA llevar cada persona hasta hoy según su capacidad y sus
// asignaciones, contra las que cuentan (entradas + estimado por bloques, #960):
//
//   • Esperado = capacidad semanal / 5 (40 h → 8 h) por día hábil (lun–vie)
//     transcurrido en [max(inicio del rango, inicio de la asignación),
//     min(fin del rango, fin de la asignación, HOY en America/Mexico_City)]
//     × porcentaje de cada asignación.
//   • Sólo colaboradores activos con asignaciones. Los contratistas
//     (`trackHours: false` o colaborador con `contractorId`) y los inactivos no
//     cuentan.
//   • Quien no tiene identidad de GitHub que se mida (p. ej. ventas) sale
//     aparte en `nonTrackedMembers` y no entra en el esperado del equipo.
//   • Real = lo mismo que plan vs real (`computePlannedVsActual`) cortado a hoy.
//     Las horas fuera de asignación (#843) cuentan en el real de la persona y
//     se muestran como filas con esperado 0.
// ---------------------------------------------------------------------------

/** Días hábiles por semana con los que se reparte la capacidad semanal. */
export const EXPECTED_WORKDAYS_PER_WEEK = 5;

/** Colaborador del módulo Equipo, con lo mínimo que necesita el esperado. */
export interface ExpectedMemberInput {
  id: string;
  name?: string | null;
  lastName?: string | null;
  status?: string | null;
  /** Colaborador dado de alta como contratista (#841). */
  contractorId?: string | null;
  weeklyCapacityHours?: number | null;
  projectAllocations?: AllocationInput[] | null;
  // --- Pago (sólo lo lee `computeExpectedCost`) ---
  /** Cómo se calcula su sueldo base (módulo Equipo, #530). */
  payMode?: string | null;
  hourlyRate?: number | null;
  dailyRate?: number | null;
  /** Sueldo fijo POR PERIODO de nómina (quincena por omisión). */
  fixedSalary?: number | null;
  /** Periodicidad del sueldo fijo; sin ella, quincenal (`semimonthly`). */
  payFrequency?: string | null;
  /** Moneda en la que se le paga (ISO 4217, #741). */
  currency?: string | null;
}

export type NonTrackedReason = "no_github";

export interface ExpectedProjectLine {
  projectId: string;
  projectName: string;
  percentage: number;
  /** Días hábiles de la asignación que caen en el rango efectivo. */
  businessDays: number;
  expectedHours: number;
  actualHours: number;
  recordedHours: number;
  estimatedHours: number;
  /** real − esperado (negativo = faltante). */
  gapHours: number;
  /** Horas que esperan aprobación del tech lead (#1281): NO están en `actualHours`. */
  pendingHours: number;
  /** real + por aprobar − esperado: la diferencia si TODO lo pendiente se aprobara. */
  gapWithPendingHours: number;
  /** Fila de horas fuera de asignación (#843): esperado 0. */
  outOfAllocation: boolean;
  /**
   * La fila fuera de asignación llegó por un repo sin proyecto marcado y se
   * atribuyó al proyecto del equipo dueño del repo (#1349). La vista por cliente
   * NO la cuenta: antes de la atribución esas horas no entraban al cliente.
   */
  reattributedFromRepo?: boolean;
  reasons?: string[];
}

export interface ExpectedPersonLine {
  userId: string;
  teamMemberId: string;
  name: string;
  weeklyCapacityHours: number;
  hoursPerDay: number;
  expectedHours: number;
  actualHours: number;
  recordedHours: number;
  estimatedHours: number;
  /** Parte de `actualHours` fuera de asignación (#843). */
  outOfAllocationHours: number;
  gapHours: number;
  /** Horas por aprobar (#1281), fuera de `actualHours`. */
  pendingHours: number;
  /** real + por aprobar − esperado. */
  gapWithPendingHours: number;
  byProject: ExpectedProjectLine[];
}

export interface ExpectedRule {
  /** Horas por día hábil con la capacidad por omisión (40 h → 8 h). */
  hoursPerDay: number;
  /** Días hábiles del rango efectivo. */
  businessDays: number;
  from: string;
  /** Fin efectivo: min(fin del rango, hoy). */
  to: string;
  today: string;
  timeZone: string;
}

export interface ExpectedToDate {
  teamExpectedHours: number;
  teamActualHours: number;
  gapHours: number;
  /** Σ horas por aprobar del equipo (#1281): fuera de `teamActualHours`. */
  teamPendingHours: number;
  /** real + por aprobar − esperado: la diferencia si todo lo pendiente se aprobara. */
  gapWithPendingHours: number;
  byPerson: ExpectedPersonLine[];
  nonTrackedMembers: { teamMemberId: string; name: string; reason: NonTrackedReason }[];
  rule: ExpectedRule;
  /** Las mismas horas, por squad y por proyecto (`computeExpectedBreakdown`). */
  breakdown?: ExpectedBreakdown;
  /** Costo por moneda (`computeExpectedCost`). Sólo para administradores: lleva sueldos. */
  cost?: ExpectedCost;
}

export interface ExpectedToDateInput {
  from: string;
  to: string;
  /** Hoy en America/Mexico_City (`YYYY-MM-DD`). */
  today: string;
  members: ExpectedMemberInput[];
  identities: WorkHoursIdentity[];
  buckets: HourBucket[];
  entries?: CountableTimeEntry[] | null;
  events?: EstimateEvent[] | null;
  estimate?: BlockEstimateOptions;
  /** Limitar a una persona (vista de un dev o tarjeta de asignaciones). */
  userId?: string | null;
  /**
   * TODOS los colaboradores de la empresa, para saber de qué proyecto es un repo
   * (#1349) aunque `members` venga recortado a un squad o a una persona. Sin
   * esto el mismo repo caería en su proyecto en la vista del equipo y con su
   * nombre de repo en la de un squad. Por omisión, `members`.
   */
  projectMembers?: ExpectedMemberInput[] | null;
}

const INACTIVE_MEMBER_STATUSES = new Set(["inactive", "offboarded"]);

/** Nombre visible de un colaborador. */
const memberName = (m: ExpectedMemberInput): string => [m.name, m.lastName].filter(Boolean).join(" ").trim() || m.id;

/** ¿La identidad se mide por commits? (tiene GitHub o correos de git y no está excluida). */
export const isTrackedIdentity = (i: WorkHoursIdentity | null | undefined): boolean =>
  Boolean(i && i.trackHours !== false && (normalizeLogin(i.githubLogin) || (i.gitEmails ?? []).some((e) => normalizeEmail(e))));

/** Esperado a la fecha vs real por persona y proyecto (#969). Función pura. */
export function computeExpectedToDate(input: ExpectedToDateInput): ExpectedToDate {
  const { from, today } = input;
  const to = input.to < today ? input.to : today;
  const hasDays = from <= to;
  const identitiesByMember = new Map<string, WorkHoursIdentity[]>();
  for (const i of input.identities ?? []) {
    if (!i || !i.teamMemberId) continue;
    identitiesByMember.set(i.teamMemberId, [...(identitiesByMember.get(i.teamMemberId) ?? []), i]);
  }
  const byPerson: ExpectedPersonLine[] = [];
  const nonTrackedMembers: ExpectedToDate["nonTrackedMembers"] = [];
  // Proyectos del equipo (los de las asignaciones de cualquiera) con sus repos,
  // para saber de qué proyecto es un repo aunque esta persona no lo tenga asignado.
  const teamProjects = new Map<string, { projectId: string; projectName: string; repos: string[] }>();
  for (const m of input.projectMembers ?? input.members ?? []) {
    for (const a of (m && m.projectAllocations) || []) {
      if (!a || !a.projectId) continue;
      const prev = teamProjects.get(a.projectId) ?? { projectId: a.projectId, projectName: a.projectName || a.projectId, repos: [] };
      prev.repos = [...new Set([...prev.repos, ...(a.repos ?? [])])];
      teamProjects.set(a.projectId, prev);
    }
  }
  const teamProjectOfRepo = (repo: string) => {
    const hits = [...teamProjects.values()].filter((p) => repoMatchesAny(p.repos, repo));
    // Un repo que es de dos proyectos no se adivina: se queda con su nombre.
    return hits.length === 1 ? hits[0] : null;
  };
  for (const m of input.members ?? []) {
    if (!m || !m.id) continue;
    if (INACTIVE_MEMBER_STATUSES.has(String(m.status ?? "active"))) continue;
    if (m.contractorId) continue;
    const linked = identitiesByMember.get(m.id) ?? [];
    // Contratista (#841): su identidad está excluida del registro de horas.
    if (linked.some((i) => i.trackHours === false)) continue;
    // Con userId: la persona se busca entre TODAS sus identidades, para que quien
    // no se mide por commits siga saliendo en `nonTrackedMembers` (tarjeta de
    // asignaciones, vista de una persona).
    if (input.userId && !linked.some((i) => i.userId === input.userId)) continue;
    const identity = input.userId
      ? linked.find((i) => i.userId === input.userId && isTrackedIdentity(i))
      : linked.find(isTrackedIdentity);
    if (!identity) {
      nonTrackedMembers.push({ teamMemberId: m.id, name: memberName(m), reason: "no_github" });
      continue;
    }
    const allocations = (m.projectAllocations ?? []).filter((a) => a && (a.projectId || a.projectName));
    if (!allocations.length) continue;
    const capacity = Number(m.weeklyCapacityHours) > 0 ? Number(m.weeklyCapacityHours) : 40;
    const userId = identity.userId;
    const pva = hasDays
      ? computePlannedVsActual(
          (input.buckets ?? []).filter((b) => b.userId === userId),
          allocations,
          capacity,
          from,
          to,
          (input.entries ?? []).filter((e) => e.userId === userId),
          { events: (input.events ?? []).filter((e) => !e.userId || e.userId === userId), estimate: input.estimate },
        )
      : null;
    const lines: ExpectedProjectLine[] = allocations.map((a, idx) => {
      const p = pva?.projects[idx];
      const start = a.startDate && a.startDate > from ? a.startDate : from;
      const end = a.endDate && a.endDate < to ? a.endDate : to;
      const days = hasDays && start <= end ? businessDays(start, end) : 0;
      const expectedHours = p?.plannedHours ?? 0;
      const actualHours = p?.actualHours ?? 0;
      const pendingHours = p?.pendingHours ?? 0;
      return {
        projectId: a.projectId,
        projectName: a.projectName,
        percentage: Number(a.percentage) || 0,
        businessDays: days,
        expectedHours,
        actualHours,
        recordedHours: p?.recordedHours ?? 0,
        estimatedHours: p?.estimatedHours ?? 0,
        gapHours: round2(actualHours - expectedHours),
        pendingHours,
        gapWithPendingHours: round2(actualHours + pendingHours - expectedHours),
        outOfAllocation: false,
      };
    });
    // Fuera de asignación. Un repo que la persona no tiene asignado llega como
    // `repo:owner/nombre` cuando la franja no trae su proyecto; si ese repo es de
    // un proyecto del equipo, las horas van a ESE proyecto (#1349): antes «Wayak»
    // y «Coding-Cloud-Online/WAYAK» salían como dos proyectos distintos.
    const outByProject = new Map<string, ExpectedProjectLine>();
    for (const o of pva?.outOfAllocation.projects ?? []) {
      const real = o.projectId.startsWith(REPO_PROJECT_PREFIX) ? teamProjectOfRepo(o.projectId.slice(REPO_PROJECT_PREFIX.length)) : null;
      const projectId = real ? real.projectId : o.projectId;
      const prev = outByProject.get(projectId);
      const hours = round2((prev?.actualHours ?? 0) + o.hours);
      outByProject.set(projectId, {
        projectId,
        projectName: real ? real.projectName : o.projectLabel,
        percentage: 0,
        businessDays: 0,
        expectedHours: 0,
        actualHours: hours,
        recordedHours: round2((prev?.recordedHours ?? 0) + o.recordedHours),
        estimatedHours: round2((prev?.estimatedHours ?? 0) + o.estimatedHours),
        gapHours: hours,
        pendingHours: 0,
        gapWithPendingHours: hours,
        outOfAllocation: true,
        reattributedFromRepo: Boolean(real) || prev?.reattributedFromRepo === true,
        reasons: [...new Set([...(prev?.reasons ?? []), ...(o.reasons ?? [])])],
      });
    }
    const outLines = [...outByProject.values()].sort((a, b) => b.actualHours - a.actualHours || a.projectName.localeCompare(b.projectName));
    const expectedHours = round2(lines.reduce((n, l) => n + l.expectedHours, 0));
    const actualHours = pva?.totalHours ?? 0;
    const pendingHours = pva?.pendingHours ?? 0;
    byPerson.push({
      userId,
      teamMemberId: m.id,
      name: memberName(m),
      weeklyCapacityHours: capacity,
      hoursPerDay: round2(capacity / EXPECTED_WORKDAYS_PER_WEEK),
      expectedHours,
      actualHours,
      recordedHours: pva?.recordedHours ?? 0,
      estimatedHours: pva?.estimatedHours ?? 0,
      outOfAllocationHours: pva?.outOfAllocation.hours ?? 0,
      gapHours: round2(actualHours - expectedHours),
      pendingHours,
      gapWithPendingHours: round2(actualHours + pendingHours - expectedHours),
      // Asignaciones fuera del rango y sin horas no aportan nada: no se listan.
      byProject: [...lines.filter((l) => l.businessDays > 0 || l.actualHours > 0 || l.pendingHours > 0), ...outLines],
    });
  }
  byPerson.sort((a, b) => a.gapHours - b.gapHours || a.name.localeCompare(b.name));
  nonTrackedMembers.sort((a, b) => a.name.localeCompare(b.name));
  const teamExpectedHours = round2(byPerson.reduce((n, p) => n + p.expectedHours, 0));
  const teamActualHours = round2(byPerson.reduce((n, p) => n + p.actualHours, 0));
  const teamPendingHours = round2(byPerson.reduce((n, p) => n + p.pendingHours, 0));
  return {
    teamExpectedHours,
    teamActualHours,
    gapHours: round2(teamActualHours - teamExpectedHours),
    teamPendingHours,
    gapWithPendingHours: round2(teamActualHours + teamPendingHours - teamExpectedHours),
    byPerson,
    nonTrackedMembers,
    rule: {
      hoursPerDay: round2(40 / EXPECTED_WORKDAYS_PER_WEEK),
      businessDays: hasDays ? businessDays(from, to) : 0,
      from,
      to,
      today,
      timeZone: WORK_HOURS_TIMEZONE,
    },
  };
}

// ---------------------------------------------------------------------------
// ESPERADO VS REAL POR SQUAD Y POR PROYECTO
// ---------------------------------------------------------------------------
// Las mismas horas de «Esperado a hoy · Real · Diferencia», agrupadas:
//
//   • Por SQUAD (módulo Equipo): cada persona cuenta en UN solo squad, para que
//     los squads sumen el total del equipo. Es de un squad quien lo lidera o es
//     miembro; si está en varios, cuenta en el primero por nombre. Quien no
//     está en ninguno cae en la fila «Sin squad» (`squadId: null`).
//   • Por PROYECTO: suma de las filas de proyecto de cada persona, incluidas
//     las horas fuera de asignación (esperado 0).
// ---------------------------------------------------------------------------

/** Squad del módulo Equipo, con lo mínimo que necesita el desglose. */
export interface ExpectedSquadInput {
  id: string;
  name?: string | null;
  leadId?: string | null;
  members?: { teamMemberId?: string | null }[] | null;
  active?: boolean | null;
}

export interface ExpectedGroupHours {
  /** Personas que suman en la fila. */
  people: number;
  expectedHours: number;
  actualHours: number;
  /** real − esperado (negativo = faltante). */
  gapHours: number;
  /** Horas por aprobar (#1281), fuera de `actualHours`. */
  pendingHours: number;
  /** real + por aprobar − esperado. */
  gapWithPendingHours: number;
}

export interface ExpectedSquadLine extends ExpectedGroupHours {
  /** `null` = personas sin squad. */
  squadId: string | null;
  squadName: string | null;
}

export interface ExpectedProjectTotal extends ExpectedGroupHours {
  projectId: string;
  projectName: string;
}

export interface ExpectedBreakdown {
  bySquad: ExpectedSquadLine[];
  byProject: ExpectedProjectTotal[];
}

/** Colaborador → su squad (el primero por nombre si está en varios). */
/**
 * A qué squad pertenece cada colaborador (`teamMemberId → squad`). Cada persona
 * cuenta en UNO solo: el que lidera o integra y, si está en varios, el primero
 * por nombre. Es la regla del desglose «por squad» y del filtro por squad del
 * tablero (#1349): las dos cosas tienen que agrupar a la misma gente.
 */
export const squadOfTeamMember = (squads: ExpectedSquadInput[] | null | undefined): Map<string, ExpectedSquadInput> => squadOfMember(squads);

function squadOfMember(squads: ExpectedSquadInput[] | null | undefined): Map<string, ExpectedSquadInput> {
  const sorted = (squads ?? [])
    .filter((s) => s && s.id && s.active !== false)
    .sort((a, b) => String(a.name ?? "").localeCompare(String(b.name ?? "")) || a.id.localeCompare(b.id));
  const out = new Map<string, ExpectedSquadInput>();
  for (const s of sorted) {
    for (const id of [s.leadId, ...(s.members ?? []).map((m) => m && m.teamMemberId)]) {
      if (id && !out.has(id)) out.set(id, s);
    }
  }
  return out;
}

/** Orden de las filas de squad: por nombre, y «Sin squad» al final. */
const bySquadName = (a: { squadId: string | null; squadName: string | null }, b: { squadId: string | null; squadName: string | null }): number =>
  Number(a.squadId === null) - Number(b.squadId === null) || String(a.squadName ?? "").localeCompare(String(b.squadName ?? ""));

/** Esperado vs real por squad y por proyecto, en horas. Función pura. */
export function computeExpectedBreakdown(expected: ExpectedToDate | null | undefined, squads?: ExpectedSquadInput[] | null): ExpectedBreakdown {
  const squadOf = squadOfMember(squads);
  const squadLines = new Map<string, ExpectedSquadLine>();
  const projectLines = new Map<string, ExpectedProjectTotal & { users: Set<string> }>();
  for (const p of expected?.byPerson ?? []) {
    const squad = squadOf.get(p.teamMemberId) ?? null;
    const key = squad ? squad.id : "";
    const line = squadLines.get(key) ?? {
      squadId: squad ? squad.id : null,
      squadName: squad ? (squad.name ?? squad.id) : null,
      people: 0,
      expectedHours: 0,
      actualHours: 0,
      gapHours: 0,
      pendingHours: 0,
      gapWithPendingHours: 0,
    };
    line.people += 1;
    line.expectedHours += p.expectedHours;
    line.actualHours += p.actualHours;
    line.pendingHours += p.pendingHours ?? 0;
    squadLines.set(key, line);
    for (const l of p.byProject ?? []) {
      const project = projectLines.get(l.projectId) ?? {
        projectId: l.projectId,
        projectName: l.projectName,
        people: 0,
        expectedHours: 0,
        actualHours: 0,
        gapHours: 0,
        pendingHours: 0,
        gapWithPendingHours: 0,
        users: new Set<string>(),
      };
      project.users.add(p.userId);
      project.expectedHours += l.expectedHours;
      project.actualHours += l.actualHours;
      project.pendingHours += l.pendingHours ?? 0;
      projectLines.set(l.projectId, project);
    }
  }
  const close = <T extends ExpectedGroupHours>(l: T): T => ({
    ...l,
    expectedHours: round2(l.expectedHours),
    actualHours: round2(l.actualHours),
    gapHours: round2(l.actualHours - l.expectedHours),
    pendingHours: round2(l.pendingHours),
    gapWithPendingHours: round2(l.actualHours + l.pendingHours - l.expectedHours),
  });
  return {
    bySquad: [...squadLines.values()].map(close).sort(bySquadName),
    byProject: [...projectLines.values()]
      .map(({ users, ...l }) => close({ ...l, people: users.size }))
      .sort((a, b) => b.expectedHours - a.expectedHours || b.actualHours - a.actualHours || a.projectName.localeCompare(b.projectName)),
  };
}

// ---------------------------------------------------------------------------
// QUINCENAS (periodos de pago)
// ---------------------------------------------------------------------------
// La nómina se paga por quincena: el 15 y el último día del mes. Si ese día cae
// en sábado o domingo se paga el VIERNES anterior, y el periodo termina ese
// viernes: el siguiente empieza al día siguiente. Así el 31 oct 2026 (sábado)
// deja la 2.ª quincena de octubre del 16 al 30 (pago viernes 30) y la 1.ª de
// noviembre del 31 oct al 13 nov (el 15 es domingo → pago viernes 13).
// No descuenta días festivos.
// ---------------------------------------------------------------------------

export interface PayPeriod {
  /** Primer día del periodo (el día siguiente al pago anterior). */
  from: string;
  /** Último día del periodo = día de pago. */
  to: string;
  /** Día en que se paga (igual a `to`). */
  payDate: string;
  /** Corte nominal: el 15 o el último día del mes. */
  nominalEnd: string;
  /** 1 = primera quincena (corte el 15), 2 = segunda (corte fin de mes). */
  half: 1 | 2;
  /** Mes de la quincena (`YYYY-MM`), el del corte nominal. */
  month: string;
}

const pad2 = (n: number): string => String(n).padStart(2, "0");

/** Corte nominal de una quincena: el 15 o el último día del mes. */
function nominalPayEnd(year: number, month: number, half: 1 | 2): string {
  const day = half === 1 ? 15 : new Date(Date.UTC(year, month, 0)).getUTCDate();
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

/** Día de pago: el corte nominal, o el viernes anterior si cae en fin de semana. */
export function payDateOf(nominalEnd: string): string {
  const wd = weekdayOf(nominalEnd); // lunes = 0 … sábado = 5, domingo = 6
  return wd === 5 ? addDays(nominalEnd, -1) : wd === 6 ? addDays(nominalEnd, -2) : nominalEnd;
}

/** Quincena anterior a (year, month, half) en el calendario. */
function prevHalf(year: number, month: number, half: 1 | 2): [number, number, 1 | 2] {
  if (half === 2) return [year, month, 1];
  return month === 1 ? [year - 1, 12, 2] : [year, month - 1, 2];
}

/** Periodo de pago de la quincena (year, month, half). */
function payPeriodFor(year: number, month: number, half: 1 | 2): PayPeriod {
  const nominalEnd = nominalPayEnd(year, month, half);
  const payDate = payDateOf(nominalEnd);
  const [py, pm, ph] = prevHalf(year, month, half);
  const from = addDays(payDateOf(nominalPayEnd(py, pm, ph)), 1);
  return { from, to: payDate, payDate, nominalEnd, half, month: `${year}-${pad2(month)}` };
}

/** Quincena (periodo de pago) que contiene la fecha `YYYY-MM-DD`. */
export function payPeriodOf(date: string): PayPeriod {
  const [y, m] = date.split("-").map(Number);
  // La fecha cae en una quincena de su mes o, si es posterior al último pago del
  // mes (p. ej. un sábado 31), en la primera del mes siguiente.
  const candidates = [payPeriodFor(y, m, 1), payPeriodFor(y, m, 2), m === 12 ? payPeriodFor(y + 1, 1, 1) : payPeriodFor(y, m + 1, 1)];
  const hit = candidates.find((p) => p.from <= date && date <= p.to);
  return hit ?? candidates[0];
}

/** Quincena inmediatamente anterior a la que contiene `date`. */
export function previousPayPeriod(date: string): PayPeriod {
  return payPeriodOf(addDays(payPeriodOf(date).from, -1));
}

// ---------------------------------------------------------------------------
// COSTO DEL ESPERADO VS REAL
// ---------------------------------------------------------------------------
// Lo que cuestan las horas de las tarjetas «Esperado a hoy · Real · Diferencia»:
//
//   • Costo por hora de CADA persona, según su modalidad de pago:
//       hourly       → hourlyRate
//       daily        → dailyRate ÷ (capacidad / 5)
//       fixed_salary → fixedSalary × periodos al año ÷ (capacidad × 52)
//     El sueldo fijo es por periodo de nómina; sin periodicidad se toma
//     quincenal (24 al año): 40 h/sem → 86.67 h por quincena.
//   • Esperado = horas esperadas × costo; real = horas que cuentan × costo.
//   • Pérdida = Σ por persona de las horas FALTANTES × su costo. Las horas de
//     más de una persona no tapan las que le faltan a otra: esas ya se pagaron.
//   • Los importes se agrupan POR MONEDA y nunca se suman entre sí: no hay tipo
//     de cambio (#741). Sin moneda declarada se asume la de la plataforma (MXN).
//   • Quien no tiene sueldo registrado sale en `unpricedMembers` y no suma.
// ---------------------------------------------------------------------------

/** Periodos de nómina por año según la periodicidad del sueldo fijo. */
export const PAY_PERIODS_PER_YEAR: Record<string, number> = {
  weekly: 52,
  biweekly: 26,
  semimonthly: 24,
  monthly: 12,
};

/** Periodicidad por omisión del sueldo fijo: quincenal. */
export const DEFAULT_PAY_FREQUENCY = "semimonthly";

/** Moneda que se asume cuando el colaborador no declara la suya. */
export const DEFAULT_COST_CURRENCY = "MXN";

export type HourlyCostBasis = "hourly" | "daily" | "fixed_salary";

export interface HourlyCost {
  hourlyCost: number;
  currency: string;
  basis: HourlyCostBasis;
  /** La moneda no venía declarada y se asumió `DEFAULT_COST_CURRENCY`. */
  currencyAssumed: boolean;
}

const positive = (v: unknown): number | null => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * Costo por hora de un colaborador. Usa su `payMode` si trae el monto que le
 * corresponde; si no, el primer monto que tenga (sueldo fijo, por hora, diario).
 * `null` si no hay ningún monto: no se inventa un costo.
 */
export function hourlyCostOf(member: ExpectedMemberInput | null | undefined): HourlyCost | null {
  if (!member) return null;
  const capacity = positive(member.weeklyCapacityHours) ?? 40;
  const periods = PAY_PERIODS_PER_YEAR[String(member.payFrequency ?? "")] ?? PAY_PERIODS_PER_YEAR[DEFAULT_PAY_FREQUENCY];
  const candidates: Record<HourlyCostBasis, number | null> = {
    fixed_salary: positive(member.fixedSalary) != null ? (positive(member.fixedSalary)! * periods) / (capacity * 52) : null,
    hourly: positive(member.hourlyRate),
    daily: positive(member.dailyRate) != null ? positive(member.dailyRate)! / (capacity / EXPECTED_WORKDAYS_PER_WEEK) : null,
  };
  const mode = member.payMode as HourlyCostBasis;
  const basis = (mode in candidates && candidates[mode] != null ? mode : (["fixed_salary", "hourly", "daily"] as const).find((b) => candidates[b] != null)) as
    | HourlyCostBasis
    | undefined;
  if (!basis) return null;
  const code = String(member.currency ?? "").trim().toUpperCase();
  const declared = /^[A-Z]{3}$/.test(code);
  return {
    // Sin redondear: se redondea el importe, no la tarifa (312 h × 11.538… = 3 600).
    hourlyCost: candidates[basis]!,
    currency: declared ? code : DEFAULT_COST_CURRENCY,
    basis,
    currencyAssumed: !declared,
  };
}

export interface CostAmounts {
  expectedCost: number;
  actualCost: number;
  /** Costo de las horas faltantes (siempre ≥ 0). */
  lossCost: number;
  /** Lo que valdrían las horas por aprobar (#1281): NO está en `actualCost`. */
  pendingCost: number;
}

export interface ExpectedCostPersonLine extends CostAmounts {
  userId: string;
  name: string;
  hourlyCost: number;
  currency: string;
  basis: HourlyCostBasis;
  currencyAssumed: boolean;
  /** Horas que le faltan para el esperado (≥ 0). */
  shortfallHours: number;
  /** Horas por aprobar (#1281). */
  pendingHours: number;
}

export interface ExpectedCost {
  /** Totales por moneda (MXN, USD…). Nunca se suman entre monedas. */
  byCurrency: Record<string, CostAmounts>;
  /** Σ por persona de las horas faltantes (≥ 0), con o sin sueldo registrado. */
  shortfallHours: number;
  /** Σ de las horas faltantes de quien SÍ tiene costo (las que suman pérdida). */
  pricedShortfallHours: number;
  byPerson: ExpectedCostPersonLine[];
  /** Los mismos importes por squad («Sin squad» = `squadId: null`). */
  bySquad: ExpectedCostSquadLine[];
  /** Los mismos importes por proyecto (incluye lo trabajado fuera de asignación). */
  byProject: ExpectedCostProjectLine[];
  /** Personas del esperado sin sueldo registrado: no suman costo. */
  unpricedMembers: { userId: string; name: string }[];
  rule: { defaultPayFrequency: string; periodsPerYear: number; defaultCurrency: string };
  /**
   * Tipo de cambio a `baseCurrency` (MXN) de cada moneda extranjera de los
   * importes, para dar su equivalente en pesos. Lo agrega el API al responder
   * (`computeExpectedCost` no consulta tipos de cambio); un backend anterior no lo manda.
   */
  baseCurrency?: string;
  fx?: Record<string, { rate: number; date?: string | null; source?: string | null }>;
  /** Monedas sin tipo de cambio disponible: sus importes no se convierten. */
  fxMissing?: string[];
}

/** Importes de una fila del desglose, por moneda. */
export interface ExpectedCostGroup {
  byCurrency: Record<string, CostAmounts>;
  /** Personas de la fila sin sueldo registrado: sus horas no suman importe. */
  unpricedPeople: number;
}

export interface ExpectedCostSquadLine extends ExpectedCostGroup {
  squadId: string | null;
  squadName: string | null;
  people: number;
}

export interface ExpectedCostProjectLine extends ExpectedCostGroup {
  projectId: string;
  projectName: string;
  people: number;
}

/** Suma `amounts` en `acc[currency]` sin redondear (se redondea al cerrar la fila). */
function addCost(acc: Record<string, CostAmounts>, currency: string, amounts: CostAmounts): void {
  const cur = (acc[currency] ??= { expectedCost: 0, actualCost: 0, lossCost: 0, pendingCost: 0 });
  cur.expectedCost += amounts.expectedCost;
  cur.actualCost += amounts.actualCost;
  cur.lossCost += amounts.lossCost;
  cur.pendingCost += amounts.pendingCost;
}

const roundCost = (byCurrency: Record<string, CostAmounts>): Record<string, CostAmounts> =>
  Object.fromEntries(
    Object.entries(byCurrency).map(([c, v]) => [
      c,
      { expectedCost: round2(v.expectedCost), actualCost: round2(v.actualCost), lossCost: round2(v.lossCost), pendingCost: round2(v.pendingCost) },
    ]),
  );

/**
 * Costo de «Esperado a hoy · Real · Diferencia», por moneda. Función pura.
 * Con `squads` (módulo Equipo) el desglose por squad usa la misma regla que
 * `computeExpectedBreakdown`; sin ellos, todo el equipo cae en «Sin squad».
 */
export function computeExpectedCost(expected: ExpectedToDate, members: ExpectedMemberInput[], squads?: ExpectedSquadInput[] | null): ExpectedCost {
  const membersById = new Map((members ?? []).filter((m) => m && m.id).map((m) => [m.id, m]));
  const byCurrency: Record<string, CostAmounts> = {};
  const byPerson: ExpectedCostPersonLine[] = [];
  const unpricedMembers: ExpectedCost["unpricedMembers"] = [];
  const squadOf = squadOfMember(squads);
  const squadLines = new Map<string, ExpectedCostSquadLine>();
  const projectLines = new Map<string, ExpectedCostProjectLine & { users: Set<string>; unpriced: Set<string> }>();
  let shortfallHours = 0;
  let pricedShortfallHours = 0;
  for (const p of expected?.byPerson ?? []) {
    const shortfall = p.gapHours < 0 ? -p.gapHours : 0;
    shortfallHours += shortfall;
    const rate = hourlyCostOf(membersById.get(p.teamMemberId));
    const squad = squadOf.get(p.teamMemberId) ?? null;
    const squadKey = squad ? squad.id : "";
    const squadLine = squadLines.get(squadKey) ?? { squadId: squad ? squad.id : null, squadName: squad ? (squad.name ?? squad.id) : null, people: 0, byCurrency: {}, unpricedPeople: 0 };
    squadLine.people += 1;
    squadLines.set(squadKey, squadLine);
    // Una persona puede traer DOS filas del mismo proyecto (la asignada y, desde
    // #1349, la de un repo de ese proyecto que no tiene asignado): se suman antes
    // de calcular la pérdida, para que el dinero diga lo mismo que las horas.
    const ownLines = new Map<string, { projectId: string; projectName: string; expectedHours: number; actualHours: number; pendingHours: number; gapHours: number }>();
    for (const raw of p.byProject ?? []) {
      const prev = ownLines.get(raw.projectId) ?? { projectId: raw.projectId, projectName: raw.projectName, expectedHours: 0, actualHours: 0, pendingHours: 0, gapHours: 0 };
      prev.expectedHours += raw.expectedHours;
      prev.actualHours += raw.actualHours;
      prev.pendingHours += raw.pendingHours ?? 0;
      prev.gapHours = prev.actualHours - prev.expectedHours;
      ownLines.set(raw.projectId, prev);
    }
    for (const l of ownLines.values()) {
      const project = projectLines.get(l.projectId) ?? { projectId: l.projectId, projectName: l.projectName, people: 0, byCurrency: {}, unpricedPeople: 0, users: new Set<string>(), unpriced: new Set<string>() };
      project.users.add(p.userId);
      if (rate) {
        // La pérdida de un proyecto es lo que faltó EN ESE proyecto: lo que la
        // persona trabajó de más en otro no la tapa.
        addCost(project.byCurrency, rate.currency, {
          expectedCost: l.expectedHours * rate.hourlyCost,
          actualCost: l.actualHours * rate.hourlyCost,
          lossCost: (l.gapHours < 0 ? -l.gapHours : 0) * rate.hourlyCost,
          pendingCost: (l.pendingHours ?? 0) * rate.hourlyCost,
        });
      } else {
        project.unpriced.add(p.userId);
      }
      projectLines.set(l.projectId, project);
    }
    if (!rate) {
      squadLine.unpricedPeople += 1;
      unpricedMembers.push({ userId: p.userId, name: p.name });
      continue;
    }
    addCost(squadLine.byCurrency, rate.currency, {
      expectedCost: p.expectedHours * rate.hourlyCost,
      actualCost: p.actualHours * rate.hourlyCost,
      lossCost: shortfall * rate.hourlyCost,
      pendingCost: (p.pendingHours ?? 0) * rate.hourlyCost,
    });
    pricedShortfallHours += shortfall;
    const line: ExpectedCostPersonLine = {
      userId: p.userId,
      name: p.name,
      ...rate,
      hourlyCost: round2(rate.hourlyCost),
      shortfallHours: round2(shortfall),
      pendingHours: round2(p.pendingHours ?? 0),
      expectedCost: round2(p.expectedHours * rate.hourlyCost),
      actualCost: round2(p.actualHours * rate.hourlyCost),
      lossCost: round2(shortfall * rate.hourlyCost),
      pendingCost: round2((p.pendingHours ?? 0) * rate.hourlyCost),
    };
    byPerson.push(line);
    const acc = (byCurrency[rate.currency] ??= { expectedCost: 0, actualCost: 0, lossCost: 0, pendingCost: 0 });
    acc.expectedCost = round2(acc.expectedCost + line.expectedCost);
    acc.actualCost = round2(acc.actualCost + line.actualCost);
    acc.lossCost = round2(acc.lossCost + line.lossCost);
    acc.pendingCost = round2(acc.pendingCost + line.pendingCost);
  }
  return {
    byCurrency,
    shortfallHours: round2(shortfallHours),
    pricedShortfallHours: round2(pricedShortfallHours),
    byPerson,
    bySquad: [...squadLines.values()].map((l) => ({ ...l, byCurrency: roundCost(l.byCurrency) })).sort(bySquadName),
    byProject: [...projectLines.values()]
      .map(({ users, unpriced, ...l }) => ({ ...l, people: users.size, unpricedPeople: unpriced.size, byCurrency: roundCost(l.byCurrency) }))
      .sort((a, b) => a.projectName.localeCompare(b.projectName)),
    unpricedMembers,
    rule: {
      defaultPayFrequency: DEFAULT_PAY_FREQUENCY,
      periodsPerYear: PAY_PERIODS_PER_YEAR[DEFAULT_PAY_FREQUENCY],
      defaultCurrency: DEFAULT_COST_CURRENCY,
    },
  };
}

// ---------------------------------------------------------------------------
// COSTO POR REPOSITORIO (#1063)
// ---------------------------------------------------------------------------
// Convierte las horas de cada repo (las de `byRepo`, medidas con bloques de
// tiempo real) en dinero: suma por persona de SUS horas en ese repo × SU costo
// por hora (`hourlyCostOf`, la misma tarifa de «Esperado a hoy», #974).
//   • Por MONEDA, nunca sumadas entre sí (no hay tipo de cambio, #741).
//   • La persona se liga a su colaborador por `identity.teamMemberId`; sin
//     colaborador o sin sueldo registrado, sus horas quedan en `unpricedHours`
//     y no suman costo (no se inventa una tarifa).
//   • Lleva sueldos: sólo lo calcula y lo recibe un dueño/administrador.
// ---------------------------------------------------------------------------

export interface RepoCostLine {
  repo: string;
  hours: number;
  /** Importe por moneda (MXN, USD…). Vacío si nadie del repo tiene sueldo. */
  byCurrency: Record<string, number>;
  /** Horas del repo de personas sin sueldo registrado (no suman costo). */
  unpricedHours: number;
}

export interface RepoCost {
  byRepo: RepoCostLine[];
  /** Total del rango por moneda. Nunca se suman entre monedas. */
  byCurrency: Record<string, number>;
  /** Personas con horas en algún repo y sin sueldo registrado. */
  unpricedUsers: { userId: string; name: string; hours: number }[];
  rule: { defaultPayFrequency: string; periodsPerYear: number; defaultCurrency: string };
}

/** Costo de las horas de cada repo, por moneda. Función pura. */
export function computeRepoCost(
  byRepoUser: RepoUserMinutes[] | null | undefined,
  identities: Pick<WorkHoursIdentity, "userId" | "userName" | "email" | "teamMemberId">[] | null | undefined,
  members: ExpectedMemberInput[] | null | undefined,
): RepoCost {
  const membersById = new Map((members ?? []).filter((m) => m && m.id).map((m) => [m.id, m]));
  const identityByUser = new Map((identities ?? []).filter((i) => i && i.userId).map((i) => [i.userId, i]));
  const rates = new Map<string, HourlyCost | null>();
  const rateOf = (userId: string): HourlyCost | null => {
    if (!rates.has(userId)) {
      const identity = identityByUser.get(userId);
      rates.set(userId, identity && identity.teamMemberId ? hourlyCostOf(membersById.get(identity.teamMemberId)) : null);
    }
    return rates.get(userId) ?? null;
  };
  const lines = new Map<string, { repo: string; minutes: number; amounts: Record<string, number>; unpricedMinutes: number }>();
  const totals: Record<string, number> = {};
  const unpriced = new Map<string, number>();
  for (const row of byRepoUser ?? []) {
    const minutes = Number(row.minutes) || 0;
    if (minutes <= 0) continue;
    const line = lines.get(row.repo) ?? { repo: row.repo, minutes: 0, amounts: {}, unpricedMinutes: 0 };
    line.minutes += minutes;
    const rate = rateOf(row.userId);
    if (rate) {
      // Sin redondear aquí: se redondea el importe final, no cada tramo.
      const amount = (minutes / 60) * rate.hourlyCost;
      line.amounts[rate.currency] = (line.amounts[rate.currency] ?? 0) + amount;
      totals[rate.currency] = (totals[rate.currency] ?? 0) + amount;
    } else {
      line.unpricedMinutes += minutes;
      unpriced.set(row.userId, (unpriced.get(row.userId) ?? 0) + minutes);
    }
    lines.set(row.repo, line);
  }
  const roundAll = (m: Record<string, number>) => Object.fromEntries(Object.entries(m).map(([c, v]) => [c, round2(v)]));
  return {
    byRepo: [...lines.values()]
      .map((l) => ({ repo: l.repo, hours: round2(l.minutes / 60), byCurrency: roundAll(l.amounts), unpricedHours: round2(l.unpricedMinutes / 60) }))
      .sort((a, b) => b.hours - a.hours || a.repo.localeCompare(b.repo)),
    byCurrency: roundAll(totals),
    unpricedUsers: [...unpriced.entries()]
      .map(([userId, minutes]) => {
        const i = identityByUser.get(userId);
        return { userId, name: (i && (i.userName || i.email)) || userId, hours: round2(minutes / 60) };
      })
      .sort((a, b) => b.hours - a.hours || a.name.localeCompare(b.name)),
    rule: {
      defaultPayFrequency: DEFAULT_PAY_FREQUENCY,
      periodsPerYear: PAY_PERIODS_PER_YEAR[DEFAULT_PAY_FREQUENCY],
      defaultCurrency: DEFAULT_COST_CURRENCY,
    },
  };
}

// ---------------------------------------------------------------------------
// ENTRADAS DE TIEMPO EN MINUTOS (#782)
// ---------------------------------------------------------------------------
// Segunda forma de registrar tiempo, al lado de las franjas por commit: una
// entrada declara `minutes` trabajados en un proyecto, con nota y evidencia
// opcional. Sirve para reuniones, planeación o soporte que no dejan commit.
//
// NO alimenta `work_hours` (las franjas siguen naciendo SOLO de commits): vive
// en su propia colección y se consulta aparte.
//
// Idempotencia: el cliente manda una `idempotencyKey` determinista
// (`{date}-{projectId}-{slug}[-n]`) y el id del documento se deriva de ella
// (`{companyId}_{userId}_{key}`). Reenviar la misma entrada no duplica horas.
// ---------------------------------------------------------------------------

export const WORK_TIME_ENTRIES_COLLECTION = "work_time_entries";

/**
 * Tipos de entrada. #1060: `course` (cursos y capacitación) y `billable` (tiempo
 * facturable que no se liga a un issue o PR de GitHub) para las entradas manuales.
 */
export const TIME_ENTRY_KINDS = ["dev", "meeting", "planning", "review", "support", "course", "billable", "other"] as const;
export type BuiltInTimeEntryKind = (typeof TIME_ENTRY_KINDS)[number];
/**
 * Desde #1300 el `kind` es una clave del catálogo de actividades de la compañía
 * (`work_activity_catalog.key`): los tipos de arriba siguen existiendo (catálogo
 * base) y la compañía puede añadir los suyos. Por eso el tipo admite cualquier
 * slug; la validación real la hace el servicio contra el catálogo.
 */
export type TimeEntryKind = BuiltInTimeEntryKind | (string & {});

/** `crm` (#1308): reuniones con prospectos registradas desde el CRM para el comercial asignado. */
export const TIME_ENTRY_SOURCES = ["skill", "ui", "admin", "mcp", "crm"] as const;
export type TimeEntrySource = (typeof TIME_ENTRY_SOURCES)[number];

// ---------------------------------------------------------------------------
// APROBACIÓN DE ENTRADAS MANUALES (#1281)
// ---------------------------------------------------------------------------
// Una entrada MANUAL —capturada a mano en la consola (`ui`) o dictada por MCP
// (`mcp`), sin evidencia de commits— de MÁS de una hora nace pendiente de
// aprobación del tech lead. Las de la skill `cc-hours` (`skill`) no pasan por
// aprobación: traen evidencia de commits.
//
// #1346: la regla es PAREJA. Los dueños y los tech leads también esperan
// aprobación en sus propias entradas de más de 1 h, así que la carga con
// `source: "admin"` (que solo puede mandar un dueño y escribe SUS horas) deja de
// ser un atajo: pasa por aprobación igual que `ui` y `mcp`. Nadie aprueba lo
// suyo; la de un dueño la aprueba otro dueño o un tech lead.
//
//   • Pendiente ⇒ NO cuenta como hora real ni entra a la asistencia de Nómina;
//     se suma aparte como «Horas por aprobar».
//   • Rechazada ⇒ no cuenta (como una anulada) y conserva el motivo.
//   • Aprobada ⇒ cuenta como cualquier otra entrada.
// Las entradas anteriores a #1281 no traen `approval` y cuentan como siempre.
// ---------------------------------------------------------------------------

/** Fuentes «manuales»: sin evidencia de commits. */
export const MANUAL_TIME_ENTRY_SOURCES: readonly TimeEntrySource[] = ["ui", "mcp"];

/** Más de esto en una entrada manual ⇒ aprobación del tech lead (1 h). */
export const APPROVAL_THRESHOLD_MINUTES = 60;

export const TIME_ENTRY_APPROVAL_STATUSES = ["pending", "approved", "rejected"] as const;
export type TimeEntryApprovalStatus = (typeof TIME_ENTRY_APPROVAL_STATUSES)[number];

/** Estado de aprobación de una entrada (`work_time_entries.approval`). */
export interface TimeEntryApproval {
  status: TimeEntryApprovalStatus;
  /** Cuándo quedó pendiente (= `createdAt` de la entrada). */
  requestedAt: string;
  /** Umbral vigente al registrarla (minutos). */
  thresholdMinutes: number;
  decidedAt: string | null;
  /** Quién aprobó o rechazó (`userId`). */
  decidedBy: string | null;
  decidedByName: string | null;
  /** Motivo del rechazo (obligatorio al rechazar). */
  reason: string | null;
}

/** Límites del motivo de rechazo. */
export const APPROVAL_REASON_LIMITS = { MIN: 5, MAX: 500 } as const;

/**
 * Fuentes que pasan por aprobación (#1346): las manuales y la carga del
 * administrador. `admin` no es «manual» para el permiso por puesto (#1300), pero
 * tampoco trae evidencia: sin esto un dueño se saltaría su propia aprobación.
 */
export const APPROVAL_TIME_ENTRY_SOURCES: readonly TimeEntrySource[] = [...MANUAL_TIME_ENTRY_SOURCES, "admin"];

/**
 * Fuentes exentas SOLO si la entrada trae evidencia (#1346): la skill `cc-hours`.
 * Una reunión de 2 h dictada a la skill, sin commits ni PRs, es tan manual como
 * una capturada en la consola: espera aprobación igual.
 */
export const EVIDENCE_TIME_ENTRY_SOURCES: readonly TimeEntrySource[] = ["skill"];

/** Campos de `evidence` que cuentan como evidencia. */
const EVIDENCE_FIELDS = ["commits", "prs", "issues", "artifacts"] as const;

/** ¿La entrada cita al menos un commit, PR, issue o artefacto? */
export const hasTimeEntryEvidence = (evidence: { commits?: unknown; prs?: unknown; issues?: unknown; artifacts?: unknown } | null | undefined): boolean =>
  Boolean(evidence) &&
  EVIDENCE_FIELDS.some((f) => {
    const list = (evidence as Record<string, unknown>)[f];
    return Array.isArray(list) && list.some((s) => typeof s === "string" && s.trim().length > 0);
  });

/**
 * ¿La entrada debe pasar por aprobación? De más de una hora y:
 *   • sin evidencia por naturaleza (`ui`, `mcp`, `admin`) — siempre; o
 *   • de la skill (`skill`) cuando NO cita evidencia (`hasEvidence === false`).
 * `hasEvidence` solo importa para la skill. Sin informarlo (`undefined`) la skill
 * queda exenta, como antes de #1346: quien escribe entradas lo pasa siempre.
 * El CRM (`crm`) nunca pasa por aprobación: nace de una reunión ya registrada.
 */
export const requiresApproval = (source: TimeEntrySource | string | null | undefined, minutes: number | null | undefined, hasEvidence?: boolean): boolean => {
  if (!((Number(minutes) || 0) > APPROVAL_THRESHOLD_MINUTES)) return false;
  const s = String(source ?? "") as TimeEntrySource;
  if (APPROVAL_TIME_ENTRY_SOURCES.includes(s)) return true;
  return EVIDENCE_TIME_ENTRY_SOURCES.includes(s) && hasEvidence === false;
};

/** Aprobación recién nacida: pendiente. */
export const pendingApproval = (requestedAt: string): TimeEntryApproval => ({
  status: "pending",
  requestedAt,
  thresholdMinutes: APPROVAL_THRESHOLD_MINUTES,
  decidedAt: null,
  decidedBy: null,
  decidedByName: null,
  reason: null,
});

type ApprovalLike = { approval?: TimeEntryApproval | null } | null | undefined;

/** Estado de aprobación de una entrada; `null` si no pasó por aprobación (o es anterior a #1281). */
export const approvalStatusOf = (e: ApprovalLike): TimeEntryApprovalStatus | null => (e && e.approval && e.approval.status) || null;

/** ¿Está esperando al tech lead? (Anulada ⇒ no.) */
export const isPendingEntry = (e: (ApprovalLike & { voided?: unknown; minutes?: unknown }) | null | undefined): boolean =>
  Boolean(e) && !e!.voided && approvalStatusOf(e) === "pending" && Number(e!.minutes) > 0;

/** ¿La rechazó el tech lead? */
export const isRejectedEntry = (e: ApprovalLike): boolean => approvalStatusOf(e) === "rejected";

export const TIME_ENTRY_LIMITS = {
  MIN_MINUTES: 1,
  /** 16 h: tope de una sola entrada. */
  MAX_MINUTES: 960,
  NOTE_MIN: 3,
  NOTE_MAX: 500,
  /** Suma del día de una persona (sin anuladas). */
  DAY_MAX_MINUTES: 1440,
  BATCH_MAX: 100,
  PAGE_DEFAULT: 200,
  PAGE_MAX: 500,
  EVIDENCE_MAX_ITEMS: 50,
  EVIDENCE_MAX_LENGTH: 300,
  PROJECT_ID_MAX: 80,
  VOID_REASON_MIN: 3,
  VOID_REASON_MAX: 500,
} as const;

/** Códigos de error por entrada. Contrato con la skill: no renombrar. */
export const TIME_ENTRY_ERROR_CODES = {
  INVALID_ENTRY: "INVALID_ENTRY",
  PROJECT_NOT_FOUND: "PROJECT_NOT_FOUND",
  NOT_ASSIGNED: "NOT_ASSIGNED",
  DAY_OVER_24H: "DAY_OVER_24H",
  /**
   * #843: proyecto existente al que la persona NO está asignada, sin
   * `unassignedReason`. Con la justificación, la entrada se escribe marcada.
   */
  UNASSIGNED_REASON_REQUIRED: "UNASSIGNED_REASON_REQUIRED",
  /**
   * #1060: toda entrada queda ligada a un repositorio. Sin `repo`, y sin poder
   * deducirlo de la evidencia ni del único repo del proyecto, se rechaza.
   */
  REPO_REQUIRED: "REPO_REQUIRED",
  /** #1060: el repo no pertenece al proyecto elegido. */
  REPO_NOT_IN_PROJECT: "REPO_NOT_IN_PROJECT",
  /** #1303: la entrada (ui/mcp/skill) no trae ticket ni evidencia con la que ligarla. */
  ACTIVITY_REQUIRED: "ACTIVITY_REQUIRED",
  /** #1303: el `activityId` no existe en la empresa (o está archivado). */
  ACTIVITY_NOT_FOUND: "ACTIVITY_NOT_FOUND",
  /** #1300: el puesto de la persona no tiene permitido ese tipo de actividad. */
  KIND_NOT_ALLOWED: "KIND_NOT_ALLOWED",
  INTERNAL: "INTERNAL",
} as const;
export type TimeEntryErrorCode = (typeof TIME_ENTRY_ERROR_CODES)[keyof typeof TIME_ENTRY_ERROR_CODES];

/** Solo `INTERNAL` vale la pena reintentar: lo demás fallará igual. */
export const isRetryableTimeEntryError = (code: string): boolean => code === TIME_ENTRY_ERROR_CODES.INTERNAL;

// ---------------------------------------------------------------------------
// TIEMPO FUERA DE ASIGNACIÓN (#843)
// ---------------------------------------------------------------------------
// Toda entrada va a un proyecto EXISTENTE de la empresa (`PROJECT_NOT_FOUND`
// sigue rechazando: nunca se registra tiempo sin proyecto). Registrar en un
// proyecto al que la persona NO está asignada sí se permite, pero SOLO con una
// justificación escrita por la persona (`unassignedReason`); sin ella se
// rechaza con `UNASSIGNED_REASON_REQUIRED`. Con ella la entrada se escribe,
// CUENTA (día, nómina, reportes) y queda MARCADA: el texto del sistema (qué
// proyecto y qué persona) más la justificación de la persona.
//
// Los días ESTIMADOS por commits también se marcan (calculado, nunca guardado):
// franjas en repos de proyectos no asignados («sin justificación») y en repos
// que no pertenecen a ningún proyecto («repo sin proyecto»).
// ---------------------------------------------------------------------------

/** Marcas de tiempo fuera de asignación. Contrato con la skill: no renombrar. */
export const TIME_ENTRY_FLAG_CODES = {
  NOT_ASSIGNED: "NOT_ASSIGNED",
  /** Solo en franjas estimadas: una entrada nunca se escribe sin proyecto. */
  PROJECT_NOT_FOUND: "PROJECT_NOT_FOUND",
} as const;
export type TimeEntryFlagCode = (typeof TIME_ENTRY_FLAG_CODES)[keyof typeof TIME_ENTRY_FLAG_CODES];

/**
 * Una marca: código estable, texto del sistema (nombra proyecto/repo y persona)
 * y, en una entrada, la justificación que escribió la persona.
 */
export interface TimeEntryFlag {
  code: TimeEntryFlagCode;
  message: string;
  /** Justificación de la persona (`unassignedReason`). `null` ⇒ sin justificación (franjas estimadas). */
  reason?: string | null;
}

/** Límites de la justificación de trabajo fuera de asignación. */
export const UNASSIGNED_REASON_LIMITS = { MIN: 10, MAX: 500 } as const;

/** Rótulo del distintivo y de la línea extra de plan vs real. */
export const OUT_OF_ALLOCATION_LABEL = "Fuera de asignación";
/** Lo que se muestra cuando una marca no trae justificación (franjas estimadas). */
export const NO_JUSTIFICATION_LABEL = "sin justificación";

/** «Trabajo en Dumax, pero Anderson Delgado no está asignado a ese proyecto». */
export function notAssignedFlag(
  projectLabel: string | null | undefined,
  personName: string | null | undefined,
  reason: string | null = null,
): TimeEntryFlag {
  const project = String(projectLabel || "").trim() || "un proyecto";
  const person = String(personName || "").trim() || "la persona";
  return {
    code: TIME_ENTRY_FLAG_CODES.NOT_ASSIGNED,
    message: `Trabajo en ${project}, pero ${person} no está asignado a ese proyecto`,
    reason: reason ? String(reason).trim() : null,
  };
}

/** «Repo sin proyecto: owner/repo no pertenece a ningún proyecto ni cliente» (solo franjas estimadas). */
export function projectNotFoundFlag(repo: string | null | undefined): TimeEntryFlag {
  return {
    code: TIME_ENTRY_FLAG_CODES.PROJECT_NOT_FOUND,
    message: `Repo sin proyecto: ${repo || "(sin repo)"} no pertenece a ningún proyecto ni cliente`,
    reason: null,
  };
}

/** Texto de una marca para reportes: motivo del sistema + justificación (o «sin justificación»). */
export const flagText = (f: TimeEntryFlag): string => `${f.message} — ${f.reason ? `«${f.reason}»` : NO_JUSTIFICATION_LABEL}`;

/** ¿Trae al menos una marca? */
export const hasFlags = (x: { flags?: TimeEntryFlag[] | null } | null | undefined): boolean =>
  Boolean(x && Array.isArray(x.flags) && x.flags.length > 0);

/** Une listas de marcas sin repetir (mismo código, motivo y justificación = la misma marca). */
export function mergeFlags(...lists: (TimeEntryFlag[] | null | undefined)[]): TimeEntryFlag[] {
  const seen = new Map<string, TimeEntryFlag>();
  for (const list of lists) {
    for (const f of list ?? []) {
      if (f && f.code) seen.set(`${f.code}|${f.message}|${f.reason ?? ""}`, { code: f.code, message: f.message, reason: f.reason ?? null });
    }
  }
  return [...seen.values()];
}

const GH_URL_REPO = /^https?:\/\/(?:www\.)?github\.com\/([A-Za-z0-9._-]+\/[A-Za-z0-9._-]+)/i;
const REF_REPO = /^([A-Za-z0-9._-]+\/[A-Za-z0-9._-]+)[@#]/;

/**
 * Repos `owner/repo` del TRABAJO citado en la evidencia de una entrada: sus
 * commits (`owner/repo@sha` o enlace) y PRs (`owner/repo#123` o enlace), en
 * orden de aparición y sin repetir. Issues y artefactos no cuentan: un issue
 * puede vivir en el repo canónico aunque el código sea de otro fork.
 * El servicio los usa para exigir que la evidencia sea del proyecto registrado.
 */
export function reposFromEvidence(evidence: TimeEntryEvidence | null | undefined): string[] {
  if (!evidence) return [];
  const out: string[] = [];
  for (const raw of [...(evidence.commits ?? []), ...(evidence.prs ?? [])]) {
    const s = String(raw ?? "").trim();
    const m = GH_URL_REPO.exec(s) || REF_REPO.exec(s);
    if (m && !out.includes(m[1])) out.push(m[1]);
  }
  return out;
}

/** Cómo saber, para una franja estimada, si sus repos caen fuera de asignación. */
export interface BucketAssignmentContext {
  /** Proyectos (id + nombre) a los que pertenece un repo. Vacío ⇒ repo sin proyecto. */
  projectsForRepo: (repo: string) => { id: string; label: string }[];
  /** ¿La persona está asignada a ese proyecto en esa fecha? */
  isAssigned: (userId: string, projectId: string, date: string) => boolean;
  /** Nombre de la persona para el motivo. */
  userName?: (userId: string) => string | null | undefined;
}

/** Un repo de una franja que cae fuera de asignación. */
export interface OutOfAllocationRepo {
  repo: string;
  code: TimeEntryFlagCode;
  message: string;
  reason?: string | null;
  /** Proyecto real del repo (`null` si no pertenece a ninguno). */
  projectId: string | null;
  projectLabel: string | null;
}

/** Franja con su marca CALCULADA (nunca se guarda: depende de las asignaciones de hoy). */
export type FlaggedBucket = HourBucket & {
  flags?: TimeEntryFlag[];
  /** Fracción de la hora (0..1) que cae en repos fuera de asignación. */
  outOfAllocationShare?: number;
  outOfAllocationRepos?: OutOfAllocationRepo[];
};

/**
 * Marca una franja por commits. Cada repo que tocó vale `1 / repos` de la hora
 * (mismo reparto que plan vs real): si el repo no pertenece a ningún proyecto
 * ⇒ `PROJECT_NOT_FOUND`; si pertenece a proyectos pero la persona no está
 * asignada a ninguno de ellos ese día ⇒ `NOT_ASSIGNED`. Una franja sin repos
 * (solo soporte) no se marca.
 */
export function flagBucket(b: HourBucket, ctx: BucketAssignmentContext): { flags: TimeEntryFlag[]; outOfAllocationShare: number; outOfAllocationRepos: OutOfAllocationRepo[] } {
  const repos = b.repos ?? [];
  const out: OutOfAllocationRepo[] = [];
  for (const repo of repos) {
    const projects = ctx.projectsForRepo(repo) ?? [];
    if (!projects.length) {
      out.push({ repo, ...projectNotFoundFlag(repo), projectId: null, projectLabel: null });
      continue;
    }
    if (projects.some((p) => ctx.isAssigned(b.userId, p.id, b.date))) continue;
    const first = projects[0];
    const flag = notAssignedFlag(projects.map((p) => p.label || p.id).join(" / "), ctx.userName?.(b.userId) ?? b.userName);
    out.push({ repo, ...flag, projectId: first.id, projectLabel: first.label || first.id });
  }
  return {
    flags: mergeFlags(out.map(({ code, message }) => ({ code, message, reason: null }))),
    outOfAllocationShare: repos.length ? out.length / repos.length : 0,
    outOfAllocationRepos: out,
  };
}

/** Copia de las franjas con su marca calculada (`flags`, `outOfAllocationShare`). */
export function flagBuckets<T extends HourBucket>(buckets: T[], ctx: BucketAssignmentContext): (T & FlaggedBucket)[] {
  return buckets.map((b) => ({ ...b, ...flagBucket(b, ctx) }));
}

export interface TimeEntryEvidence {
  commits?: string[];
  prs?: string[];
  issues?: string[];
  artifacts?: string[];
}

/** Lo que manda el cliente (skill, UI o MCP). */
export interface TimeEntryInput {
  projectId: string;
  date: string;
  minutes: number;
  note: string;
  kind?: TimeEntryKind;
  start?: string | null;
  end?: string | null;
  evidence?: TimeEntryEvidence | null;
  /**
   * #1060: repositorio `owner/nombre` al que se liga la entrada. Opcional en la
   * entrada: el servicio lo deduce de la evidencia o del único repo del
   * proyecto, y rechaza (`REPO_REQUIRED`) si no puede.
   */
  repo?: string | null;
  idempotencyKey: string;
  /**
   * #843: justificación de la persona para registrar en un proyecto al que no
   * está asignada (10–500 caracteres). Obligatoria SOLO en ese caso.
   */
  unassignedReason?: string | null;
  /**
   * #1303: ticket (`work_activities.id`) al que se liga la entrada. Obligatorio
   * (uno de `activityId` o `activityRef`) en las fuentes ui/mcp/skill; si la
   * evidencia cita PRs/issues el servidor la liga solo.
   */
  activityId?: string | null;
  /** #1303: `owner/repo#n` del issue o PR: el servidor busca o crea su ticket. */
  activityRef?: string | null;
  /**
   * #1404: datos de una REUNIÓN — con quiénes fue. Exige `start` y `end` (de qué
   * hora a qué hora). El servidor registra la misma entrada a cada asistente del
   * equipo que tenga identidad de Horas.
   */
  meeting?: TimeEntryMeetingInput | null;
}

/** #1404: con quiénes fue la reunión, como lo manda el cliente. */
export interface TimeEntryMeetingInput {
  /** Colaboradores del equipo (`team_members.id`) que estuvieron, sin contar a quien registra. */
  attendeeTeamMemberIds?: string[];
  /** Texto libre: «alguien más» (cliente, proveedor, invitado…). */
  otherAttendees?: string | null;
}

/** #1404: asistente de una reunión ya resuelto contra Equipo. */
export interface TimeEntryMeetingAttendee {
  teamMemberId: string;
  name: string | null;
  /** Usuario de Horas del asistente; `null` si su ficha no está ligada a una identidad. */
  userId: string | null;
}

/** #1404: reunión guardada en la entrada. */
export interface TimeEntryMeeting {
  attendees: TimeEntryMeetingAttendee[];
  others: string | null;
  /** Quién la registró (usuario de Horas y nombre). */
  organizerUserId: string | null;
  organizerName: string | null;
  /** Entrada original, cuando ésta es la copia que se le registró a un asistente. */
  mirrorOf: string | null;
}

export const MEETING_LIMITS = { ATTENDEES_MAX: 50, OTHERS_MAX: 300, ID_MAX: 200 } as const;

/** Tipo del catálogo que representa una reunión. */
export const MEETING_KIND = "meeting";

export type MeetingValidation =
  | { ok: true; value: { attendeeTeamMemberIds: string[]; otherAttendees: string | null } | null }
  | { ok: false; message: string };

/**
 * #1404: valida `meeting`. `null` ⇒ no vino. Una reunión con asistentes lleva
 * horario: sin `start`/`end` no se sabe de qué hora a qué hora fue.
 */
export function validateMeetingInput(raw: unknown, { hasSchedule = false }: { hasSchedule?: boolean } = {}): MeetingValidation {
  if (raw === undefined || raw === null) return { ok: true, value: null };
  if (typeof raw !== "object" || Array.isArray(raw)) return { ok: false, message: "meeting debe ser un objeto { attendeeTeamMemberIds, otherAttendees }" };
  const m = raw as Record<string, unknown>;
  const ids: string[] = [];
  if (m.attendeeTeamMemberIds !== undefined && m.attendeeTeamMemberIds !== null) {
    if (!Array.isArray(m.attendeeTeamMemberIds)) return { ok: false, message: "meeting.attendeeTeamMemberIds debe ser una lista de ids de colaboradores" };
    for (const x of m.attendeeTeamMemberIds) {
      if (typeof x !== "string" || !x.trim() || x.trim().length > MEETING_LIMITS.ID_MAX) return { ok: false, message: "meeting.attendeeTeamMemberIds debe ser una lista de ids de colaboradores" };
      if (!ids.includes(x.trim())) ids.push(x.trim());
    }
    if (ids.length > MEETING_LIMITS.ATTENDEES_MAX) return { ok: false, message: `meeting.attendeeTeamMemberIds admite hasta ${MEETING_LIMITS.ATTENDEES_MAX} personas` };
  }
  let others: string | null = null;
  if (m.otherAttendees !== undefined && m.otherAttendees !== null && m.otherAttendees !== "") {
    if (typeof m.otherAttendees !== "string") return { ok: false, message: "meeting.otherAttendees debe ser texto" };
    others = m.otherAttendees.trim().replace(/\s+/g, " ") || null;
    if (others && others.length > MEETING_LIMITS.OTHERS_MAX) return { ok: false, message: `meeting.otherAttendees no puede pasar de ${MEETING_LIMITS.OTHERS_MAX} caracteres` };
  }
  if (!ids.length && !others) return { ok: false, message: "meeting debe decir con quiénes fue la reunión: attendeeTeamMemberIds u otherAttendees" };
  if (!hasSchedule) return { ok: false, message: "Una reunión con asistentes lleva horario: manda start y end (HH:mm)" };
  return { ok: true, value: { attendeeTeamMemberIds: ids, otherAttendees: others } };
}

/** #1404: clave de idempotencia de la copia de una reunión para un asistente (determinista por persona). */
export function meetingMirrorKey(idempotencyKey: string, attendeeUserId: string): string {
  let h = 2166136261;
  for (let i = 0; i < attendeeUserId.length; i += 1) {
    h ^= attendeeUserId.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const suffix = `-con-${(h >>> 0).toString(36)}`;
  return `${idempotencyKey.slice(0, 200 - suffix.length)}${suffix}`;
}

/** Documento de `work_time_entries`. */
export interface TimeEntry {
  company_id: string;
  userId: string;
  projectId: string;
  projectLabel: string;
  date: string;
  minutes: number;
  note: string;
  kind: TimeEntryKind;
  start: string | null;
  end: string | null;
  evidence: TimeEntryEvidence | null;
  /** #1060: repositorio `owner/nombre` al que se liga. `null` en entradas anteriores a #1060. */
  repo?: string | null;
  idempotencyKey: string;
  source: TimeEntrySource;
  createdAt: string;
  createdBy: string;
  voided?: { at: string; by: string; reason: string } | null;
  /**
   * Marcas de tiempo fuera de asignación (#843). Vacío ⇒ entrada normal. La
   * entrada cuenta igual; la marca dice qué hay que corregir y por qué la
   * persona trabajó ahí (`reason`).
   */
  flags?: TimeEntryFlag[] | null;
  /**
   * Aprobación del tech lead (#1281). `null` o ausente ⇒ no la necesitó (skill,
   * admin, ≤ 1 h, o anterior a #1281) y cuenta. Pendiente o rechazada ⇒ no cuenta.
   */
  approval?: TimeEntryApproval | null;
  /** #1303: ticket al que pertenece la entrada (`work_activities`). `null` en entradas anteriores o de cargas admin. */
  activityId?: string | null;
  /** Clave y título del ticket al momento de registrar, para listar sin otra lectura. */
  activityKey?: string | null;
  activityTitle?: string | null;
  /** #1404: con quiénes fue la reunión. `null` en lo que no es reunión o es anterior. */
  meeting?: TimeEntryMeeting | null;
}

export const IDEMPOTENCY_KEY_RE = /^\d{4}-\d{2}-\d{2}-[a-z0-9._]+-[a-z0-9-]+$/;
export const PROJECT_ID_RE = /^[a-z0-9][a-z0-9._-]*$/;

/** Repositorio `owner/nombre` de GitHub (#1060). */
export const TIME_ENTRY_REPO_RE = /^[A-Za-z0-9_.-]+\/[A-Za-z0-9_.-]+$/;
const HHMM_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

/** `YYYY-MM-DD` que además es una fecha real (nada de 2026-02-30). */
export function isValidLocalDate(date: unknown): date is string {
  if (typeof date !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return false;
  const d = new Date(`${date}T12:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === date;
}

/** Minutos desde medianoche de un `HH:mm`, o `null` si no es válido. */
export function parseHHmm(value: unknown): number | null {
  if (typeof value !== "string") return null;
  const m = HHMM_RE.exec(value);
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

/** Slug en minúsculas `[a-z0-9-]`, sin acentos. Base de ids de proyecto y de claves. */
export function slugify(text: unknown, maxLength = 60): string {
  return String(text ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, maxLength)
    .replace(/-+$/g, "");
}

/** Id de proyecto normalizado: minúsculas y `[a-z0-9._-]`. */
export function normalizeProjectId(raw: unknown): string {
  return String(raw ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, "-")
    .replace(/^[-._]+|-+$/g, "")
    .slice(0, TIME_ENTRY_LIMITS.PROJECT_ID_MAX);
}

/**
 * Clave de idempotencia determinista: `{date}-{projectId}-{slug}` y, a partir
 * de la segunda entrada con el mismo slug ese día, `-{n}`.
 */
export function buildIdempotencyKey(date: string, projectId: string, slug: string, n?: number | null): string {
  const s = slugify(slug) || "entrada";
  const suffix = n && n > 1 ? `-${Math.floor(n)}` : "";
  return `${date}-${projectId}-${s}${suffix}`;
}

/** `null` si la clave es válida para esa fecha y proyecto; si no, el motivo. */
export function validateIdempotencyKey(key: unknown, date: string, projectId: string): string | null {
  if (typeof key !== "string" || !key) return "idempotencyKey es obligatoria";
  if (key.length > 200) return "idempotencyKey no puede pasar de 200 caracteres";
  if (!IDEMPOTENCY_KEY_RE.test(key)) return "idempotencyKey no cumple el formato {date}-{projectId}-{slug}[-n]";
  const prefix = `${date}-${projectId}-`;
  if (!key.startsWith(prefix)) return `idempotencyKey debe empezar con «${prefix}»`;
  if (!/^[a-z0-9-]+$/.test(key.slice(prefix.length))) return "el slug de idempotencyKey solo admite [a-z0-9-]";
  return null;
}

/** Id del documento en `work_time_entries`. */
export const timeEntryDocId = (companyId: string, userId: string, idempotencyKey: string): string =>
  `${companyId}_${userId}_${idempotencyKey}`;

export type NormalizedTimeEntryInput = Required<Omit<TimeEntryInput, "evidence" | "start" | "end" | "unassignedReason" | "meeting">> & {
  /** #1404: asistentes de la reunión ya validados; `null` si no vino. */
  meeting: { attendeeTeamMemberIds: string[]; otherAttendees: string | null } | null;
  start: string | null;
  end: string | null;
  evidence: TimeEntryEvidence | null;
  /** Justificación fuera de asignación (#843), ya recortada; `null` si no vino. */
  unassignedReason: string | null;
  /** #1303: ticket explícito, si vino. */
  activityId: string | null;
  /** #1303: referencia `owner/repo#n` ya validada, si vino. */
  activityRef: string | null;
};

export type TimeEntryValidation = { ok: true; entry: NormalizedTimeEntryInput } | { ok: false; message: string };

function cleanEvidence(raw: unknown): { ok: true; value: TimeEntryEvidence | null } | { ok: false; message: string } {
  if (raw === undefined || raw === null) return { ok: true, value: null };
  if (typeof raw !== "object" || Array.isArray(raw)) return { ok: false, message: "evidence debe ser un objeto" };
  const out: TimeEntryEvidence = {};
  const src = raw as Record<string, unknown>;
  for (const field of ["commits", "prs", "issues", "artifacts"] as const) {
    const list = src[field];
    if (list === undefined || list === null) continue;
    if (!Array.isArray(list)) return { ok: false, message: `evidence.${field} debe ser una lista` };
    if (list.length > TIME_ENTRY_LIMITS.EVIDENCE_MAX_ITEMS) {
      return { ok: false, message: `evidence.${field} admite máximo ${TIME_ENTRY_LIMITS.EVIDENCE_MAX_ITEMS} elementos` };
    }
    const items = uniq(list.map((v) => String(v ?? "").trim()).filter(Boolean));
    if (items.some((v) => v.length > TIME_ENTRY_LIMITS.EVIDENCE_MAX_LENGTH)) {
      return { ok: false, message: `evidence.${field}: cada elemento admite máximo ${TIME_ENTRY_LIMITS.EVIDENCE_MAX_LENGTH} caracteres` };
    }
    if (items.length) out[field] = items;
  }
  return { ok: true, value: Object.keys(out).length ? out : null };
}

/**
 * Validación pura de UNA entrada (forma, rangos y clave). No sabe nada de
 * proyectos asignados ni del tope diario: eso necesita datos y lo hace el servicio.
 */
export interface TimeEntryValidationOptions {
  /**
   * Claves válidas como `kind` (catálogo de la compañía). Sin ella se aceptan
   * solo los tipos base (`TIME_ENTRY_KINDS`). El permiso POR PUESTO no se
   * decide aquí: lo hace el servicio (`KIND_NOT_ALLOWED`).
   */
  allowedKinds?: readonly string[];
}

export function validateTimeEntryInput(raw: unknown, options: TimeEntryValidationOptions = {}): TimeEntryValidation {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return { ok: false, message: "La entrada debe ser un objeto" };
  const e = raw as Record<string, unknown>;
  const L = TIME_ENTRY_LIMITS;

  const projectId = typeof e.projectId === "string" ? e.projectId.trim() : "";
  if (!projectId) return { ok: false, message: "projectId es obligatorio" };
  if (projectId.length > L.PROJECT_ID_MAX || !PROJECT_ID_RE.test(projectId)) {
    return { ok: false, message: "projectId debe ser un slug en minúsculas ([a-z0-9._-])" };
  }
  if (!isValidLocalDate(e.date)) return { ok: false, message: "date debe ser una fecha local YYYY-MM-DD válida" };
  const date = e.date;
  if (typeof e.minutes !== "number" || !Number.isInteger(e.minutes) || e.minutes < L.MIN_MINUTES || e.minutes > L.MAX_MINUTES) {
    return { ok: false, message: `minutes debe ser un entero entre ${L.MIN_MINUTES} y ${L.MAX_MINUTES}` };
  }
  const note = typeof e.note === "string" ? e.note.trim() : "";
  if (note.length < L.NOTE_MIN || note.length > L.NOTE_MAX) {
    return { ok: false, message: `note debe tener entre ${L.NOTE_MIN} y ${L.NOTE_MAX} caracteres` };
  }
  const kind = e.kind === undefined || e.kind === null || e.kind === "" ? "dev" : e.kind;
  const validKinds: readonly string[] = options.allowedKinds ?? TIME_ENTRY_KINDS;
  if (typeof kind !== "string" || !ACTIVITY_TYPE_KEY_RE.test(kind) || !validKinds.includes(kind)) {
    return { ok: false, message: `kind debe ser uno de: ${validKinds.join(", ")}` };
  }
  let activityId: string | null = null;
  if (e.activityId !== undefined && e.activityId !== null && e.activityId !== "") {
    if (typeof e.activityId !== "string" || e.activityId.trim().length > 200) return { ok: false, message: "activityId debe ser el id de un ticket" };
    activityId = e.activityId.trim();
  }
  let activityRef: string | null = null;
  if (e.activityRef !== undefined && e.activityRef !== null && e.activityRef !== "") {
    const ref = parseGithubRef(e.activityRef);
    if (!ref) return { ok: false, message: "activityRef debe ser owner/repo#n o la URL del issue/PR" };
    activityRef = githubRefKey(ref);
  }
  const hasStart = e.start !== undefined && e.start !== null && e.start !== "";
  const hasEnd = e.end !== undefined && e.end !== null && e.end !== "";
  const startMin = hasStart ? parseHHmm(e.start) : null;
  const endMin = hasEnd ? parseHHmm(e.end) : null;
  if (hasStart && startMin === null) return { ok: false, message: "start debe tener formato HH:mm" };
  if (hasEnd && endMin === null) return { ok: false, message: "end debe tener formato HH:mm" };
  if (startMin !== null && endMin !== null && startMin >= endMin) return { ok: false, message: "start debe ser anterior a end" };
  const evidence = cleanEvidence(e.evidence);
  if (!evidence.ok) return evidence;
  const meeting = validateMeetingInput(e.meeting, { hasSchedule: startMin !== null && endMin !== null });
  if (!meeting.ok) return meeting;
  let repo: string | null = null;
  if (e.repo !== undefined && e.repo !== null && e.repo !== "") {
    if (typeof e.repo !== "string" || !TIME_ENTRY_REPO_RE.test(e.repo.trim())) return { ok: false, message: "repo debe tener formato owner/nombre" };
    repo = e.repo.trim();
  }
  const keyError = validateIdempotencyKey(e.idempotencyKey, date, projectId);
  if (keyError) return { ok: false, message: keyError };
  let unassignedReason: string | null = null;
  if (e.unassignedReason !== undefined && e.unassignedReason !== null && e.unassignedReason !== "") {
    if (typeof e.unassignedReason !== "string") return { ok: false, message: "unassignedReason debe ser texto" };
    unassignedReason = e.unassignedReason.trim().replace(/\s+/g, " ");
    if (unassignedReason.length < UNASSIGNED_REASON_LIMITS.MIN || unassignedReason.length > UNASSIGNED_REASON_LIMITS.MAX) {
      return { ok: false, message: `unassignedReason debe tener entre ${UNASSIGNED_REASON_LIMITS.MIN} y ${UNASSIGNED_REASON_LIMITS.MAX} caracteres` };
    }
  }

  return {
    ok: true,
    entry: {
      projectId,
      date,
      minutes: e.minutes,
      note,
      kind: kind as TimeEntryKind,
      start: hasStart ? (e.start as string) : null,
      end: hasEnd ? (e.end as string) : null,
      evidence: evidence.value,
      repo,
      idempotencyKey: e.idempotencyKey as string,
      unassignedReason,
      activityId,
      activityRef,
      meeting: meeting.value,
    },
  };
}

/** Minutos ya registrados por persona y día, sin contar las anuladas. Clave `userId|date`. */
export function sumMinutesByUserDay(entries: Pick<TimeEntry, "userId" | "date" | "minutes" | "voided">[]): Map<string, number> {
  const out = new Map<string, number>();
  for (const e of entries) {
    if (e.voided) continue;
    const k = `${e.userId}|${e.date}`;
    out.set(k, (out.get(k) ?? 0) + (Number(e.minutes) || 0));
  }
  return out;
}

/** ¿Cabe `minutes` en el día si ya hay `existing` minutos? */
export const fitsInDay = (existing: number, minutes: number): boolean => existing + minutes <= TIME_ENTRY_LIMITS.DAY_MAX_MINUTES;

/** Minutos → horas decimales (2 decimales). */
export const minutesToHours = (minutes: number): number => round2((Number(minutes) || 0) / 60);

/** Orden de lectura: fecha asc, alta asc y, a igualdad, id (orden total y estable). */
export function compareTimeEntries(
  a: { date: string; createdAt?: string | null; id?: string },
  b: { date: string; createdAt?: string | null; id?: string },
): number {
  if (a.date !== b.date) return a.date < b.date ? -1 : 1;
  const ca = String(a.createdAt ?? "");
  const cb = String(b.createdAt ?? "");
  if (ca !== cb) return ca < cb ? -1 : 1;
  const ia = String(a.id ?? "");
  const ib = String(b.id ?? "");
  return ia === ib ? 0 : ia < ib ? -1 : 1;
}

export interface PageMeta {
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
  nextOffset: number | null;
}

/** `limit`/`offset` de una query string, acotados. `null` si no son enteros válidos. */
export function parsePagination(
  query: { limit?: unknown; offset?: unknown } = {},
  { defaultLimit = TIME_ENTRY_LIMITS.PAGE_DEFAULT, maxLimit = TIME_ENTRY_LIMITS.PAGE_MAX } = {},
): { limit: number; offset: number } | null {
  const num = (v: unknown, fallback: number) => (v === undefined || v === null || v === "" ? fallback : Number(v));
  const limit = num(query.limit, defaultLimit);
  const offset = num(query.offset, 0);
  if (!Number.isInteger(limit) || !Number.isInteger(offset) || limit < 1 || offset < 0) return null;
  return { limit: Math.min(limit, maxLimit), offset };
}

/** Página de una lista YA ordenada. `nextOffset` es `null` en la última página. */
export function paginate<T>(rows: T[], limit: number, offset: number): { data: T[]; meta: PageMeta } {
  const data = rows.slice(offset, offset + limit);
  const hasMore = offset + data.length < rows.length;
  return { data, meta: { total: rows.length, limit, offset, hasMore, nextOffset: hasMore ? offset + data.length : null } };
}

/**
 * Instante ISO en hora local con su desplazamiento (`2026-09-14T10:05:00-06:00`).
 * El desplazamiento se calcula para ESE instante, así que respeta cualquier
 * cambio de horario de la zona.
 */
export function toLocalIsoWithOffset(iso: string | null | undefined, timeZone = WORK_HOURS_TIMEZONE): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const parts: Record<string, string> = {};
  const f = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  } as Intl.DateTimeFormatOptions);
  for (const p of f.formatToParts(d)) parts[p.type] = p.value;
  const hour = String(Number(parts.hour) % 24).padStart(2, "0");
  const local = `${parts.year}-${parts.month}-${parts.day}T${hour}:${parts.minute}:${parts.second}`;
  const wholeSeconds = Math.floor(d.getTime() / 1000) * 1000;
  const offsetMin = Math.round((Date.parse(`${local}Z`) - wholeSeconds) / 60000);
  const sign = offsetMin < 0 ? "-" : "+";
  const abs = Math.abs(offsetMin);
  return `${local}${sign}${String(Math.floor(abs / 60)).padStart(2, "0")}:${String(abs % 60).padStart(2, "0")}`;
}

// ---------------------------------------------------------------------------
// ESTIMADO POR BLOQUES DE TIEMPO REAL (#960)
// ---------------------------------------------------------------------------
// Un día-persona SIN entradas se estima con la MISMA regla que la skill
// `cc-hours` (`plugins/cc-hours/skills/cc-hours/scripts/lib/blocks.mjs`):
//
//   1. La evidencia del día (fecha de AUTOR de cada commit, sin repetir SHA, y
//      los eventos de soporte) va a UNA línea de tiempo por día local
//      (America/Mexico_City).
//   2. Una pausa de más de `gapMinutes` (30) sin evidencia corta el bloque. Las
//      pausas largas se quedan VACÍAS: no cuentan.
//   3. Cada bloque dura desde `leadInMinutes` (15) antes del primer evento hasta
//      el último, sin traslaparse con el bloque anterior.
//   4. Los minutos de cada bloque se REPARTEN entre repos (y PRs) según la
//      densidad de commits, en enteros que suman exacto (residuo mayor). Nunca
//      se suma un estimado por unidad.
//
// Antes (#783) un día estimado valía 1 h por cada hora de reloj con al menos un
// commit: un commit suelto contaba 60 min y cuatro commits de 9:00 a 12:00,
// «4 h de reloj». Las franjas (`work_hours`) siguen existiendo como evidencia
// (mapa de calor, pico, conteo de commits); ya no dicen cuánto se trabajó.
// ---------------------------------------------------------------------------

/** Mismos valores por omisión que `DEFAULTS` de `blocks.mjs` (cc-hours). No cambiar uno sin el otro. */
export const BLOCK_ESTIMATE_DEFAULTS = Object.freeze({ gapMinutes: 30, leadInMinutes: 15 });

/** Método del estimado de un día sin entradas. */
export const ESTIMATE_METHOD_BLOCKS = "blocks" as const;
export type EstimateMethod = typeof ESTIMATE_METHOD_BLOCKS;

/** Clave de repo (y de PR) para evidencia sin repo: eventos de soporte, commits sin PR. */
export const NO_REPO_KEY = "";

const MINUTES_PER_DAY = 1440;

/** Un punto de evidencia de la línea de tiempo. */
export interface EstimateEvent {
  /** Instante (ISO con zona, ms o Date). Para commits: `authoredAt`. */
  at?: string | number | Date | null;
  /**
   * Alternativa a `at` para evidencia sin instante exacto (respaldo de franjas
   * cuyos commits ya no se pueden leer): fecha y minuto LOCALES del día.
   */
  date?: string | null;
  minute?: number | null;
  userId?: string | null;
  /** SHA del commit: dos eventos con el mismo SHA son UN commit. */
  sha?: string | null;
  /** Id de un evento de soporte: mismo criterio de deduplicado. */
  id?: string | null;
  kind?: "commit" | "support" | null;
  repos?: string[] | null;
  repo?: string | null;
  /** Claves `owner/repo#123`. */
  pullRequests?: string[] | null;
  /** Peso al repartir el bloque. Por omisión: commit 1, soporte 0.5 (review/comentario en cc-hours). */
  weight?: number | null;
}

export interface EstimateBlock {
  date: string;
  /** `HH:mm` local. */
  start: string;
  end: string;
  startMin: number;
  endMin: number;
  minutes: number;
  events: number;
}

export interface DayEstimate {
  method: EstimateMethod;
  /** Minutos del día: suma de los bloques. */
  minutes: number;
  blocks: EstimateBlock[];
  /** Minutos por repo (`""` = soporte sin repo). Suman exactamente `minutes`. */
  byRepo: Record<string, number>;
  /** Minutos por PR (`""` = sin PR). Suman exactamente `minutes`. */
  byPullRequest: Record<string, number>;
  /** Eventos distintos que usó el cálculo. */
  events: number;
}

export interface BlockEstimateOptions {
  gapMinutes?: number | null;
  leadInMinutes?: number | null;
  timeZone?: string | null;
}

const clockFormatters = new Map<string, Intl.DateTimeFormat>();

/** Fecha local y minuto del día (0..1439) de un instante. `null` si no es válido. */
export function localClock(value: string | number | Date | null | undefined, timeZone = WORK_HOURS_TIMEZONE): { date: string; minute: number } | null {
  if (value === null || value === undefined || value === "") return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  let f = clockFormatters.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    } as Intl.DateTimeFormatOptions);
    clockFormatters.set(timeZone, f);
  }
  const parts: Record<string, string> = {};
  for (const p of f.formatToParts(d)) parts[p.type] = p.value;
  return { date: `${parts.year}-${parts.month}-${parts.day}`, minute: (Number(parts.hour) % 24) * 60 + Number(parts.minute) };
}

const hhmm = (minute: number): string => `${String(Math.floor(minute / 60)).padStart(2, "0")}:${String(minute % 60).padStart(2, "0")}`;

/**
 * Reparte `total` minutos entre unidades según su peso, con enteros que suman
 * EXACTAMENTE `total` (residuo mayor; empates por clave, determinista). Pesos
 * todos en 0 ⇒ partes iguales. Port de `splitMinutes` de cc-hours.
 */
export function splitMinutesByWeight<T extends { key: string; weight: number }>(total: number, units: T[]): (T & { minutes: number })[] {
  if (!units.length) return [];
  const allZero = units.every((u) => !(u.weight > 0));
  const weights = units.map((u) => (allZero ? 1 : Math.max(0, u.weight || 0)));
  const W = weights.reduce((a, b) => a + b, 0);
  const raw = weights.map((w) => (total * w) / W);
  const base = raw.map(Math.floor);
  let rest = total - base.reduce((a, b) => a + b, 0);
  const order = raw
    .map((r, i) => ({ i, frac: r - Math.floor(r), key: units[i].key }))
    .sort((a, b) => b.frac - a.frac || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
  for (const o of order) {
    if (rest <= 0) break;
    base[o.i] += 1;
    rest -= 1;
  }
  return units.map((u, i) => ({ ...u, minutes: base[i] }));
}

const eventRepos = (e: EstimateEvent): string[] => {
  const list = (e.repos ?? []).filter((r) => typeof r === "string" && r);
  if (list.length) return uniq(list);
  return e.repo ? [e.repo] : [NO_REPO_KEY];
};
const eventWeight = (e: EstimateEvent): number => {
  const w = Number(e.weight);
  if (e.weight !== null && e.weight !== undefined && Number.isFinite(w)) return Math.max(0, w);
  return e.kind === "support" ? 0.5 : 1;
};

function addSplit(target: Record<string, number>, total: number, weights: Map<string, number>): void {
  const units = [...weights.entries()].map(([key, weight]) => ({ key, weight })).sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
  for (const u of splitMinutesByWeight(total, units)) if (u.minutes > 0) target[u.key] = (target[u.key] ?? 0) + u.minutes;
}

const emptyEstimate = (): DayEstimate => ({ method: ESTIMATE_METHOD_BLOCKS, minutes: 0, blocks: [], byRepo: {}, byPullRequest: {}, events: 0 });

/**
 * Minutos REALES de trabajo a partir de la evidencia (commits + soporte), con
 * bloques de reloj. Pura. Si la evidencia cae en varios días locales, cada día
 * arma sus propios bloques (nunca cruzan medianoche) y el total los suma.
 *
 *   • un commit suelto ⇒ 15 min;
 *   • commits cada 20 min de 09:00 a 12:00 ⇒ 08:45–12:00 = 195 min;
 *   • 09:00 y 11:00 ⇒ dos bloques de 15 min (la pausa de 2 h no cuenta).
 */
export function estimateDayFromEvents(events: EstimateEvent[] | null | undefined, opts: BlockEstimateOptions = {}): DayEstimate {
  const gapMinutes = Number.isFinite(Number(opts.gapMinutes)) && opts.gapMinutes !== null && opts.gapMinutes !== undefined ? Math.max(0, Number(opts.gapMinutes)) : BLOCK_ESTIMATE_DEFAULTS.gapMinutes;
  const leadInMinutes =
    Number.isFinite(Number(opts.leadInMinutes)) && opts.leadInMinutes !== null && opts.leadInMinutes !== undefined ? Math.max(0, Number(opts.leadInMinutes)) : BLOCK_ESTIMATE_DEFAULTS.leadInMinutes;
  const timeZone = opts.timeZone || WORK_HOURS_TIMEZONE;

  // 1 · puntos válidos, sin commits (ni eventos) repetidos
  const seen = new Set<string>();
  const points: { date: string; minute: number; ms: number; tie: string; e: EstimateEvent }[] = [];
  for (const e of events ?? []) {
    if (!e) continue;
    const dedupe = e.sha ? `sha:${e.sha}` : e.id ? `id:${e.id}` : null;
    if (dedupe && seen.has(dedupe)) continue;
    let date: string;
    let minute: number;
    let ms: number;
    if (e.at !== null && e.at !== undefined && e.at !== "") {
      const c = localClock(e.at, timeZone);
      if (!c) continue;
      date = c.date;
      minute = c.minute;
      ms = (e.at instanceof Date ? e.at : new Date(e.at)).getTime();
    } else if (e.date && isValidLocalDate(e.date) && Number.isFinite(Number(e.minute))) {
      date = e.date;
      minute = Math.min(MINUTES_PER_DAY - 1, Math.max(0, Math.floor(Number(e.minute))));
      ms = Date.parse(`${e.date}T00:00:00Z`) + minute * 60000;
    } else continue;
    if (dedupe) seen.add(dedupe);
    points.push({ date, minute, ms, tie: String(e.sha || e.id || ""), e });
  }
  if (!points.length) return emptyEstimate();
  points.sort((a, b) => (a.date === b.date ? a.minute - b.minute || a.ms - b.ms || a.tie.localeCompare(b.tie) : a.date < b.date ? -1 : 1));

  // 2 · bloques por día local
  const out = emptyEstimate();
  out.events = points.length;
  let i = 0;
  while (i < points.length) {
    const date = points[i].date;
    const day: (typeof points)[] = [];
    let current: typeof points | null = null;
    let last = -Infinity;
    for (; i < points.length && points[i].date === date; i++) {
      const p = points[i];
      if (current && p.minute - last <= gapMinutes) current.push(p);
      else {
        current = [p];
        day.push(current);
      }
      last = p.minute;
    }
    let prevEnd = 0;
    for (const group of day) {
      const first = group[0].minute;
      const lastMinute = group[group.length - 1].minute;
      const start = Math.max(0, first - leadInMinutes, prevEnd);
      let end = lastMinute;
      if (end <= start) end = Math.min(start + 1, MINUTES_PER_DAY);
      const minutes = end - start;
      out.blocks.push({ date, start: hhmm(start), end: hhmm(Math.min(end, MINUTES_PER_DAY - 1)), startMin: start, endMin: end, minutes, events: group.length });
      out.minutes += minutes;
      prevEnd = end;

      // 3 · reparto del bloque por densidad de commits (repos y PRs)
      const repoW = new Map<string, number>();
      const prW = new Map<string, number>();
      for (const { e } of group) {
        const w = eventWeight(e);
        const repos = eventRepos(e);
        for (const r of repos) repoW.set(r, (repoW.get(r) ?? 0) + w / repos.length);
        const prs = uniq((e.pullRequests ?? []).filter(Boolean));
        const keys = prs.length ? prs : [NO_REPO_KEY];
        for (const k of keys) prW.set(k, (prW.get(k) ?? 0) + w / keys.length);
      }
      addSplit(out.byRepo, minutes, repoW);
      addSplit(out.byPullRequest, minutes, prW);
    }
  }
  return out;
}

/** Clave `userId|fecha` de un día-persona. */
const dayKey = (userId: string, date: string): string => `${userId}|${date}`;

/**
 * Eventos de estimación de un commit persistido (`work_commits`): fecha de
 * autor, SHA, repos y PRs.
 */
export function commitToEstimateEvent(c: {
  sha?: string | null;
  userId?: string | null;
  authoredAt?: string | null;
  repos?: string[] | null;
  repo?: string | null;
  pullRequests?: (PullRequestRef | string)[] | null;
}): EstimateEvent {
  return {
    kind: "commit",
    at: c.authoredAt ?? null,
    sha: c.sha ?? null,
    userId: c.userId ?? null,
    repos: c.repos ?? (c.repo ? [c.repo] : []),
    pullRequests: (c.pullRequests ?? []).map((p) => (typeof p === "string" ? p : prKey(p))),
  };
}

/** Evento de estimación de un evento de soporte (`work_support_events`). */
export function supportToEstimateEvent(e: { eventId?: string | null; id?: string | null; userId?: string | null; occurredAt?: string | null }): EstimateEvent {
  return { kind: "support", at: e.occurredAt ?? null, id: e.eventId || e.id || null, userId: e.userId ?? null, repos: [] };
}

/**
 * Estimado por bloques de cada día-persona de unas franjas. Usa los eventos con
 * instante (`events`, con `userId`) y, para cada franja cuya evidencia no llegó
 * en `events` (commits que ya no se pueden leer), un punto de respaldo al
 * inicio de su hora: 15 min, nunca 1 h. Devuelve `userId|fecha` → estimado.
 */
export function estimateBucketDays(
  buckets: HourBucket[],
  events: EstimateEvent[] | null | undefined = [],
  opts: BlockEstimateOptions = {},
): Map<string, DayEstimate> {
  const timeZone = opts.timeZone || WORK_HOURS_TIMEZONE;
  const wanted = new Set(buckets.map((b) => dayKey(b.userId, b.date)));
  const byDay = new Map<string, EstimateEvent[]>();
  const covered = new Set<string>();
  for (const e of events ?? []) {
    if (!e || !e.userId) continue;
    const date = e.at !== null && e.at !== undefined && e.at !== "" ? localClock(e.at, timeZone)?.date : e.date;
    if (!date) continue;
    const key = dayKey(e.userId, date);
    if (!wanted.has(key)) continue;
    if (!byDay.has(key)) byDay.set(key, []);
    byDay.get(key)!.push(e);
    if (e.sha) covered.add(`sha:${e.sha}`);
    if (e.id) covered.add(`id:${e.id}`);
  }
  for (const b of buckets) {
    const ids = [...(b.commitShas ?? []).map((s) => `sha:${s}`), ...(((b as { supportEventIds?: string[] }).supportEventIds ?? []) as string[]).map((s) => `id:${s}`)];
    if (ids.some((k) => covered.has(k))) continue;
    const key = dayKey(b.userId, b.date);
    if (!byDay.has(key)) byDay.set(key, []);
    byDay.get(key)!.push({
      kind: b.commitCount > 0 ? "commit" : "support",
      userId: b.userId,
      date: b.date,
      minute: b.hour * 60,
      repos: b.repos,
      pullRequests: b.pullRequests,
      weight: Math.max(1, b.commitCount || 0),
    });
  }
  const out = new Map<string, DayEstimate>();
  for (const [key, list] of byDay) out.set(key, estimateDayFromEvents(list, opts));
  return out;
}

// ---------------------------------------------------------------------------
// HORAS QUE CUENTAN: ENTRADAS EN MINUTOS + ESTIMADO POR BLOQUES (#783 / #960)
// ---------------------------------------------------------------------------
// Regla única para tablero, nómina, reportes y plan vs real:
//
//   • Un día-persona con al menos UNA entrada vigente (no anulada) cuenta la
//     suma de minutos de sus entradas. Sus franjas por commit/soporte son solo
//     evidencia: NO suman.
//   • Un día-persona sin entradas vigentes se ESTIMA con bloques de tiempo real
//     (#960, ver arriba): pausas de más de 30 min no cuentan y cada bloque
//     empieza 15 min antes de su primer commit. Marcado como ESTIMADO
//     (`estimated: true`, `source: "commits"`, `estimateMethod: "blocks"`).
//   • Las entradas anuladas nunca cuentan, ni para sumar ni para decidir que el
//     día «tiene entradas».
// ---------------------------------------------------------------------------

/** De dónde salen las horas de un día-persona. */
export const HOURS_SOURCES = { ENTRIES: "time_entries", COMMITS: "commits" } as const;
export type HoursSource = (typeof HOURS_SOURCES)[keyof typeof HOURS_SOURCES];

/** Lo mínimo de una entrada que necesita el cálculo. */
export type CountableTimeEntry = Pick<TimeEntry, "userId" | "date" | "minutes" | "projectId"> & {
  id?: string;
  projectLabel?: string | null;
  voided?: TimeEntry["voided"];
  /** Marcas fuera de asignación (#843): cuentan igual, se exponen aparte. */
  flags?: TimeEntryFlag[] | null;
  /** Aprobación del tech lead (#1281): pendiente o rechazada ⇒ no cuenta. */
  approval?: TimeEntryApproval | null;
};

/**
 * ¿La entrada cuenta? Anuladas y minutos no positivos, nunca. Tampoco las que
 * esperan aprobación del tech lead ni las rechazadas (#1281): las pendientes se
 * suman aparte como «Horas por aprobar» (`isPendingEntry`).
 */
export const isCountedEntry = (e: Pick<CountableTimeEntry, "minutes" | "voided" | "approval"> | null | undefined): boolean => {
  if (!e || e.voided || !(Number(e.minutes) > 0)) return false;
  const status = approvalStatusOf(e);
  return status !== "pending" && status !== "rejected";
};

/** Minutos en `h:mm` (95 → «1:35»). */
export function formatMinutes(minutes: number): string {
  const total = Math.max(0, Math.round(Number(minutes) || 0));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

/** Un día-persona ya resuelto con la regla de conteo. */
export interface ResolvedDay {
  userId: string;
  userName: string | null;
  date: string;
  source: HoursSource;
  /** `true` ⇒ no hubo entradas: las horas son el estimado por bloques (#960). */
  estimated: boolean;
  /** Minutos que cuentan (en un día estimado, los minutos de sus bloques de tiempo real). */
  minutes: number;
  hours: number;
  /** Entradas vigentes que suman (tras el filtro de proyectos, si lo hay). */
  entryCount: number;
  /** Evidencia: franjas del día (cuenten o no) y sus commits. */
  bucketCount: number;
  commitCount: number;
  /** Minutos por proyecto (solo días con entradas). */
  projects: { projectId: string; projectLabel: string; minutes: number; outOfAllocationMinutes: number }[];
  /**
   * Parte de `minutes` fuera de asignación (#843): entradas marcadas o, en un
   * día estimado, la fracción de sus franjas en repos fuera de asignación.
   * CUENTA en `minutes`; se expone aparte para mostrarla.
   */
  outOfAllocationMinutes: number;
  /** Motivos de la parte fuera de asignación (sin repetir). */
  flags: TimeEntryFlag[];
  /** Solo en días estimados (#960): método y bloques de reloj que dieron los minutos. */
  estimateMethod?: EstimateMethod;
  blocks?: EstimateBlock[];
  /** Solo en días estimados: minutos por repo (`""` = soporte). */
  byRepo?: Record<string, number>;
}

export interface ResolveDaysOptions {
  from?: string | null;
  to?: string | null;
  userId?: string | null;
  /** Solo franjas que tocan estos repos (reporte a clientes / filtro del tablero). */
  repos?: string[] | null;
  /** Solo entradas de estos proyectos. `null` ⇒ todas. */
  projectIds?: string[] | null;
  /** Nombres por `userId` para días que solo tienen entradas (sin franjas). */
  userNames?: Record<string, string | null> | null;
  /**
   * Evidencia con instante para el estimado por bloques (#960): commits
   * (`commitToEstimateEvent`) y eventos de soporte, con `userId`. Sin ella, cada
   * franja aporta un punto al inicio de su hora (15 min).
   */
  events?: EstimateEvent[] | null;
  /** Parámetros del estimado (por omisión, los de cc-hours: 30 / 15). */
  estimate?: BlockEstimateOptions | null;
}

/**
 * Resuelve cada día-persona con la regla de conteo. OJO con los filtros: el día
 * «tiene entradas» se decide con TODAS sus entradas vigentes, no solo con las
 * del proyecto filtrado. Si no, un día con entradas de otro proyecto caería al
 * estimado por commits del proyecto filtrado y se contaría dos veces.
 */
export function resolveUserDays(buckets: HourBucket[], entries: CountableTimeEntry[], opts: ResolveDaysOptions = {}): ResolvedDay[] {
  return resolveDaysWithEstimates(buckets, entries, opts).days;
}

/** Repos fuera de asignación de las franjas (marcadas) de un día-persona, por repo. */
function outOfAllocationReposByDay(buckets: HourBucket[]): Map<string, Map<string, OutOfAllocationRepo>> {
  const out = new Map<string, Map<string, OutOfAllocationRepo>>();
  for (const b of buckets) {
    const list = (b as FlaggedBucket).outOfAllocationRepos ?? [];
    if (!list.length) continue;
    const key = dayKey(b.userId, b.date);
    if (!out.has(key)) out.set(key, new Map());
    const m = out.get(key)!;
    for (const r of list) if (!m.has(r.repo)) m.set(r.repo, r);
  }
  return out;
}

function resolveDaysWithEstimates(
  buckets: HourBucket[],
  entries: CountableTimeEntry[],
  opts: ResolveDaysOptions = {},
): { days: ResolvedDay[]; estimates: Map<string, DayEstimate> } {
  const inRange = (d: string) => (!opts.from || d >= opts.from) && (!opts.to || d <= opts.to);
  const okUser = (u: string) => !opts.userId || u === opts.userId;
  const counted = entries.filter((e) => isCountedEntry(e) && inRange(e.date) && okUser(e.userId));
  const withEntries = new Set(counted.map((e) => `${e.userId}|${e.date}`));
  const projectFilter = opts.projectIds ? new Set(opts.projectIds) : null;
  const repoPatterns = opts.repos && opts.repos.length ? opts.repos : null;
  const repoOk = (r: string) => !repoPatterns || (r !== NO_REPO_KEY && repoMatchesAny(repoPatterns, r));
  // El bloque es el reloj de la PERSONA: se arma con TODA la evidencia del día,
  // no solo con la de los repos filtrados. El filtro decide qué parte se
  // muestra, nunca cuánto dura el bloque.
  const dayBuckets = filterBuckets(buckets, { from: opts.from, to: opts.to, userId: opts.userId });
  const shown = repoPatterns ? filterBuckets(dayBuckets, { repos: repoPatterns }) : dayBuckets;
  const estimates = estimateBucketDays(dayBuckets, opts.events ?? [], opts.estimate ?? {});
  const outRepos = outOfAllocationReposByDay(dayBuckets);

  const days = new Map<string, ResolvedDay>();
  const get = (userId: string, date: string): ResolvedDay => {
    const key = `${userId}|${date}`;
    let d = days.get(key);
    if (!d) {
      d = {
        userId,
        userName: opts.userNames?.[userId] ?? null,
        date,
        source: withEntries.has(key) ? HOURS_SOURCES.ENTRIES : HOURS_SOURCES.COMMITS,
        estimated: !withEntries.has(key),
        minutes: 0,
        hours: 0,
        entryCount: 0,
        bucketCount: 0,
        commitCount: 0,
        projects: [],
        outOfAllocationMinutes: 0,
        flags: [],
      };
      days.set(key, d);
    }
    return d;
  };

  for (const b of shown) {
    const d = get(b.userId, b.date);
    d.bucketCount += 1;
    d.commitCount += b.commitCount;
    if (!d.userName && b.userName) d.userName = b.userName;
  }
  for (const e of counted) {
    if (projectFilter && !projectFilter.has(e.projectId)) continue;
    const d = get(e.userId, e.date);
    const minutes = Number(e.minutes) || 0;
    const flagged = hasFlags(e);
    d.entryCount += 1;
    d.minutes += minutes;
    if (flagged) {
      d.outOfAllocationMinutes += minutes;
      d.flags = mergeFlags(d.flags, e.flags);
    }
    const p = d.projects.find((x) => x.projectId === e.projectId);
    if (p) {
      p.minutes += minutes;
      if (flagged) p.outOfAllocationMinutes += minutes;
    } else d.projects.push({ projectId: e.projectId, projectLabel: e.projectLabel || e.projectId, minutes, outOfAllocationMinutes: flagged ? minutes : 0 });
  }

  const out: ResolvedDay[] = [];
  for (const [key, d] of days) {
    if (d.estimated) {
      // #960: minutos de los bloques de tiempo real (con filtro de repos, solo
      // la parte de esos repos). #843: la parte en repos fuera de asignación.
      const est = estimates.get(key) ?? emptyEstimate();
      const byRepo: Record<string, number> = {};
      for (const [r, m] of Object.entries(est.byRepo)) if (repoOk(r)) byRepo[r] = m;
      d.minutes = repoPatterns ? Object.values(byRepo).reduce((n, m) => n + m, 0) : est.minutes;
      const outs = outRepos.get(key);
      if (outs) {
        let outMinutes = 0;
        const flags: TimeEntryFlag[] = [];
        for (const [repo, info] of outs) {
          const m = byRepo[repo] ?? 0;
          if (m <= 0) continue;
          outMinutes += m;
          flags.push({ code: info.code, message: info.message, reason: null });
        }
        d.outOfAllocationMinutes = Math.min(d.minutes, outMinutes);
        d.flags = mergeFlags(flags);
      }
      d.estimateMethod = ESTIMATE_METHOD_BLOCKS;
      d.blocks = est.blocks;
      d.byRepo = byRepo;
    }
    d.hours = minutesToHours(d.minutes);
    d.projects.sort((a, b) => b.minutes - a.minutes || a.projectId.localeCompare(b.projectId));
    // Un día con entradas SOLO de otros proyectos no pertenece a este filtro.
    if (d.minutes > 0) out.push(d);
  }
  return {
    days: out.sort((a, b) => (a.date === b.date ? a.userId.localeCompare(b.userId) : a.date < b.date ? -1 : 1)),
    estimates,
  };
}

/** Un día-persona con tiempo fuera de asignación (#843). */
export interface OutOfAllocationItem {
  userId: string;
  userName: string | null;
  date: string;
  minutes: number;
  hours: number;
  estimated: boolean;
  flags: TimeEntryFlag[];
}

/** Resumen del tablero con la regla de conteo de #783. */
export interface RecordedHoursSummary extends WorkHoursSummary {
  /** `time_entries` (todo registrado), `commits` (todo estimado), `mixed` o `none`. */
  hoursSource: HoursSource | "mixed" | "none";
  totalMinutes: number;
  recordedHours: number;
  estimatedHours: number;
  /** Días-persona estimados por bloques de tiempo real. */
  estimatedDays: number;
  /** Cómo se estiman los días sin entradas (#960). */
  estimateMethod: EstimateMethod;
  byDay: { date: string; hours: number; recordedHours: number; estimatedHours: number; estimated: boolean; outOfAllocationMinutes: number }[];
  byProject: { projectId: string; projectLabel: string; minutes: number; hours: number; outOfAllocationMinutes: number }[];
  /** Minutos fuera de asignación (#843). YA están sumados en el total: esto solo los separa. */
  outOfAllocationMinutes: number;
  outOfAllocationHours: number;
  /** Cada día-persona con tiempo fuera de asignación y sus motivos. */
  outOfAllocation: OutOfAllocationItem[];
  /** Cada día-persona con su procedencia. */
  days: ResolvedDay[];
  /**
   * Actividad SIN la regla de conteo (todos los días, tengan entradas o no),
   * medida con bloques de tiempo real (#960). `bucketHours` = franjas con
   * actividad (horas de reloj tocadas), solo como referencia.
   */
  evidence: { totalHours: number; bucketHours: number; totalCommits: number; byDay: { date: string; hours: number }[]; byUser: UserHours[] };
  /**
   * #1063: minutos de `byRepo` abiertos por persona. Es INTERNO del servidor
   * (alimenta `computeRepoCost`): lleva ids de usuario, así que la API lo quita
   * antes de responder y `anonymizeSummary` lo vacía.
   */
  byRepoUser?: RepoUserMinutes[];
  /**
   * #1342: de dónde sale la diferencia entre lo que cuenta y la actividad por
   * commits de `byRepo` / `pullRequests`. Lleva notas de entradas y nombres: la
   * vista del cliente y `anonymizeSummary` lo quitan.
   */
  repoBreakdown?: RepoHoursBreakdown;
  /** #1342: lo que cuenta por tipo de actividad (reuniones del CRM, ventas, marketing, desarrollo…). */
  byKind?: KindHours[];
}

/** Minutos de actividad de UNA persona en UN repo (bloques de tiempo real, #960). */
export interface RepoUserMinutes {
  repo: string;
  userId: string;
  minutes: number;
}

/**
 * Resumen con la regla de conteo. Lo que se suma (total, personas, series por
 * día/semana/mes, proyectos) sale de `resolveUserDays`. Lo que describe
 * ACTIVIDAD —repos y PRs— se mide con los mismos bloques de tiempo real (#960)
 * de TODOS los días; el mapa de calor, el pico, los commits y la franja horaria
 * de cada persona siguen saliendo de las franjas: es evidencia, no horas pagadas.
 */
export function summarizeRecordedHours(
  buckets: HourBucket[],
  entries: CountableTimeEntry[],
  opts: SummarizeOptions & Pick<ResolveDaysOptions, "projectIds" | "userNames"> = {},
): RecordedHoursSummary {
  const evidence = summarizeWorkHours(buckets, opts);
  const { days, estimates } = resolveDaysWithEstimates(buckets, entries, opts);
  const evidenceByUser = new Map(evidence.byUser.map((u) => [u.userId, u]));

  type Acc = { userId: string; userName: string | null; commits: number; firstHour: number | null; lastHour: number | null; minutes: number; recorded: number; estimated: number; estimatedDays: number; outMinutes: number; dates: Set<string> };
  const users = new Map<string, Acc>();
  const byDay = new Map<string, { recorded: number; estimated: number; estimatedDay: boolean; outMinutes: number }>();
  const weekly = new Map<string, number>();
  const monthly = new Map<string, number>();
  const byProject = new Map<string, { projectId: string; projectLabel: string; minutes: number; outOfAllocationMinutes: number }>();
  let recordedMinutes = 0;
  let estimatedMinutes = 0;
  let outMinutes = 0;

  for (const d of days) {
    const ev = evidenceByUser.get(d.userId);
    let u = users.get(d.userId);
    if (!u) {
      u = {
        userId: d.userId,
        userName: d.userName ?? ev?.userName ?? null,
        commits: ev?.commits ?? 0,
        firstHour: ev?.firstHour ?? null,
        lastHour: ev?.lastHour ?? null,
        minutes: 0,
        recorded: 0,
        estimated: 0,
        estimatedDays: 0,
        outMinutes: 0,
        dates: new Set(),
      };
      users.set(d.userId, u);
    }
    u.dates.add(d.date);
    u.minutes += d.minutes;
    const dayOut = d.outOfAllocationMinutes || 0;
    u.outMinutes += dayOut;
    outMinutes += dayOut;
    const slot = byDay.get(d.date) ?? { recorded: 0, estimated: 0, estimatedDay: false, outMinutes: 0 };
    slot.outMinutes += dayOut;
    if (d.estimated) {
      estimatedMinutes += d.minutes;
      u.estimated += d.minutes / 60;
      u.estimatedDays += 1;
      slot.estimated += d.minutes / 60;
      slot.estimatedDay = true;
    } else {
      recordedMinutes += d.minutes;
      u.recorded += d.minutes / 60;
      slot.recorded += d.minutes / 60;
      for (const p of d.projects) {
        const prev = byProject.get(p.projectId) ?? { projectId: p.projectId, projectLabel: p.projectLabel, minutes: 0, outOfAllocationMinutes: 0 };
        prev.minutes += p.minutes;
        prev.outOfAllocationMinutes += p.outOfAllocationMinutes || 0;
        byProject.set(p.projectId, prev);
      }
    }
    byDay.set(d.date, slot);
    const hours = d.minutes / 60;
    weekly.set(weekStartOf(d.date), (weekly.get(weekStartOf(d.date)) ?? 0) + hours);
    monthly.set(d.date.slice(0, 7), (monthly.get(d.date.slice(0, 7)) ?? 0) + hours);
  }

  const sortByKey = <V>(m: Map<string, V>) => [...m.entries()].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  const estimatedDays = days.filter((d) => d.estimated).length;
  const activity = blockActivity(buckets, estimates, evidence, opts);

  return {
    ...evidence,
    byRepo: activity.byRepo,
    byRepoUser: activity.byRepoUser,
    pullRequests: activity.pullRequests,
    hoursWithoutPullRequest: activity.hoursWithoutPullRequest,
    repoBreakdown: repoHoursBreakdown(buckets, entries, days, estimates, opts),
    byKind: kindHours(entries, days, opts),
    totalHours: round2((recordedMinutes + estimatedMinutes) / 60),
    activeUsers: users.size,
    byUser: [...users.values()]
      .map((u) => ({
        userId: u.userId,
        userName: u.userName,
        hours: round2(u.recorded + u.estimated),
        days: u.dates.size,
        commits: u.commits,
        firstHour: u.firstHour,
        lastHour: u.lastHour,
        minutes: Math.round(u.minutes),
        recordedHours: round2(u.recorded),
        estimatedHours: round2(u.estimated),
        estimatedDays: u.estimatedDays,
        outOfAllocationMinutes: Math.round(u.outMinutes),
      }))
      .sort((a, b) => b.hours - a.hours),
    byDay: sortByKey(byDay).map(([date, v]) => ({
      date,
      hours: round2(v.recorded + v.estimated),
      recordedHours: round2(v.recorded),
      estimatedHours: round2(v.estimated),
      estimated: v.estimatedDay,
      outOfAllocationMinutes: Math.round(v.outMinutes),
    })),
    weekly: sortByKey(weekly).map(([weekStart, hours]) => ({ weekStart, hours: round2(hours) })),
    monthly: sortByKey(monthly).map(([month, hours]) => ({ month, hours: round2(hours) })),
    hoursSource: days.length === 0 ? "none" : estimatedDays === 0 ? HOURS_SOURCES.ENTRIES : estimatedDays === days.length ? HOURS_SOURCES.COMMITS : "mixed",
    totalMinutes: Math.round(recordedMinutes + estimatedMinutes),
    recordedHours: round2(recordedMinutes / 60),
    estimatedHours: round2(estimatedMinutes / 60),
    estimatedDays,
    estimateMethod: ESTIMATE_METHOD_BLOCKS,
    byProject: [...byProject.values()]
      .map((p) => ({ ...p, hours: minutesToHours(p.minutes) }))
      .sort((a, b) => b.minutes - a.minutes || a.projectId.localeCompare(b.projectId)),
    outOfAllocationMinutes: Math.round(outMinutes),
    outOfAllocationHours: minutesToHours(outMinutes),
    outOfAllocation: days
      .filter((d) => d.outOfAllocationMinutes > 0)
      .map((d) => ({
        userId: d.userId,
        userName: d.userName,
        date: d.date,
        minutes: d.outOfAllocationMinutes,
        hours: minutesToHours(d.outOfAllocationMinutes),
        estimated: d.estimated,
        flags: d.flags,
      })),
    days,
    evidence: { totalHours: activity.totalHours, bucketHours: evidence.totalHours, totalCommits: evidence.totalCommits, byDay: activity.byDay, byUser: activity.byUser },
  };
}

/**
 * Actividad por repo, PR, día y persona medida con los bloques de tiempo real
 * de TODOS los días-persona del rango (#960). Repos y PRs respetan el filtro
 * de repos (qué se muestra); los minutos salen del bloque completo.
 */
function blockActivity(
  buckets: HourBucket[],
  estimates: Map<string, DayEstimate>,
  evidence: WorkHoursSummary,
  opts: SummarizeOptions,
): Pick<WorkHoursSummary, "byRepo" | "pullRequests" | "hoursWithoutPullRequest" | "byDay" | "byUser"> & { totalHours: number; byRepoUser: RepoUserMinutes[] } {
  const repoPatterns = opts.repos && opts.repos.length ? opts.repos : null;
  const repoOk = (r: string) => !repoPatterns || (r !== NO_REPO_KEY && repoMatchesAny(repoPatterns, r));
  const shownDays = new Set(filterBuckets(buckets, opts).map((b) => dayKey(b.userId, b.date)));
  const byRepo = new Map<string, number>();
  // #1063: los mismos minutos de `byRepo`, abiertos por persona (para el costo).
  const byRepoUser = new Map<string, RepoUserMinutes>();
  const byPr = new Map<string, number>();
  const byDay = new Map<string, number>();
  const byUser = new Map<string, number>();
  let shownRepoMinutes = 0;
  let prMinutes = 0;
  let noPrMinutes = 0;
  let total = 0;
  for (const [key, est] of estimates) {
    if (!shownDays.has(key)) continue;
    const [userId, date] = [key.slice(0, key.lastIndexOf("|")), key.slice(key.lastIndexOf("|") + 1)];
    let dayMinutes = 0;
    for (const [r, m] of Object.entries(est.byRepo)) {
      if (!repoOk(r)) continue;
      dayMinutes += m;
      if (r !== NO_REPO_KEY) {
        byRepo.set(r, (byRepo.get(r) ?? 0) + m);
        const k = `${r}|${userId}`;
        const prev = byRepoUser.get(k) ?? { repo: r, userId, minutes: 0 };
        prev.minutes += m;
        byRepoUser.set(k, prev);
      }
    }
    shownRepoMinutes += dayMinutes;
    for (const [k, m] of Object.entries(est.byPullRequest)) {
      if (k === NO_REPO_KEY) {
        if (!repoPatterns) noPrMinutes += m;
        continue;
      }
      if (!repoOk(k.slice(0, k.lastIndexOf("#")))) continue;
      byPr.set(k, (byPr.get(k) ?? 0) + m);
      prMinutes += m;
    }
    total += dayMinutes;
    byDay.set(date, (byDay.get(date) ?? 0) + dayMinutes);
    byUser.set(userId, (byUser.get(userId) ?? 0) + dayMinutes);
  }
  const withoutPr = repoPatterns ? Math.max(0, shownRepoMinutes - prMinutes) : noPrMinutes;
  const h = (m: number) => round2(m / 60);
  return {
    totalHours: h(total),
    byRepo: [...byRepo.entries()].map(([repo, m]) => ({ repo, hours: h(m) })).sort((a, b) => b.hours - a.hours || a.repo.localeCompare(b.repo)),
    byRepoUser: [...byRepoUser.values()].sort((a, b) => a.repo.localeCompare(b.repo) || a.userId.localeCompare(b.userId)),
    pullRequests: evidence.pullRequests
      .map((p) => ({ ...p, hours: h(byPr.get(p.key) ?? 0) }))
      .sort((a, b) => b.hours - a.hours || a.key.localeCompare(b.key)),
    hoursWithoutPullRequest: h(withoutPr),
    byDay: [...byDay.entries()].sort(([a], [b]) => (a < b ? -1 : 1)).map(([date, m]) => ({ date, hours: h(m) })),
    byUser: evidence.byUser.map((u) => ({ ...u, hours: h(byUser.get(u.userId) ?? 0) })).sort((a, b) => b.hours - a.hours),
  };
}

// ---------------------------------------------------------------------------
// DE DÓNDE SALE LA DIFERENCIA ENTRE «LO QUE CUENTA» Y «POR REPOSITORIO» (#1342)
// ---------------------------------------------------------------------------
// El total del tablero suma lo que CUENTA (entradas registradas; estimado por
// bloques en los días sin entradas). «Por repositorio» y «PRs trabajados» miden
// ACTIVIDAD: los bloques de tiempo real de los commits de TODOS los días. En un
// día con entradas las dos cifras no tienen por qué coincidir, y sin explicación
// parece que «faltan» horas. Este desglose dice de dónde sale cada minuto de la
// diferencia. La identidad es exacta, en minutos:
//
//   lo que cuenta = actividad por commits
//                 + registrado SIN commits (pruebas, reuniones, análisis…)
//                 + registrado CON commits por encima del estimado de esos commits
//                 − commits SIN horas registradas (o registradas de menos)
//                 + soporte estimado sin repositorio
//                 + registrado en otros repos del proyecto (solo con filtro de repos)
//
// «Por encima» y «sin horas» se miden por persona, día y repo, sin compensar
// entre personas ni entre días.
// ---------------------------------------------------------------------------

/** Un repo: lo que cuenta contra su actividad por commits. `repo: ""` = entradas sin repositorio / soporte. */
export interface RepoHoursBreakdownRow {
  repo: string;
  /** Minutos que cuentan en el total (entradas del repo + estimado de sus días sin entradas). */
  countedMinutes: number;
  /** Minutos de actividad medida por commits (lo que muestra «Por repositorio»). */
  activityMinutes: number;
  /** Parte de `countedMinutes` registrada en entradas sin commits. */
  withoutCommitsMinutes: number;
  /** Parte de `countedMinutes` que es soporte estimado sin repositorio (solo en el renglón `""`). */
  supportMinutes: number;
  /** `countedMinutes − activityMinutes`. */
  differenceMinutes: number;
}

/** Trabajo registrado sin commits, agrupado por persona, repo, tipo y nota. */
export interface RecordedWithoutCommitsItem {
  userId: string;
  userName: string | null;
  repo: string;
  kind: string | null;
  note: string | null;
  minutes: number;
  entries: number;
}

export interface RepoHoursBreakdown {
  byRepo: RepoHoursBreakdownRow[];
  countedMinutes: number;
  activityMinutes: number;
  /** Registrado en entradas sin commits (pruebas, reuniones, análisis…). */
  withoutCommitsMinutes: number;
  /** Registrado en entradas con commits, por encima de lo que esos commits demuestran. */
  beyondEstimateMinutes: number;
  /** Actividad de commits sin horas registradas (o registradas de menos) en su repo. */
  commitsWithoutHoursMinutes: number;
  /** Soporte sin repositorio en días estimados: cuenta en el total y no pertenece a ningún repo. */
  supportMinutes: number;
  /**
   * Solo con filtro de repos: registrado en OTROS repos del mismo proyecto. El
   * total los cuenta (el filtro elige proyectos), pero su actividad quedó fuera.
   */
  otherReposMinutes: number;
  /** Las entradas sin commits de mayor a menor (máximo `REPO_BREAKDOWN_MAX_ITEMS`). */
  withoutCommits: RecordedWithoutCommitsItem[];
  withoutCommitsTruncated: boolean;
}

export const REPO_BREAKDOWN_MAX_ITEMS = 20;

/** Lo que el desglose necesita de una entrada, además de lo que cuenta. */
type BreakdownEntry = CountableTimeEntry & Partial<Pick<TimeEntry, "repo" | "evidence" | "kind" | "note">>;

/**
 * Desglose de la diferencia entre lo que cuenta y la actividad por commits.
 * Usa los mismos filtros que `resolveUserDays` (rango, persona, proyectos) y los
 * mismos bloques de `blockActivity`. Función pura.
 */
export function repoHoursBreakdown(
  buckets: HourBucket[],
  entries: BreakdownEntry[],
  days: ResolvedDay[],
  estimates: Map<string, DayEstimate>,
  opts: SummarizeOptions & Pick<ResolveDaysOptions, "projectIds" | "userNames"> = {},
): RepoHoursBreakdown {
  const repoPatterns = opts.repos && opts.repos.length ? opts.repos : null;
  const repoOk = (r: string) => !repoPatterns || (r !== NO_REPO_KEY && repoMatchesAny(repoPatterns, r));
  const inRange = (d: string) => (!opts.from || d >= opts.from) && (!opts.to || d <= opts.to);
  const okUser = (u: string) => !opts.userId || u === opts.userId;
  const projectFilter = opts.projectIds ? new Set(opts.projectIds) : null;
  const estimatedKeys = new Set(days.filter((d) => d.estimated).map((d) => dayKey(d.userId, d.date)));
  const names = new Map(days.map((d) => [d.userId, d.userName]));
  const shownDays = new Set(filterBuckets(buckets, opts).map((b) => dayKey(b.userId, b.date)));

  // est: estimado de días sin entradas (cuenta Y es actividad) · nc: registrado
  // sin commits. Lo registrado CON commits (wc) y la actividad de los días con
  // entradas (act) se comparan por persona, día y repo: si se compensaran entre
  // personas o entre días, 8 h registradas de más un lunes taparían 6 h de
  // commits sin registrar un martes y la cuenta diría «todo coincide».
  type Row = { est: number; nc: number; wc: number; act: number };
  const rows = new Map<string, Row>();
  const row = (repo: string): Row => {
    let r = rows.get(repo);
    if (!r) rows.set(repo, (r = { est: 0, nc: 0, wc: 0, act: 0 }));
    return r;
  };
  const grains = new Map<string, { wc: number; act: number }>();
  const grain = (userId: string, date: string, repo: string) => {
    const k = `${userId}|${date}|${repo}`;
    let g = grains.get(k);
    if (!g) grains.set(k, (g = { wc: 0, act: 0 }));
    return g;
  };
  let supportMinutes = 0;
  let otherReposMinutes = 0;

  for (const [key, est] of estimates) {
    if (!shownDays.has(key)) continue;
    const estimatedDay = estimatedKeys.has(key);
    const [userId, date] = [key.slice(0, key.lastIndexOf("|")), key.slice(key.lastIndexOf("|") + 1)];
    for (const [r, m] of Object.entries(est.byRepo)) {
      if (r === NO_REPO_KEY) {
        // Soporte sin repo: solo cuenta en días estimados y sin filtro de repos.
        if (estimatedDay && !repoPatterns) supportMinutes += m;
        continue;
      }
      if (!repoOk(r)) continue;
      if (estimatedDay) row(r).est += m;
      else {
        row(r).act += m;
        grain(userId, date, r).act += m;
      }
    }
  }

  const items = new Map<string, RecordedWithoutCommitsItem>();
  for (const e of entries) {
    if (!isCountedEntry(e) || !inRange(e.date) || !okUser(e.userId)) continue;
    if (projectFilter && !projectFilter.has(e.projectId)) continue;
    const minutes = Number(e.minutes) || 0;
    const repo = typeof e.repo === "string" && e.repo ? e.repo : NO_REPO_KEY;
    // Con filtro de repos, el total sigue contando las entradas de los demás
    // repos del proyecto, pero su actividad quedó fuera del filtro: van en su
    // propio renglón, no como «por encima de lo que demuestran los commits».
    if (repoPatterns && repo !== NO_REPO_KEY && !repoOk(repo)) {
      otherReposMinutes += minutes;
      continue;
    }
    if ((e.evidence?.commits?.length ?? 0) > 0) {
      row(repo).wc += minutes;
      grain(e.userId, e.date, repo).wc += minutes;
      continue;
    }
    row(repo).nc += minutes;
    const note = e.note ? String(e.note).trim() || null : null;
    const k = [e.userId, repo, e.kind ?? "", note ?? ""].join("\u0000");
    const prev = items.get(k) ?? { userId: e.userId, userName: opts.userNames?.[e.userId] ?? names.get(e.userId) ?? null, repo, kind: e.kind ?? null, note, minutes: 0, entries: 0 };
    prev.minutes += minutes;
    prev.entries += 1;
    items.set(k, prev);
  }

  let beyond = 0;
  let commitsWithoutHours = 0;
  for (const g of grains.values()) {
    beyond += Math.max(0, g.wc - g.act);
    commitsWithoutHours += Math.max(0, g.act - g.wc);
  }
  let counted = supportMinutes + otherReposMinutes;
  let activity = 0;
  let withoutCommits = 0;
  const byRepo: RepoHoursBreakdownRow[] = [];
  for (const [repo, r] of rows) {
    const countedMinutes = r.est + r.nc + r.wc;
    const activityMinutes = r.est + r.act;
    counted += countedMinutes;
    activity += activityMinutes;
    withoutCommits += r.nc;
    byRepo.push({
      repo,
      countedMinutes: Math.round(countedMinutes),
      activityMinutes: Math.round(activityMinutes),
      withoutCommitsMinutes: Math.round(r.nc),
      supportMinutes: 0,
      differenceMinutes: Math.round(countedMinutes - activityMinutes),
    });
  }
  if (supportMinutes > 0) {
    const s = Math.round(supportMinutes);
    const support = byRepo.find((r) => r.repo === NO_REPO_KEY);
    if (support) {
      support.countedMinutes += s;
      support.supportMinutes += s;
      support.differenceMinutes += s;
    } else byRepo.push({ repo: NO_REPO_KEY, countedMinutes: s, activityMinutes: 0, withoutCommitsMinutes: 0, supportMinutes: s, differenceMinutes: s });
  }
  const sorted = [...items.values()].sort((a, b) => b.minutes - a.minutes || a.repo.localeCompare(b.repo) || (a.note ?? "").localeCompare(b.note ?? ""));
  return {
    byRepo: byRepo
      .filter((r) => r.countedMinutes > 0 || r.activityMinutes > 0)
      .sort((a, b) => b.countedMinutes - a.countedMinutes || b.activityMinutes - a.activityMinutes || a.repo.localeCompare(b.repo)),
    countedMinutes: Math.round(counted),
    activityMinutes: Math.round(activity),
    withoutCommitsMinutes: Math.round(withoutCommits),
    beyondEstimateMinutes: Math.round(beyond),
    commitsWithoutHoursMinutes: Math.round(commitsWithoutHours),
    supportMinutes: Math.round(supportMinutes),
    otherReposMinutes: Math.round(otherReposMinutes),
    withoutCommits: sorted.slice(0, REPO_BREAKDOWN_MAX_ITEMS),
    withoutCommitsTruncated: sorted.length > REPO_BREAKDOWN_MAX_ITEMS,
  };
}

// ---------------------------------------------------------------------------
// EL LIBRO: LAS LÍNEAS DETRÁS DE CADA CIFRA DEL TABLERO (#1344)
// ---------------------------------------------------------------------------
// Cada tabla del tablero (por tipo, por repositorio, por PR, la cuenta de la
// diferencia) es una suma. El libro son los sumandos: las entradas que cuentan,
// la actividad por commits de cada persona, día y repo —con sus commits—, y los
// minutos de cada PR por persona. Con él la pantalla abre cualquier renglón y
// enseña quién registró qué, cuándo, y de dónde sale cada minuto. El costo NO
// vive aquí (lleva sueldos): lo añade el servidor, solo para los dueños.
// Mismos filtros y mismos bloques que `repoHoursBreakdown`: las líneas suman
// exactamente sus cifras.

export interface LedgerEntryLine {
  id: string | null;
  userId: string;
  userName: string | null;
  date: string;
  /** `""` = sin repositorio. */
  repo: string;
  kind: string;
  source: string | null;
  note: string | null;
  minutes: number;
  /** Horario declarado (`HH:mm`), si la entrada lo trae. */
  start: string | null;
  end: string | null;
  /** Cuándo se registró (ISO). */
  createdAt: string | null;
  /** SHA de los commits que la entrada cita como evidencia. */
  commits: string[];
  prs: string[];
  /** Con filtro de repos: es de OTRO repo del proyecto (cuenta en el total, no en las filas). */
  otherRepo: boolean;
}

export interface LedgerCommit {
  sha: string;
  url: string | null;
  authoredAt: string | null;
  message: string | null;
}

/** Actividad por commits de UNA persona en UN día y UN repo. */
export interface LedgerActivityLine {
  userId: string;
  userName: string | null;
  date: string;
  repo: string;
  /** Minutos que demuestran los commits (bloques de tiempo real). */
  minutes: number;
  /** Día sin entradas: estos minutos son los que cuentan (estimado). */
  estimatedDay: boolean;
  /** Minutos registrados ese día en ese repo en entradas que citan commits. */
  registeredWithCommitsMinutes: number;
  /** `max(0, registrado con commits − actividad)`; 0 en días estimados. */
  beyondMinutes: number;
  /** `max(0, actividad − registrado con commits)`; 0 en días estimados. */
  commitsWithoutHoursMinutes: number;
  /** Commits de la persona ese día en ese repo (máximo `LEDGER_MAX_COMMITS`). */
  commits: LedgerCommit[];
  commitCount: number;
  /** Bloques de reloj de la persona ese día (todo su trabajo, no solo este repo). */
  blocks: { start: string; end: string; minutes: number }[];
}

export interface LedgerPullRequestLine {
  userId: string;
  userName: string | null;
  /** `owner/repo#n`. */
  key: string;
  repo: string;
  minutes: number;
}

export interface LedgerSupportLine {
  userId: string;
  userName: string | null;
  date: string;
  minutes: number;
}

export interface HoursLedger {
  entries: LedgerEntryLine[];
  activity: LedgerActivityLine[];
  pullRequests: LedgerPullRequestLine[];
  /** Soporte sin repositorio en días estimados. */
  support: LedgerSupportLine[];
}

/** Costo de una línea: en la moneda de la persona y su equivalente en MXN (`null` sin tipo de cambio). */
export interface LedgerCost {
  currency: string;
  amount: number;
  mxn: number | null;
}

/**
 * El libro como lo responde `GET /workHoursDetail`. `costs: false` ⇒ quien
 * consulta no es dueño: ninguna línea trae `cost`. `cost: null` en una línea de
 * dueño ⇒ esa persona no tiene sueldo registrado.
 */
export interface PricedHoursLedger {
  entries: (LedgerEntryLine & { cost?: LedgerCost | null })[];
  activity: (LedgerActivityLine & { cost?: LedgerCost | null; beyondCost?: LedgerCost | null; commitsWithoutHoursCost?: LedgerCost | null })[];
  pullRequests: (LedgerPullRequestLine & { cost?: LedgerCost | null })[];
  support: (LedgerSupportLine & { cost?: LedgerCost | null })[];
  costs: boolean;
  baseCurrency?: string;
  fx?: Record<string, { rate: number; date: string | null; source: string | null }>;
  /** Monedas sin tipo de cambio: sus líneas no tienen equivalente en MXN. */
  fxMissing?: string[];
  /**
   * El rango es demasiado largo para listar todas las líneas del equipo: llega
   * vacío. Hay que acotar el rango o elegir a una persona.
   */
  tooLarge?: boolean;
  unpricedUsers?: { userId: string; name: string }[];
}

export const LEDGER_MAX_COMMITS = 60;

/** Commit con lo necesario para listarlo en el libro. */
export interface LedgerCommitInput {
  sha: string;
  userId?: string | null;
  authoredAt?: string | null;
  repos?: string[] | null;
  url?: string | null;
  message?: string | null;
}

/** SHA de una referencia de evidencia (`sha`, `owner/repo@sha` o URL de commit); si no trae uno, la referencia tal cual. */
const bareSha = (ref: string): string => {
  const hits = ref.match(/[0-9a-f]{7,64}/gi);
  return hits ? hits[hits.length - 1] : ref;
};

type LedgerEntryInput = CountableTimeEntry & Partial<Pick<TimeEntry, "repo" | "evidence" | "kind" | "note" | "source" | "start" | "end" | "createdAt">>;

/** El libro del rango. Función pura; mismos filtros que `summarizeRecordedHours`. */
export function hoursLedger(
  buckets: HourBucket[],
  entries: LedgerEntryInput[],
  opts: SummarizeOptions & Pick<ResolveDaysOptions, "projectIds" | "userNames" | "events" | "estimate"> & { commits?: LedgerCommitInput[] | null } = {},
): HoursLedger {
  const { days, estimates } = resolveDaysWithEstimates(buckets, entries, opts);
  const repoPatterns = opts.repos && opts.repos.length ? opts.repos : null;
  const repoOk = (r: string) => !repoPatterns || (r !== NO_REPO_KEY && repoMatchesAny(repoPatterns, r));
  const inRange = (d: string) => (!opts.from || d >= opts.from) && (!opts.to || d <= opts.to);
  const okUser = (u: string) => !opts.userId || u === opts.userId;
  const projectFilter = opts.projectIds ? new Set(opts.projectIds) : null;
  const estimatedKeys = new Set(days.filter((d) => d.estimated).map((d) => dayKey(d.userId, d.date)));
  const names = new Map<string, string | null>();
  for (const b of buckets) if (b.userName && !names.get(b.userId)) names.set(b.userId, b.userName);
  for (const d of days) if (d.userName && !names.get(d.userId)) names.set(d.userId, d.userName);
  const nameOf = (userId: string) => opts.userNames?.[userId] ?? names.get(userId) ?? null;
  const shownDays = new Set(filterBuckets(buckets, opts).map((b) => dayKey(b.userId, b.date)));

  const activity = new Map<string, LedgerActivityLine>();
  const grain = (userId: string, date: string, repo: string): LedgerActivityLine => {
    const k = `${userId}|${date}|${repo}`;
    let g = activity.get(k);
    if (!g) {
      const key = dayKey(userId, date);
      g = {
        userId,
        userName: nameOf(userId),
        date,
        repo,
        minutes: 0,
        estimatedDay: estimatedKeys.has(key),
        registeredWithCommitsMinutes: 0,
        beyondMinutes: 0,
        commitsWithoutHoursMinutes: 0,
        commits: [],
        commitCount: 0,
        blocks: (estimates.get(key)?.blocks ?? []).map((b) => ({ start: b.start, end: b.end, minutes: b.minutes })),
      };
      activity.set(k, g);
    }
    return g;
  };
  const prs = new Map<string, LedgerPullRequestLine>();
  const support: LedgerSupportLine[] = [];

  for (const [key, est] of estimates) {
    if (!shownDays.has(key)) continue;
    const [userId, date] = [key.slice(0, key.lastIndexOf("|")), key.slice(key.lastIndexOf("|") + 1)];
    for (const [r, m] of Object.entries(est.byRepo)) {
      if (r === NO_REPO_KEY) {
        if (estimatedKeys.has(key) && !repoPatterns && m > 0) support.push({ userId, userName: nameOf(userId), date, minutes: Math.round(m) });
        continue;
      }
      if (!repoOk(r) || !(m > 0)) continue;
      grain(userId, date, r).minutes += m;
    }
    for (const [k, m] of Object.entries(est.byPullRequest)) {
      if (k === NO_REPO_KEY || !(m > 0)) continue;
      const repo = k.slice(0, k.lastIndexOf("#"));
      if (!repoOk(repo)) continue;
      const id = `${userId}|${k}`;
      const prev = prs.get(id) ?? { userId, userName: nameOf(userId), key: k, repo, minutes: 0 };
      prev.minutes += m;
      prs.set(id, prev);
    }
  }

  const lines: LedgerEntryLine[] = [];
  for (const e of entries) {
    if (!isCountedEntry(e) || !inRange(e.date) || !okUser(e.userId)) continue;
    if (projectFilter && !projectFilter.has(e.projectId)) continue;
    const minutes = Number(e.minutes) || 0;
    const repo = typeof e.repo === "string" && e.repo ? e.repo : NO_REPO_KEY;
    // La evidencia puede venir como SHA, `owner/repo@sha` o una URL: se deja el SHA.
    const commits = (e.evidence?.commits ?? []).filter((s): s is string => typeof s === "string" && s.length > 0).map(bareSha);
    const otherRepo = Boolean(repoPatterns && repo !== NO_REPO_KEY && !repoOk(repo));
    lines.push({
      id: e.id ?? null,
      userId: e.userId,
      userName: nameOf(e.userId),
      date: e.date,
      repo,
      kind: e.kind ? String(e.kind) : "other",
      source: e.source ?? null,
      note: e.note ? String(e.note).trim() || null : null,
      minutes,
      start: e.start ?? null,
      end: e.end ?? null,
      createdAt: e.createdAt ?? null,
      commits,
      prs: (e.evidence?.prs ?? []).filter((s): s is string => typeof s === "string" && s.length > 0),
      otherRepo,
    });
    if (!otherRepo && commits.length > 0) grain(e.userId, e.date, repo).registeredWithCommitsMinutes += minutes;
  }

  // Commits de cada persona, día y repo: la evidencia que respalda (o no) lo
  // registrado. El dueño de un commit es el de la FRANJA que lo cita (así se
  // atribuyen los minutos); un commit re-atribuido puede traer otro `userId`.
  const shaOwner = new Map<string, string>();
  for (const b of buckets) for (const sha of b.commitShas ?? []) if (!shaOwner.has(sha)) shaOwner.set(sha, b.userId);
  for (const c of opts.commits ?? []) {
    if (!c || !c.sha || !c.authoredAt) continue;
    const owner = shaOwner.get(c.sha) ?? c.userId;
    if (!owner) continue;
    const slot = localSlot(c.authoredAt);
    if (!slot) continue;
    for (const repo of c.repos ?? []) {
      const g = activity.get(`${owner}|${slot.date}|${repo}`);
      if (!g) continue;
      g.commitCount += 1;
      if (g.commits.length < LEDGER_MAX_COMMITS) g.commits.push({ sha: c.sha, url: c.url ?? null, authoredAt: c.authoredAt, message: c.message ?? null });
    }
  }

  const out: LedgerActivityLine[] = [];
  for (const g of activity.values()) {
    if (!g.estimatedDay) {
      g.beyondMinutes = Math.max(0, g.registeredWithCommitsMinutes - g.minutes);
      g.commitsWithoutHoursMinutes = Math.max(0, g.minutes - g.registeredWithCommitsMinutes);
    }
    g.minutes = Math.round(g.minutes);
    g.beyondMinutes = Math.round(g.beyondMinutes);
    g.commitsWithoutHoursMinutes = Math.round(g.commitsWithoutHoursMinutes);
    g.commits.sort((a, b) => String(a.authoredAt).localeCompare(String(b.authoredAt)));
    // La lista de commits y los bloques del día solo hacen falta donde hay algo
    // que explicar (registrado de más o commits sin horas). En el resto basta el
    // conteo: en un rango largo serían megas de respuesta que nadie lee.
    if (g.beyondMinutes === 0 && g.commitsWithoutHoursMinutes === 0) {
      g.commits = [];
      g.blocks = [];
    }
    if (g.minutes > 0 || g.registeredWithCommitsMinutes > 0) out.push(g);
  }
  const byDate = <T extends { date: string; userId: string }>(a: T, b: T) => a.date.localeCompare(b.date) || a.userId.localeCompare(b.userId);
  return {
    entries: lines.sort((a, b) => byDate(a, b) || String(a.start ?? "").localeCompare(String(b.start ?? "")) || b.minutes - a.minutes),
    activity: out.sort((a, b) => byDate(a, b) || a.repo.localeCompare(b.repo)),
    pullRequests: [...prs.values()].map((p) => ({ ...p, minutes: Math.round(p.minutes) })).filter((p) => p.minutes > 0).sort((a, b) => b.minutes - a.minutes || a.key.localeCompare(b.key)),
    support: support.sort(byDate),
  };
}

// ---------------------------------------------------------------------------
// HORAS POR TIPO DE ACTIVIDAD (#1342)
// ---------------------------------------------------------------------------
// El tablero sumaba las reuniones del CRM y las horas de ventas o marketing
// dentro del total de cada persona, sin decir cuáles eran: no se veían. Aquí
// salen por tipo (`kind` del catálogo de actividades): desarrollo, reunión,
// prospección… con cuánto de cada uno nació de una reunión del CRM.

/** Clave del renglón de los días SIN entradas (estimado por commits): no tiene tipo. */
export const ESTIMATED_KIND_KEY = "";

export interface KindHours {
  /** `kind` de las entradas; `ESTIMATED_KIND_KEY` = estimado por commits de días sin entradas. */
  kind: string;
  minutes: number;
  entries: number;
  /** Personas distintas con ese tipo. */
  people: number;
  /** Parte de `minutes` que nació de reuniones del CRM (`source: "crm"`). */
  crmMinutes: number;
}

/** Lo que cuenta en el rango, por tipo de actividad. Mismos filtros que `resolveUserDays`. Función pura. */
export function kindHours(
  entries: (CountableTimeEntry & Partial<Pick<TimeEntry, "kind" | "source">>)[],
  days: ResolvedDay[],
  opts: Pick<ResolveDaysOptions, "from" | "to" | "userId" | "projectIds"> = {},
): KindHours[] {
  const inRange = (d: string) => (!opts.from || d >= opts.from) && (!opts.to || d <= opts.to);
  const okUser = (u: string) => !opts.userId || u === opts.userId;
  const projectFilter = opts.projectIds ? new Set(opts.projectIds) : null;
  const rows = new Map<string, { minutes: number; entries: number; users: Set<string>; crmMinutes: number }>();
  const row = (kind: string) => {
    let r = rows.get(kind);
    if (!r) rows.set(kind, (r = { minutes: 0, entries: 0, users: new Set(), crmMinutes: 0 }));
    return r;
  };
  for (const e of entries) {
    if (!isCountedEntry(e) || !inRange(e.date) || !okUser(e.userId)) continue;
    if (projectFilter && !projectFilter.has(e.projectId)) continue;
    const minutes = Number(e.minutes) || 0;
    const r = row(e.kind ? String(e.kind) : "other");
    r.minutes += minutes;
    r.entries += 1;
    r.users.add(e.userId);
    if (e.source === "crm") r.crmMinutes += minutes;
  }
  for (const d of days) {
    if (!d.estimated) continue;
    const r = row(ESTIMATED_KIND_KEY);
    r.minutes += d.minutes;
    r.users.add(d.userId);
  }
  return [...rows.entries()]
    .map(([kind, r]) => ({ kind, minutes: Math.round(r.minutes), entries: r.entries, people: r.users.size, crmMinutes: Math.round(r.crmMinutes) }))
    .filter((r) => r.minutes > 0)
    .sort((a, b) => Number(a.kind === ESTIMATED_KIND_KEY) - Number(b.kind === ESTIMATED_KIND_KEY) || b.minutes - a.minutes || a.kind.localeCompare(b.kind));
}

// ---------------------------------------------------------------------------
// REPORTES
// ---------------------------------------------------------------------------

export const escapeHtml = (s: unknown): string =>
  String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] as string);

/** Pie de los reportes: cómo se cuentan las horas (#783). */
export const HOURS_RULE_NOTE =
  "Horas = minutos registrados en entradas de tiempo (sin anuladas). Los días sin entradas se estiman por bloques de " +
  "tiempo real a partir de los commits y eventos de soporte (pausas de más de 30 min no cuentan; cada bloque empieza " +
  "15 min antes de su primer commit; America/Mexico_City) y se marcan como estimados.";

const hh = (h: number | null) => (h === null ? "—" : `${String(h).padStart(2, "0")}:00`);
const hoursLabel = (n: number) => `${round2(n)} h`;

const shell = (title: string, body: string) =>
  `<div style="font-family:Inter,Arial,sans-serif;max-width:640px;margin:0 auto;color:#111827">` +
  `<h2 style="margin:0 0 4px">${escapeHtml(title)}</h2>${body}` +
  `<p style="margin-top:24px;font-size:12px;color:#6b7280">${escapeHtml(HOURS_RULE_NOTE)}</p></div>`;

const table = (headers: string[], rows: string[][]) =>
  `<table style="border-collapse:collapse;width:100%;font-size:14px;margin-top:12px">` +
  `<thead><tr>${headers.map((h) => `<th style="text-align:left;border-bottom:1px solid #e5e7eb;padding:6px">${escapeHtml(h)}</th>`).join("")}</tr></thead>` +
  `<tbody>${rows.map((r) => `<tr>${r.map((c) => `<td style="border-bottom:1px solid #f3f4f6;padding:6px">${c}</td>`).join("")}</tr>`).join("")}</tbody></table>`;

export interface DailyAdminReportInput {
  companyName?: string | null;
  date: string;
  summary: WorkHoursSummary;
  /** Usuarios con identidad GitHub registrada: los que no aparecen quedan como sin actividad. */
  expectedUsers?: { userId: string; userName: string | null }[];
  run?: { status: RunStatus | "missing"; finishedAt?: string | null; errors?: string[] } | null;
  unmatchedAuthors?: string[];
}

/** Reporte diario para administradores: horas, franjas, repos y PRs por usuario. */
export function buildDailyAdminReport(input: DailyAdminReportInput): ReportMessage {
  const { summary, date } = input;
  const company = input.companyName ? ` · ${input.companyName}` : "";
  const active = new Set(summary.byUser.map((u) => u.userId));
  const inactive = (input.expectedUsers ?? []).filter((u) => !active.has(u.userId));
  const run = input.run;
  const runLine =
    !run || run.status === "missing"
      ? "⚠️ El agente nocturno no reportó corrida para esta fecha."
      : run.status === "failed"
        ? `⚠️ La corrida nocturna falló${run.errors?.length ? `: ${run.errors.slice(0, 3).join("; ")}` : "."}`
        : `Corrida nocturna: ${run.status === "running" ? "en curso" : "completada"}.`;

  // Parte estimada por commits (#783): días sin entradas de tiempo.
  const est = (n: number | undefined) => (n && n > 0 ? ` (${hoursLabel(n)} estimadas)` : "");
  const estimatedTotal = (summary as Partial<RecordedHoursSummary>).estimatedHours;
  const userRows = summary.byUser.map((u) => [
    escapeHtml(u.userName ?? u.userId),
    hoursLabel(u.hours) + est(u.estimatedHours),
    `${hh(u.firstHour)} – ${hh(u.lastHour === null ? null : u.lastHour + 1)}`,
    String(u.commits),
  ]);
  const prRows = summary.pullRequests.slice(0, 15).map((p) => [
    p.url ? `<a href="${escapeHtml(p.url)}">${escapeHtml(p.key)}</a>` : escapeHtml(p.key),
    escapeHtml(p.title ?? ""),
    hoursLabel(p.hours),
    escapeHtml(p.users.join(", ")),
  ]);
  // #843: tiempo fuera de asignación. Cuenta en el total; se lista con el
  // motivo del sistema y la justificación de la persona (o «sin justificación»).
  const recordedSummary = summary as Partial<RecordedHoursSummary>;
  const outItems = recordedSummary.outOfAllocation ?? [];
  const outHours = recordedSummary.outOfAllocationHours ?? 0;
  const outReason = (i: OutOfAllocationItem) => i.flags.map(flagText).join("; ") || OUT_OF_ALLOCATION_LABEL;
  const outLine = outHours > 0 ? `${OUT_OF_ALLOCATION_LABEL}: ${hoursLabel(outHours)} (incluidas en el total)` : "";

  const html = shell(
    `Horas trabajadas del ${date}${company}`,
    `<p style="margin:0;color:#374151">${summary.totalHours} h en total${est(estimatedTotal)} · ${summary.activeUsers} personas · ${summary.totalCommits} commits</p>` +
      `<p style="margin:8px 0 0;font-size:13px">${escapeHtml(runLine)}</p>` +
      (userRows.length ? table(["Persona", "Horas", "Franja", "Commits"], userRows) : `<p>Sin horas registradas.</p>`) +
      (summary.byRepo.length ? `<h3 style="margin:20px 0 0">Repos (actividad por commits)</h3>` + table(["Repo", "Horas"], summary.byRepo.map((r) => [escapeHtml(r.repo), hoursLabel(r.hours)])) : "") +
      (prRows.length ? `<h3 style="margin:20px 0 0">PRs trabajados</h3>` + table(["PR", "Título", "Horas", "Personas"], prRows) : "") +
      (outItems.length
        ? `<h3 style="margin:20px 0 0">${escapeHtml(OUT_OF_ALLOCATION_LABEL)} · ${hoursLabel(outHours)}</h3>` +
          `<p style="margin:4px 0 0;font-size:13px;color:#6b7280">Horas que cuentan, pero en proyectos sin asignación o repos sin proyecto: revisa la asignación o el trabajo.</p>` +
          table(
            ["Persona", "Horas", "Motivo"],
            outItems.map((i) => [escapeHtml(i.userName ?? i.userId), hoursLabel(i.hours) + (i.estimated ? " (estimadas)" : ""), escapeHtml(outReason(i))]),
          )
        : "") +
      (inactive.length ? `<h3 style="margin:20px 0 0">Sin actividad</h3><p>${inactive.map((u) => escapeHtml(u.userName ?? u.userId)).join(", ")}</p>` : "") +
      (input.unmatchedAuthors?.length ? `<h3 style="margin:20px 0 0">Autores sin usuario</h3><p>${input.unmatchedAuthors.map(escapeHtml).join(", ")}</p>` : ""),
  );

  const lines = [
    `Horas trabajadas del ${date}${company}`,
    `${summary.totalHours} h${est(estimatedTotal)} · ${summary.activeUsers} personas · ${summary.totalCommits} commits`,
    runLine,
    ...summary.byUser.map((u) => `- ${u.userName ?? u.userId}: ${hoursLabel(u.hours)}${est(u.estimatedHours)} (${hh(u.firstHour)}–${hh(u.lastHour === null ? null : u.lastHour + 1)}, ${u.commits} commits)`),
    ...(summary.pullRequests.length ? ["PRs:", ...summary.pullRequests.slice(0, 15).map((p) => `- ${p.key} ${p.title ?? ""} · ${hoursLabel(p.hours)}`)] : []),
    ...(outItems.length
      ? [`${outLine}:`, ...outItems.map((i) => `- ${i.userName ?? i.userId}: ${hoursLabel(i.hours)}${i.estimated ? " (estimadas)" : ""} · ${outReason(i)}`)]
      : []),
    ...(inactive.length ? [`Sin actividad: ${inactive.map((u) => u.userName ?? u.userId).join(", ")}`] : []),
    HOURS_RULE_NOTE,
  ];

  return {
    subject: `Horas trabajadas ${date}${company}: ${summary.totalHours} h`,
    text: lines.join("\n"),
    html,
    shortText: [
      `*Horas ${date}*${company}: ${summary.totalHours} h, ${summary.activeUsers} personas`,
      ...summary.byUser.slice(0, 10).map((u) => `• ${u.userName ?? u.userId}: ${hoursLabel(u.hours)}${est(u.estimatedHours)}`),
      ...(outLine ? [`⚠️ ${outLine}`] : []),
      ...(inactive.length ? [`Sin actividad: ${inactive.length}`] : []),
    ].join("\n"),
  };
}

export interface WeeklyClientReportInput {
  clientName: string;
  companyName?: string | null;
  weekStart: string;
  weekEnd: string;
  /** Resumen YA filtrado a los repos del cliente. */
  summary: WorkHoursSummary;
}

/**
 * Reporte semanal a un cliente: sus repos, sus PRs y las horas totales del
 * proyecto. NUNCA incluye nombres de devs ni horas individuales.
 */
export function buildWeeklyClientReport(input: WeeklyClientReportInput): ReportMessage {
  const { summary } = input;
  const from = input.companyName ? input.companyName : "Coding Cloud";
  // #783: horas por proyecto (entradas de tiempo) y la parte estimada por commits.
  const recorded = summary as Partial<RecordedHoursSummary>;
  const projects = recorded.byProject ?? [];
  const estimatedNote = recorded.estimatedHours && recorded.estimatedHours > 0 ? ` (${hoursLabel(recorded.estimatedHours)} estimadas por commits)` : "";
  // #843: horas de personas fuera del equipo asignado al proyecto. Cuentan; se
  // mencionan sin nombres ni motivos (el reporte al cliente nunca identifica personas).
  const outNote =
    recorded.outOfAllocationHours && recorded.outOfAllocationHours > 0
      ? `Incluye ${hoursLabel(recorded.outOfAllocationHours)} de apoyo de personas fuera del equipo asignado.`
      : "";
  const prRows = summary.pullRequests.map((p) => [
    p.url ? `<a href="${escapeHtml(p.url)}">${escapeHtml(p.key)}</a>` : escapeHtml(p.key),
    escapeHtml(p.title ?? ""),
    hoursLabel(p.hours),
  ]);
  const html = shell(
    `Resumen semanal para ${input.clientName}`,
    `<p style="margin:0;color:#374151">Semana del ${input.weekStart} al ${input.weekEnd} · ${summary.totalHours} h de trabajo${escapeHtml(estimatedNote)} · ${summary.pullRequests.length} PRs</p>` +
      (projects.length ? `<h3 style="margin:20px 0 0">Horas registradas por proyecto</h3>` + table(["Proyecto", "Horas"], projects.map((p) => [escapeHtml(p.projectLabel), hoursLabel(p.hours)])) : "") +
      (summary.byRepo.length ? (projects.length ? `<h3 style="margin:20px 0 0">Actividad por repositorio (commits)</h3>` : "") + table(["Repositorio", "Horas"], summary.byRepo.map((r) => [escapeHtml(r.repo), hoursLabel(r.hours)])) : projects.length ? "" : `<p>No hubo actividad en tus repositorios esta semana.</p>`) +
      (prRows.length ? `<h3 style="margin:20px 0 0">Pull requests trabajados</h3>` + table(["PR", "Título", "Horas"], prRows) : "") +
      (outNote ? `<p style="margin-top:12px;font-size:13px;color:#374151">${escapeHtml(outNote)}</p>` : "") +
      `<p style="margin-top:16px">Enviado por ${escapeHtml(from)}.</p>`,
  );
  const text = [
    `Resumen semanal para ${input.clientName} (${input.weekStart} – ${input.weekEnd})`,
    `${summary.totalHours} h de trabajo${estimatedNote} · ${summary.pullRequests.length} PRs`,
    ...projects.map((p) => `- ${p.projectLabel}: ${hoursLabel(p.hours)}`),
    ...summary.byRepo.map((r) => `- ${r.repo}: ${hoursLabel(r.hours)}`),
    ...(summary.pullRequests.length ? ["PRs:", ...summary.pullRequests.map((p) => `- ${p.key} ${p.title ?? ""} · ${hoursLabel(p.hours)}`)] : []),
    ...(outNote ? [outNote] : []),
    HOURS_RULE_NOTE,
  ].join("\n");
  return {
    subject: `Resumen semanal ${input.weekStart}: ${summary.totalHours} h en tus proyectos`,
    text,
    html,
    shortText: `*${input.clientName}* · semana ${input.weekStart}: ${summary.totalHours} h, ${summary.pullRequests.length} PRs`,
  };
}

/**
 * Quita nombres de personas de un resumen (defensa extra del reporte a clientes).
 * También vacía lo que el resumen de #783 trae por persona: `days` y
 * `evidence.byUser`, y el detalle fuera de asignación de #843 (sus motivos
 * nombran a la persona).
 */
export function anonymizeSummary<T extends WorkHoursSummary>(summary: T): T {
  const out = {
    ...summary,
    byUser: [],
    activeUsers: 0,
    pullRequests: summary.pullRequests.map((p) => ({ ...p, users: [] })),
  } as T & Partial<Pick<RecordedHoursSummary, "days" | "evidence" | "outOfAllocation">>;
  if ("days" in out) out.days = [];
  // #843: los motivos nombran a la persona. Se conservan solo los minutos.
  if ("outOfAllocation" in out) out.outOfAllocation = [];
  if (out.evidence) out.evidence = { ...out.evidence, byUser: [] };
  // #1063: el desglose repo × persona lleva ids de usuario.
  if ("byRepoUser" in out) (out as { byRepoUser?: RepoUserMinutes[] }).byRepoUser = [];
  // #1342: el desglose lleva notas internas de las entradas y nombres.
  delete (out as { repoBreakdown?: RepoHoursBreakdown }).repoBreakdown;
  // Los tipos de actividad son del equipo interno (ventas, marketing…).
  delete (out as { byKind?: KindHours[] }).byKind;
  return out;
}

// ---------------------------------------------------------------------------
// CONTRATO DE SERVICIO
// ---------------------------------------------------------------------------

// ============================================================================
// Actividades (épica #1299) — catálogo por puesto, tickets y sprints
// ============================================================================
// Contrato compartido entre el API (`workHoursActivities*`), la app Gestión de
// Staff › Actividades (tipo Jira), el modal «Registrar tiempo» de Horas, el
// plugin cc-hours, el MCP y el CRM. Todo lo que aquí se declara lo consumen
// varias piezas a la vez: un cambio de forma se hace aquí y se regenera
// `generated/` para functions (`pnpm --filter @torre/work-hours build:functions`).
//
//   • `work_activity_catalog` — tipos de actividad por compañía con los PUESTOS
//     que pueden realizarla. Su `key` es el `kind` de las entradas de tiempo:
//     el catálogo del Staff y el «Tipo» de Horas son la misma lista (#1300).
//   • `work_activities` — tickets: issues/milestones/PRs de GitHub, tickets de
//     la Mesa de Servicio, reuniones del CRM y actividades manuales (#1301).
//   • `work_sprints` — periodos de trabajo; un solo sprint activo a la vez.
//   • `work_time_entries.activityId` — toda entrada manual o de la skill queda
//     ligada a un ticket (#1303).
// ============================================================================

export const WORK_ACTIVITY_CATALOG_COLLECTION = "work_activity_catalog";
export const WORK_ACTIVITIES_COLLECTION = "work_activities";
export const WORK_SPRINTS_COLLECTION = "work_sprints";

/**
 * Puestos del módulo Equipo (`team_members.role`). Espejo de `TEAM_MEMBER_ROLES`
 * de `@torre/team` (este paquete no depende de aquél): si allá se agrega uno,
 * agrégalo aquí.
 */
export const ACTIVITY_ROLES = [
  "developer",
  "tech_lead",
  "engineering_manager",
  "scrum_master",
  "product_owner",
  "qa_engineer",
  "devops_engineer",
  "designer",
  "sales",
  "marketing",
  "freelancer",
  "administrative",
] as const;
export type ActivityRole = (typeof ACTIVITY_ROLES)[number];

/** Puestos de ingeniería: desarrollan, revisan, prueban y despliegan. */
export const ENGINEERING_ROLES: readonly ActivityRole[] = ["developer", "tech_lead", "engineering_manager", "qa_engineer", "devops_engineer", "designer", "freelancer"];

/** Clave de un tipo de actividad (= `kind` de las entradas): slug en minúsculas. */
export const ACTIVITY_TYPE_KEY_RE = /^[a-z][a-z0-9_]{1,39}$/;

/** Documento de `work_activity_catalog`. Id = `{company_id}_{key}`. */
export interface ActivityType {
  id: string;
  company_id: string;
  /** Clave estable: es el `kind` de las entradas de tiempo. */
  key: string;
  name: string;
  nameEn: string | null;
  description: string | null;
  /** Clase de remixicon (`ri-…`). */
  icon: string;
  /** Puestos que pueden realizarla; `"all"` = todos. */
  roles: ActivityRole[] | "all";
  /** ¿Se ofrece en «Registrar tiempo»? `dev` no: nace de los commits. */
  manualEntry: boolean;
  active: boolean;
  /** Del catálogo base: no se borra, se desactiva. */
  builtIn: boolean;
  order: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
}

export type ActivityTypeSeed = Pick<ActivityType, "key" | "name" | "nameEn" | "description" | "icon" | "roles" | "manualEntry" | "order">;

/**
 * Catálogo base. Lo que pidió el usuario (#1300): reuniones para todos; tickets
 * de soporte solo ventas y desarrollo; desarrollo (commits) no para ventas pero
 * sí para marketing; lo comercial para ventas; lo de contenido para marketing.
 * La compañía lo ajusta desde Gestión de Staff › Catálogo (los dueños).
 */
export const DEFAULT_ACTIVITY_CATALOG: readonly ActivityTypeSeed[] = [
  // --- Para todos ------------------------------------------------------------
  { key: "meeting", name: "Reunión", nameEn: "Meeting", description: "Reuniones internas, con clientes o con prospectos.", icon: "ri-team-line", roles: "all", manualEntry: true, order: 10 },
  { key: "planning", name: "Planeación", nameEn: "Planning", description: "Planeación de sprint, roadmap y prioridades.", icon: "ri-calendar-todo-line", roles: "all", manualEntry: true, order: 20 },
  { key: "course", name: "Curso / capacitación", nameEn: "Course / training", description: "Cursos, certificaciones y capacitación interna.", icon: "ri-graduation-cap-line", roles: "all", manualEntry: true, order: 30 },
  { key: "documentation", name: "Documentación", nameEn: "Documentation", description: "Wiki, manuales, procesos y documentos.", icon: "ri-book-2-line", roles: "all", manualEntry: true, order: 40 },
  { key: "admin", name: "Administrativo", nameEn: "Administrative", description: "Trámites, reportes internos y gestión.", icon: "ri-file-list-3-line", roles: "all", manualEntry: true, order: 50 },
  { key: "other", name: "Otro", nameEn: "Other", description: "Lo que no cabe en ningún otro tipo (explica en la nota).", icon: "ri-more-line", roles: "all", manualEntry: true, order: 999 },
  // --- Desarrollo ------------------------------------------------------------
  { key: "dev", name: "Desarrollo (commits)", nameEn: "Development (commits)", description: "Nace de la evidencia de commits y PRs; no se captura a mano.", icon: "ri-code-s-slash-line", roles: [...ENGINEERING_ROLES, "marketing"], manualEntry: false, order: 100 },
  { key: "review", name: "Revisión de código", nameEn: "Code review", description: "Revisión de PRs y de entregables técnicos.", icon: "ri-git-pull-request-line", roles: [...ENGINEERING_ROLES, "product_owner", "scrum_master"], manualEntry: true, order: 110 },
  { key: "qa", name: "Pruebas / QA", nameEn: "Testing / QA", description: "Pruebas manuales y automatizadas, reporte de defectos.", icon: "ri-bug-line", roles: [...ENGINEERING_ROLES], manualEntry: true, order: 120 },
  { key: "deploy", name: "Despliegue / infraestructura", nameEn: "Deploy / infrastructure", description: "Despliegues a dev y prod, infraestructura y operación.", icon: "ri-rocket-line", roles: ["developer", "tech_lead", "engineering_manager", "devops_engineer"], manualEntry: true, order: 130 },
  { key: "support", name: "Ticket de soporte", nameEn: "Support ticket", description: "Atención de tickets de la Mesa de Servicio.", icon: "ri-customer-service-2-line", roles: ["sales", ...ENGINEERING_ROLES], manualEntry: true, order: 140 },
  { key: "billable", name: "Facturable a cliente", nameEn: "Billable to client", description: "Tiempo facturable que no se liga a un issue o PR.", icon: "ri-money-dollar-circle-line", roles: [...ENGINEERING_ROLES, "sales"], manualEntry: true, order: 150 },
  // --- Ventas ----------------------------------------------------------------
  { key: "prospecting", name: "Prospección", nameEn: "Prospecting", description: "Llamadas y correos en frío, búsqueda y calificación de prospectos.", icon: "ri-user-search-line", roles: ["sales"], manualEntry: true, order: 200 },
  { key: "demo", name: "Demostración / presentación", nameEn: "Demo / presentation", description: "Demo del producto o presentación comercial.", icon: "ri-slideshow-line", roles: ["sales", "product_owner", "engineering_manager", "tech_lead"], manualEntry: true, order: 210 },
  { key: "follow_up", name: "Seguimiento comercial", nameEn: "Sales follow-up", description: "Seguimiento a prospectos y negocios abiertos.", icon: "ri-chat-check-line", roles: ["sales"], manualEntry: true, order: 220 },
  { key: "quote", name: "Cotización / propuesta", nameEn: "Quote / proposal", description: "Armado de cotizaciones y propuestas.", icon: "ri-file-text-line", roles: ["sales", "engineering_manager", "product_owner"], manualEntry: true, order: 230 },
  { key: "client_visit", name: "Visita a cliente", nameEn: "Client visit", description: "Visitas presenciales a clientes o prospectos.", icon: "ri-map-pin-user-line", roles: ["sales"], manualEntry: true, order: 240 },
  { key: "crm_update", name: "Actualización del CRM", nameEn: "CRM update", description: "Captura y limpieza de leads, empresas y negocios.", icon: "ri-contacts-book-2-line", roles: ["sales", "marketing"], manualEntry: true, order: 250 },
  // --- Marketing -------------------------------------------------------------
  { key: "content", name: "Creación de contenido", nameEn: "Content creation", description: "Artículos, guiones, piezas y copys.", icon: "ri-quill-pen-line", roles: ["marketing", "designer"], manualEntry: true, order: 300 },
  { key: "social_media", name: "Redes sociales", nameEn: "Social media", description: "Publicación y comunidad en redes.", icon: "ri-share-line", roles: ["marketing"], manualEntry: true, order: 310 },
  { key: "campaign", name: "Campaña / anuncios", nameEn: "Campaign / ads", description: "Campañas pagadas y su optimización.", icon: "ri-megaphone-line", roles: ["marketing", "sales"], manualEntry: true, order: 320 },
  { key: "design", name: "Diseño gráfico", nameEn: "Graphic design", description: "Diseño de piezas, presentaciones y material.", icon: "ri-palette-line", roles: ["marketing", "designer"], manualEntry: true, order: 330 },
  { key: "seo_web", name: "SEO / sitio web", nameEn: "SEO / website", description: "Posicionamiento, landing y sitio.", icon: "ri-global-line", roles: ["marketing", "designer", "developer"], manualEntry: true, order: 340 },
  { key: "email_marketing", name: "Email marketing", nameEn: "Email marketing", description: "Campañas de correo y automatizaciones.", icon: "ri-mail-send-line", roles: ["marketing", "sales"], manualEntry: true, order: 350 },
  { key: "analytics", name: "Analítica de marketing", nameEn: "Marketing analytics", description: "Medición de campañas, embudo y reportes.", icon: "ri-line-chart-line", roles: ["marketing", "sales", "product_owner"], manualEntry: true, order: 360 },
];

/** Claves del catálogo base (siempre válidas como `kind`, aunque estén desactivadas para la UI). */
export const DEFAULT_ACTIVITY_KEYS: readonly string[] = DEFAULT_ACTIVITY_CATALOG.map((t) => t.key);

/** ¿Puede este puesto realizar la actividad? Sin puesto conocido, solo las de todos. */
export function isActivityTypeAllowedForRole(type: Pick<ActivityType, "roles" | "active">, role: string | null | undefined): boolean {
  if (type.active === false) return false;
  if (type.roles === "all") return true;
  if (role === null || role === undefined || role === "") return false;
  return (type.roles as readonly string[]).includes(String(role));
}

/**
 * Catálogo efectivo de una compañía: el base más lo guardado; un documento
 * guardado con la misma `key` que un tipo base lo PISA (así se desactiva o se
 * cambian sus puestos sin perder la clave). Ordenado por `order` y nombre.
 */
export function mergeActivityCatalog(companyId: string, stored: readonly Partial<ActivityType>[], now: string = new Date().toISOString()): ActivityType[] {
  const byKey = new Map<string, ActivityType>();
  for (const seed of DEFAULT_ACTIVITY_CATALOG) {
    byKey.set(seed.key, {
      id: `${companyId}_${seed.key}`,
      company_id: companyId,
      ...seed,
      roles: seed.roles === "all" ? "all" : [...seed.roles],
      active: true,
      builtIn: true,
      createdAt: now,
      updatedAt: now,
      createdBy: null,
    });
  }
  for (const doc of stored) {
    if (!doc || typeof doc.key !== "string" || !ACTIVITY_TYPE_KEY_RE.test(doc.key)) continue;
    const base = byKey.get(doc.key);
    const roles: ActivityRole[] | "all" =
      doc.roles === "all" ? "all" : Array.isArray(doc.roles) ? (doc.roles.filter((r) => (ACTIVITY_ROLES as readonly string[]).includes(String(r))) as ActivityRole[]) : base ? base.roles : "all";
    byKey.set(doc.key, {
      id: doc.id || `${companyId}_${doc.key}`,
      company_id: companyId,
      key: doc.key,
      name: typeof doc.name === "string" && doc.name.trim() ? doc.name.trim() : base ? base.name : doc.key,
      nameEn: typeof doc.nameEn === "string" && doc.nameEn.trim() ? doc.nameEn.trim() : base ? base.nameEn : null,
      description: typeof doc.description === "string" ? doc.description : base ? base.description : null,
      icon: typeof doc.icon === "string" && doc.icon.trim() ? doc.icon.trim() : base ? base.icon : "ri-clipboard-line",
      roles,
      manualEntry: typeof doc.manualEntry === "boolean" ? doc.manualEntry : base ? base.manualEntry : true,
      active: typeof doc.active === "boolean" ? doc.active : true,
      builtIn: Boolean(base),
      order: typeof doc.order === "number" && Number.isFinite(doc.order) ? doc.order : base ? base.order : 500,
      createdAt: typeof doc.createdAt === "string" ? doc.createdAt : now,
      updatedAt: typeof doc.updatedAt === "string" ? doc.updatedAt : now,
      createdBy: typeof doc.createdBy === "string" ? doc.createdBy : null,
    });
  }
  return [...byKey.values()].sort((a, b) => a.order - b.order || a.name.localeCompare(b.name));
}

/** Claves (`kind`) que un puesto puede registrar; `manualOnly` deja fuera las que no se capturan a mano (dev). */
export function allowedKindsFor(catalog: readonly ActivityType[], role: string | null | undefined, { manualOnly = false }: { manualOnly?: boolean } = {}): string[] {
  return catalog.filter((t) => isActivityTypeAllowedForRole(t, role) && (!manualOnly || t.manualEntry)).map((t) => t.key);
}

export const ACTIVITY_TYPE_LIMITS = { NAME_MIN: 2, NAME_MAX: 60, DESCRIPTION_MAX: 300, ICON_MAX: 60 } as const;

export type ActivityTypeInput = ActivityTypeSeed & { active: boolean };

/** Validación pura de un tipo del catálogo (`POST /workHoursActivityCatalog`). */
export function validateActivityTypeInput(raw: unknown): { ok: true; value: ActivityTypeInput } | { ok: false; message: string } {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return { ok: false, message: "El tipo debe ser un objeto" };
  const e = raw as Record<string, unknown>;
  const L = ACTIVITY_TYPE_LIMITS;
  const key = typeof e.key === "string" ? e.key.trim().toLowerCase() : "";
  if (!ACTIVITY_TYPE_KEY_RE.test(key)) return { ok: false, message: "key debe ser un slug en minúsculas ([a-z][a-z0-9_]{1,39})" };
  const name = typeof e.name === "string" ? e.name.trim() : "";
  if (name.length < L.NAME_MIN || name.length > L.NAME_MAX) return { ok: false, message: `name debe tener entre ${L.NAME_MIN} y ${L.NAME_MAX} caracteres` };
  const nameEn = typeof e.nameEn === "string" && e.nameEn.trim() ? e.nameEn.trim().slice(0, L.NAME_MAX) : null;
  const description = typeof e.description === "string" && e.description.trim() ? e.description.trim().slice(0, L.DESCRIPTION_MAX) : null;
  const icon = typeof e.icon === "string" && /^ri-[a-z0-9-]+$/.test(e.icon.trim()) ? e.icon.trim().slice(0, L.ICON_MAX) : "ri-clipboard-line";
  let roles: ActivityRole[] | "all" = "all";
  if (e.roles !== undefined && e.roles !== null && e.roles !== "all") {
    if (!Array.isArray(e.roles)) return { ok: false, message: 'roles debe ser "all" o una lista de puestos' };
    const list = [...new Set(e.roles.map((r) => String(r)))];
    const bad = list.find((r) => !(ACTIVITY_ROLES as readonly string[]).includes(r));
    if (bad) return { ok: false, message: `roles: «${bad}» no es un puesto (${ACTIVITY_ROLES.join(", ")})` };
    if (!list.length) return { ok: false, message: "roles no puede quedar vacío: elige al menos un puesto o manda \"all\"" };
    roles = list as ActivityRole[];
  }
  const manualEntry = e.manualEntry === undefined || e.manualEntry === null ? true : Boolean(e.manualEntry);
  const active = e.active === undefined || e.active === null ? true : Boolean(e.active);
  const order = typeof e.order === "number" && Number.isFinite(e.order) ? Math.round(e.order) : 500;
  return { ok: true, value: { key, name, nameEn, description, icon, roles, manualEntry, order, active } };
}

// ---------------------------------------------------------------------------
// Tickets (`work_activities`) y sprints (`work_sprints`) — #1301
// ---------------------------------------------------------------------------

export const ACTIVITY_STATUSES = ["backlog", "todo", "in_progress", "review", "blocked", "done"] as const;
export type ActivityStatus = (typeof ACTIVITY_STATUSES)[number];
/** Estados «en curso» (ni backlog ni hecho): los que pueden quedarse parados. */
export const ACTIVITY_OPEN_STATUSES: readonly ActivityStatus[] = ["todo", "in_progress", "review", "blocked"];

export const ACTIVITY_SOURCES = ["manual", "github_issue", "github_milestone", "github_pr", "service_desk", "crm"] as const;
export type ActivitySource = (typeof ACTIVITY_SOURCES)[number];

export const ACTIVITY_PRIORITIES = ["low", "medium", "high", "urgent"] as const;
export type ActivityPriority = (typeof ACTIVITY_PRIORITIES)[number];

/** Lo que se sabe del issue, milestone o PR de GitHub que es este ticket. */
export interface ActivityGithubRef {
  owner: string;
  repo: string;
  number: number;
  url: string | null;
  state: "open" | "closed";
  isPullRequest: boolean;
  isMilestone: boolean;
  labels: string[];
  milestoneNumber: number | null;
  /** Issue padre (sub-issues de GitHub), si lo tiene. */
  parentNumber: number | null;
  assignees: string[];
  author: string | null;
  updatedAt: string | null;
  closedAt: string | null;
  dueOn: string | null;
  syncedAt: string;
}

/** Prospecto / empresa / negocio del CRM al que pertenece el ticket (reuniones comerciales, #1308). */
export interface ActivityCrmRef {
  leadId: string | null;
  companyId: string | null;
  dealId: string | null;
  contactId: string | null;
  /** Nombre del prospecto para mostrar sin ir al CRM. */
  name: string | null;
  /** Etapa del embudo en el momento de la actividad. */
  stage: string | null;
  /** Actividad del CRM (`crm_activities`) que originó el ticket: idempotencia. */
  crmActivityId: string | null;
}

/** Documento de `work_activities`. */
export interface WorkActivity {
  id: string;
  company_id: string;
  /**
   * Clave legible y única por compañía: `owner/repo#123` (GitHub),
   * `owner/repo!m12` (milestone), `sd:<ticketId>` (Mesa de Servicio),
   * `crm:<crmActivityId>` o `manual:<projectId>:<slug>` / `CC-123` (manuales).
   */
  key: string;
  title: string;
  description: string | null;
  /** Tipo del catálogo (`work_activity_catalog.key`). */
  typeKey: string;
  status: ActivityStatus;
  /** Último cambio de estado: con él se sabe desde cuándo está parado. */
  statusChangedAt: string;
  source: ActivitySource;
  github: ActivityGithubRef | null;
  crm: ActivityCrmRef | null;
  /**
   * #1419: ticket de la Mesa de Servicio que trae este issue de GitHub en su
   * `githubIssue`. Lo pone la sincronización; ausente o `null` ⇒ ninguno.
   */
  serviceDesk?: { ticketId: string; title: string | null } | null;
  /** Épica (milestone o issue padre) a la que pertenece. */
  parentId: string | null;
  /** Milestone o ticket con hijos: se pinta en el roadmap. */
  isEpic: boolean;
  /** Proyecto de Horas (slug del catálogo de proyectos) y repo al que pertenece. */
  projectId: string | null;
  repo: string | null;
  assigneeTeamMemberId: string | null;
  assigneeUserId: string | null;
  assigneeName: string | null;
  sprintId: string | null;
  storyPoints: number | null;
  estimateMinutes: number | null;
  /** Fechas locales `YYYY-MM-DD`. */
  startDate: string | null;
  dueDate: string | null;
  priority: ActivityPriority;
  /** Minutos registrados que cuentan (sin anuladas ni pendientes/rechazadas), denormalizados de `work_time_entries`. */
  loggedMinutes: number;
  archived: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
}

export const SPRINT_STATUSES = ["planned", "active", "closed"] as const;
export type SprintStatus = (typeof SPRINT_STATUSES)[number];

/** Documento de `work_sprints`. Solo UN sprint activo por compañía. */
export interface WorkSprint {
  id: string;
  company_id: string;
  name: string;
  goal: string | null;
  startDate: string;
  endDate: string;
  status: SprintStatus;
  createdAt: string;
  updatedAt: string;
  startedAt: string | null;
  closedAt: string | null;
  createdBy: string | null;
}

export const ACTIVITY_LIMITS = {
  TITLE_MIN: 3,
  TITLE_MAX: 200,
  DESCRIPTION_MAX: 5000,
  KEY_MAX: 160,
  POINTS_MAX: 100,
  /** 600 h por ticket, en minutos. */
  ESTIMATE_MAX_MINUTES: 36000,
  SPRINT_NAME_MIN: 2,
  SPRINT_NAME_MAX: 80,
  SPRINT_GOAL_MAX: 500,
  /** Un sprint dura entre 1 día y 8 semanas. */
  SPRINT_MAX_DAYS: 56,
  PAGE_DEFAULT: 500,
  PAGE_MAX: 2000,
} as const;

/** Días sin cambio de estado a partir de los cuales un ticket en curso está «parado». */
export const ACTIVITY_STUCK_DAYS = 3;

/** Clave idempotente de un ticket manual/externo: `manual:cc-console:daily-equipo`, `sd:abc`, `crm:xyz`. */
export const ACTIVITY_KEY_RE = /^[A-Za-z0-9][A-Za-z0-9:_./#!-]{1,159}$/;

/** Referencia a un issue/PR de GitHub: `owner/repo#123` o su URL. */
export function parseGithubRef(raw: unknown): { owner: string; repo: string; number: number } | null {
  if (typeof raw !== "string") return null;
  const s = raw.trim();
  let m = s.match(/^([A-Za-z0-9_.-]+)\/([A-Za-z0-9_.-]+)#(\d{1,7})$/);
  if (!m) m = s.match(/^https?:\/\/github\.com\/([A-Za-z0-9_.-]+)\/([A-Za-z0-9_.-]+)\/(?:issues|pull)\/(\d{1,7})(?:[/?#].*)?$/);
  if (!m) return null;
  return { owner: m[1], repo: m[2], number: Number(m[3]) };
}

export const githubRefKey = (ref: { owner: string; repo: string; number: number }): string => `${ref.owner}/${ref.repo}#${ref.number}`;
export const githubMilestoneKey = (ref: { owner: string; repo: string; number: number }): string => `${ref.owner}/${ref.repo}!m${ref.number}`;

/** Id del documento de un ticket a partir de su clave (Firestore no admite `/` en el id). */
export function activityDocId(companyId: string, key: string): string {
  const safe = String(key)
    .trim()
    .toLowerCase()
    .replace(/\//g, "__")
    .replace(/[^a-z0-9#_.:!-]/g, "-");
  return `${companyId}_${safe}`;
}

/** Fuentes de entrada que DEBEN venir ligadas a un ticket (#1303). `admin` (cargas) y `crm` (reuniones) ya traen el suyo o están exentas. */
export const ACTIVITY_REQUIRED_SOURCES: readonly string[] = ["ui", "mcp", "skill"];
export const requiresActivity = (source: string | null | undefined): boolean => ACTIVITY_REQUIRED_SOURCES.includes(String(source ?? ""));

export interface ActivityInput {
  title: string;
  description: string | null;
  typeKey: string;
  status: ActivityStatus | null;
  assigneeTeamMemberId: string | null;
  sprintId: string | null;
  storyPoints: number | null;
  estimateMinutes: number | null;
  startDate: string | null;
  dueDate: string | null;
  priority: ActivityPriority | null;
  parentId: string | null;
  projectId: string | null;
  repo: string | null;
  /** Clave idempotente (plugin / integraciones). Sin ella el servidor asigna `CC-n`. */
  key: string | null;
  /** `owner/repo#n`: liga (o crea) el ticket de ese issue/PR. */
  githubRef: { owner: string; repo: string; number: number } | null;
  crm: ActivityCrmRef | null;
  /** Épica propia (milestone del equipo): aparece en el roadmap aunque aún no tenga hijos. */
  isEpic: boolean | null;
}

const optionalText = (v: unknown, max: number): string | null => (typeof v === "string" && v.trim() ? v.trim().slice(0, max) : null);
const optionalDate = (v: unknown): { ok: true; value: string | null } | { ok: false } =>
  v === undefined || v === null || v === "" ? { ok: true, value: null } : isValidLocalDate(v) ? { ok: true, value: v } : { ok: false };

/**
 * Validación pura de un ticket (`POST /workHoursActivities`, `PATCH /workHoursActivity/:id`).
 * `catalogKeys`: claves válidas del catálogo de la compañía (activas). Con
 * `partial` se aceptan solo los campos presentes (PATCH).
 */
export function validateActivityInput(
  raw: unknown,
  { catalogKeys, partial = false }: { catalogKeys: readonly string[]; partial?: boolean },
): { ok: true; value: Partial<ActivityInput> } | { ok: false; message: string } {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return { ok: false, message: "La actividad debe ser un objeto" };
  const e = raw as Record<string, unknown>;
  const L = ACTIVITY_LIMITS;
  const out: Partial<ActivityInput> = {};
  const has = (k: string) => e[k] !== undefined;

  if (!partial || has("title")) {
    const title = typeof e.title === "string" ? e.title.trim().replace(/\s+/g, " ") : "";
    if (title.length < L.TITLE_MIN || title.length > L.TITLE_MAX) return { ok: false, message: `title debe tener entre ${L.TITLE_MIN} y ${L.TITLE_MAX} caracteres` };
    out.title = title;
  }
  if (!partial || has("typeKey")) {
    const typeKey = typeof e.typeKey === "string" ? e.typeKey.trim().toLowerCase() : "";
    if (!ACTIVITY_TYPE_KEY_RE.test(typeKey)) return { ok: false, message: "typeKey es obligatorio: una clave del catálogo de actividades" };
    if (!catalogKeys.includes(typeKey)) return { ok: false, message: `typeKey «${typeKey}» no está en el catálogo de actividades (${catalogKeys.join(", ")})` };
    out.typeKey = typeKey;
  }
  if (has("description")) {
    if (e.description !== null && typeof e.description !== "string") return { ok: false, message: "description debe ser texto" };
    out.description = optionalText(e.description, L.DESCRIPTION_MAX);
  }
  if (has("status")) {
    if (e.status !== null && !(ACTIVITY_STATUSES as readonly unknown[]).includes(e.status)) return { ok: false, message: `status debe ser uno de: ${ACTIVITY_STATUSES.join(", ")}` };
    out.status = (e.status as ActivityStatus) || null;
  }
  if (has("priority")) {
    if (e.priority !== null && !(ACTIVITY_PRIORITIES as readonly unknown[]).includes(e.priority)) return { ok: false, message: `priority debe ser uno de: ${ACTIVITY_PRIORITIES.join(", ")}` };
    out.priority = (e.priority as ActivityPriority) || null;
  }
  for (const k of ["assigneeTeamMemberId", "sprintId", "parentId"] as const) {
    if (!has(k)) continue;
    if (e[k] !== null && (typeof e[k] !== "string" || String(e[k]).length > 200)) return { ok: false, message: `${k} debe ser un id` };
    out[k] = optionalText(e[k], 200);
  }
  if (has("storyPoints")) {
    if (e.storyPoints !== null && (typeof e.storyPoints !== "number" || !Number.isFinite(e.storyPoints) || e.storyPoints < 0 || e.storyPoints > L.POINTS_MAX)) {
      return { ok: false, message: `storyPoints debe ser un número entre 0 y ${L.POINTS_MAX}` };
    }
    out.storyPoints = e.storyPoints === null ? null : Math.round(Number(e.storyPoints) * 2) / 2;
  }
  if (has("estimateMinutes")) {
    if (e.estimateMinutes !== null && (!Number.isInteger(e.estimateMinutes) || Number(e.estimateMinutes) < 0 || Number(e.estimateMinutes) > L.ESTIMATE_MAX_MINUTES)) {
      return { ok: false, message: `estimateMinutes debe ser un entero entre 0 y ${L.ESTIMATE_MAX_MINUTES}` };
    }
    out.estimateMinutes = e.estimateMinutes === null ? null : Number(e.estimateMinutes);
  }
  for (const k of ["startDate", "dueDate"] as const) {
    if (!has(k)) continue;
    const d = optionalDate(e[k]);
    if (!d.ok) return { ok: false, message: `${k} debe ser una fecha local YYYY-MM-DD` };
    out[k] = d.value;
  }
  if (out.startDate && out.dueDate && out.startDate > out.dueDate) return { ok: false, message: "startDate no puede ser posterior a dueDate" };
  if (has("projectId")) {
    if (e.projectId !== null && (typeof e.projectId !== "string" || !PROJECT_ID_RE.test(e.projectId.trim()))) return { ok: false, message: "projectId debe ser un slug en minúsculas" };
    out.projectId = optionalText(e.projectId, TIME_ENTRY_LIMITS.PROJECT_ID_MAX);
  }
  if (has("repo")) {
    if (e.repo !== null && (typeof e.repo !== "string" || !TIME_ENTRY_REPO_RE.test(e.repo.trim()))) return { ok: false, message: "repo debe tener formato owner/nombre" };
    out.repo = optionalText(e.repo, 200);
  }
  if (has("key")) {
    if (e.key !== null && (typeof e.key !== "string" || !ACTIVITY_KEY_RE.test(e.key.trim()))) return { ok: false, message: "key debe ser una clave corta ([A-Za-z0-9:_./#!-], máx. 160)" };
    out.key = optionalText(e.key, L.KEY_MAX);
  }
  if (has("githubRef")) {
    if (e.githubRef !== null) {
      const ref = parseGithubRef(e.githubRef);
      if (!ref) return { ok: false, message: "githubRef debe ser owner/repo#n o la URL del issue/PR" };
      out.githubRef = ref;
    } else out.githubRef = null;
  }
  if (has("isEpic")) {
    if (e.isEpic !== null && typeof e.isEpic !== "boolean") return { ok: false, message: "isEpic debe ser true o false" };
    out.isEpic = e.isEpic === null ? null : Boolean(e.isEpic);
  }
  if (has("crm")) {
    if (e.crm !== null) {
      if (typeof e.crm !== "object" || Array.isArray(e.crm)) return { ok: false, message: "crm debe ser un objeto" };
      const c = e.crm as Record<string, unknown>;
      out.crm = {
        leadId: optionalText(c.leadId, 200),
        companyId: optionalText(c.companyId, 200),
        dealId: optionalText(c.dealId, 200),
        contactId: optionalText(c.contactId, 200),
        name: optionalText(c.name, 200),
        stage: optionalText(c.stage, 80),
        crmActivityId: optionalText(c.crmActivityId, 200),
      };
    } else out.crm = null;
  }
  return { ok: true, value: out };
}

export interface SprintInput {
  name: string;
  goal: string | null;
  startDate: string;
  endDate: string;
}

/** Validación pura de un sprint (`POST /workHoursSprints`, `PATCH /workHoursSprint/:id`). */
export function validateSprintInput(raw: unknown, { partial = false }: { partial?: boolean } = {}): { ok: true; value: Partial<SprintInput> } | { ok: false; message: string } {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return { ok: false, message: "El sprint debe ser un objeto" };
  const e = raw as Record<string, unknown>;
  const L = ACTIVITY_LIMITS;
  const out: Partial<SprintInput> = {};
  if (!partial || e.name !== undefined) {
    const name = typeof e.name === "string" ? e.name.trim().replace(/\s+/g, " ") : "";
    if (name.length < L.SPRINT_NAME_MIN || name.length > L.SPRINT_NAME_MAX) return { ok: false, message: `name debe tener entre ${L.SPRINT_NAME_MIN} y ${L.SPRINT_NAME_MAX} caracteres` };
    out.name = name;
  }
  if (e.goal !== undefined) out.goal = optionalText(e.goal, L.SPRINT_GOAL_MAX);
  if (!partial || e.startDate !== undefined) {
    if (!isValidLocalDate(e.startDate)) return { ok: false, message: "startDate debe ser una fecha local YYYY-MM-DD" };
    out.startDate = e.startDate;
  }
  if (!partial || e.endDate !== undefined) {
    if (!isValidLocalDate(e.endDate)) return { ok: false, message: "endDate debe ser una fecha local YYYY-MM-DD" };
    out.endDate = e.endDate;
  }
  if (out.startDate && out.endDate) {
    if (out.startDate > out.endDate) return { ok: false, message: "startDate no puede ser posterior a endDate" };
    if (daysBetweenLocal(out.startDate, out.endDate) > L.SPRINT_MAX_DAYS) return { ok: false, message: `Un sprint dura como máximo ${L.SPRINT_MAX_DAYS} días` };
  }
  return { ok: true, value: out };
}

/** Días calendario entre dos fechas locales (inclusive el primero, exclusivo el último). */
export function daysBetweenLocal(from: string, to: string): number {
  const a = Date.UTC(Number(from.slice(0, 4)), Number(from.slice(5, 7)) - 1, Number(from.slice(8, 10)));
  const b = Date.UTC(Number(to.slice(0, 4)), Number(to.slice(5, 7)) - 1, Number(to.slice(8, 10)));
  return Math.round((b - a) / 86400000);
}

/** Costo de un ticket (solo dueños): lo estimado y lo real con la tarifa de la persona asignada. */
export interface ActivityCost {
  hourlyCost: number;
  currency: string;
  basis: HourlyCostBasis;
  /** `null` sin estimación. */
  estimated: number | null;
  actual: number;
}

export function activityCost(activity: Pick<WorkActivity, "estimateMinutes" | "loggedMinutes">, rate: HourlyCost | null | undefined): ActivityCost | null {
  if (!rate) return null;
  const money = (minutes: number) => Math.round((minutes / 60) * rate.hourlyCost * 100) / 100;
  return {
    hourlyCost: Math.round(rate.hourlyCost * 100) / 100,
    currency: rate.currency,
    basis: rate.basis,
    estimated: activity.estimateMinutes === null || activity.estimateMinutes === undefined ? null : money(activity.estimateMinutes),
    actual: money(Number(activity.loggedMinutes) || 0),
  };
}

/** Días que lleva un ticket en curso sin cambiar de estado; `null` si no está en curso. */
export function stuckDays(activity: Pick<WorkActivity, "status" | "statusChangedAt" | "updatedAt">, nowIso: string): number | null {
  if (!ACTIVITY_OPEN_STATUSES.includes(activity.status)) return null;
  const since = Date.parse(activity.statusChangedAt || activity.updatedAt || "");
  if (!Number.isFinite(since)) return null;
  return Math.max(0, Math.floor((Date.parse(nowIso) - since) / 86400000));
}

export const isActivityStuck = (activity: Pick<WorkActivity, "status" | "statusChangedAt" | "updatedAt">, nowIso: string, threshold: number = ACTIVITY_STUCK_DAYS): boolean => {
  const d = stuckDays(activity, nowIso);
  return d !== null && d >= threshold;
};

/**
 * Estado de un ticket de GitHub al sincronizar. Lo que GitHub sabe manda sobre
 * lo cerrado (hecho) y lo abierto con PR (revisión); un estado local «en curso»
 * (in_progress, review, blocked) se respeta mientras el issue siga abierto —una
 * tarjeta que alguien arrastró a Revisión no vuelve sola a Por hacer—; lo demás
 * se deriva de si tiene asignado.
 */
export function githubStatusFor(gh: Pick<ActivityGithubRef, "state" | "assignees">, local: ActivityStatus | null, { hasOpenPr = false, inSprint = false }: { hasOpenPr?: boolean; inSprint?: boolean } = {}): ActivityStatus {
  if (gh.state === "closed") return "done";
  if (hasOpenPr) return "review";
  if (local === "in_progress" || local === "blocked" || local === "review") return local;
  if (local === "todo") return "todo";
  return gh.assignees.length || inSprint ? "todo" : "backlog";
}

// ---------------------------------------------------------------------------
// REVISIÓN DE HORAS POR COLABORADOR EN NÓMINA (#1346)
// ---------------------------------------------------------------------------
// La tabla de «Autorización de incidencias»: por colaborador, cuánto registró,
// cuánto de eso está justificado, cuánto espera aprobación y cuánto no cuenta,
// contra lo que se esperaba de él a la fecha.
//
// NO se inventa una definición de «justificadas». Es la de las pérdidas de
// Gastos (#1177, `payrollHoursJustification`): las horas que CUENTAN en Horas
// Trabajadas (`summary.days`, regla de #783/#960/#1281):
//
//   • entradas de tiempo vigentes que no necesitaron aprobación o ya fueron
//     aprobadas, y
//   • en los días SIN entradas que cuenten, el estimado por bloques de commits.
//
// Lo demás que la persona registró se separa, nunca se mezcla:
//
//   • Por aprobar  = entradas manuales de más de 1 h que esperan (#1281).
//   • No justificadas = entradas RECHAZADAS: se registraron y no cuentan.
//   • Registrado = justificadas de entradas + por aprobar + no justificadas
//     (las anuladas no existen para nadie).
//
// Diferencia = justificadas − esperadas a la fecha (#969). Negativa ⇒ pérdida
// (horas pagadas que nadie justificó); positiva ⇒ ganancia (horas de más). Quien
// no tiene esperado (no se mide o no tiene asignaciones) queda sin diferencia:
// «no sé» no es «perdí», igual que en Gastos.
//
// Las cuentas ligadas a la misma ficha del módulo Equipo suman en UNA fila (como
// en Gastos). El dinero va aparte (`computePayrollHoursReviewCost`): lleva
// sueldos y solo lo arma el servidor para los dueños.
// ---------------------------------------------------------------------------

/** `GET` de la revisión por colaborador (fuera de `WORK_HOURS_ENDPOINTS` para no chocar con otras ramas). */
export const PAYROLL_HOURS_REVIEW_ENDPOINT = "/workHoursPayrollReview";

/** Rango máximo (días, ambos extremos inclusive) que acepta la revisión: el tope de lectura de Horas. */
export const PAYROLL_HOURS_REVIEW_MAX_DAYS = 366;

/** Entrada de tiempo con lo que necesita la revisión (el repo es de #1060). */
export type PayrollReviewEntry = CountableTimeEntry & { repo?: string | null };

/** Minutos de una persona en un repo. `repo: ""` = sin repositorio (o soporte, en días estimados). */
export interface PayrollHoursRepoLine {
  repo: string;
  /** Minutos que cuentan en el repo: entradas + estimado. */
  justifiedMinutes: number;
  /** Parte de `justifiedMinutes` que viene de entradas de tiempo. */
  entryMinutes: number;
  /** Parte de `justifiedMinutes` estimada por commits. */
  estimatedMinutes: number;
  pendingMinutes: number;
  unjustifiedMinutes: number;
}

export type PayrollHoursOutcome = "loss" | "gain" | "even" | "none";

export interface PayrollHoursReviewRow {
  /** Ficha del módulo Equipo, o `user:<userId>` si la cuenta no está ligada. */
  key: string;
  teamMemberId: string | null;
  userIds: string[];
  name: string;
  /** Σ de sus entradas de tiempo no anuladas: cuentan + por aprobar + rechazadas. */
  registeredMinutes: number;
  /** Entradas que cuentan (sin aprobación o aprobadas). */
  entryMinutes: number;
  /** Días sin entradas que cuenten, estimados por commits. */
  estimatedMinutes: number;
  /** Lo que cuenta: `entryMinutes + estimatedMinutes` (la definición de Gastos, #1177). */
  justifiedMinutes: number;
  /** Entradas que esperan aprobación (#1281). */
  pendingMinutes: number;
  /** Entradas rechazadas: registradas y sin contar. */
  unjustifiedMinutes: number;
  /** Esperado a la fecha (#969); `null` si la persona no tiene esperado. */
  expectedHours: number | null;
  /** justificadas − esperadas; `null` sin esperado. */
  diffHours: number | null;
  outcome: PayrollHoursOutcome;
  byRepo: PayrollHoursRepoLine[];
}

export interface PayrollHoursReview {
  rows: PayrollHoursReviewRow[];
  totals: {
    registeredMinutes: number;
    entryMinutes: number;
    estimatedMinutes: number;
    justifiedMinutes: number;
    pendingMinutes: number;
    unjustifiedMinutes: number;
    /** Σ del esperado de quienes lo tienen. */
    expectedHours: number;
    /** Σ por persona de las horas faltantes (≥ 0): lo de más de uno no tapa lo de otro. */
    lossHours: number;
    /** Σ por persona de las horas de más (≥ 0). */
    gainHours: number;
  };
}

export interface PayrollHoursReviewInput {
  /** Entradas del rango, cuenten o no (las anuladas se ignoran). */
  entries: PayrollReviewEntry[];
  /** Días-persona ya resueltos con la regla de conteo (`summary.days`). */
  days: ResolvedDay[];
  /** Esperado a la fecha del mismo rango (`summary.expected`). */
  expected?: Pick<ExpectedToDate, "byPerson"> | null;
  identities: WorkHoursIdentity[];
  members: ExpectedMemberInput[];
  from?: string | null;
  to?: string | null;
}

/** Revisión de horas por colaborador (#1346). Función pura. */
export function computePayrollHoursReview(input: PayrollHoursReviewInput): PayrollHoursReview {
  const inRange = (d: string) => (!input.from || d >= input.from) && (!input.to || d <= input.to);
  const identityOf = new Map((input.identities ?? []).filter((i) => i && i.userId).map((i) => [i.userId, i]));
  const membersById = new Map((input.members ?? []).filter((m) => m && m.id).map((m) => [m.id, m]));

  type Acc = PayrollHoursReviewRow & { repos: Map<string, PayrollHoursRepoLine>; fallbackName: string | null };
  const rows = new Map<string, Acc>();
  const rowOf = (userId: string, userName?: string | null): Acc => {
    const identity = identityOf.get(userId);
    const member = identity?.teamMemberId ? membersById.get(identity.teamMemberId) : undefined;
    const key = member ? member.id : `user:${userId}`;
    let row = rows.get(key);
    if (!row) {
      row = {
        key,
        teamMemberId: member ? member.id : null,
        userIds: [],
        name: member ? memberName(member) : "",
        registeredMinutes: 0,
        entryMinutes: 0,
        estimatedMinutes: 0,
        justifiedMinutes: 0,
        pendingMinutes: 0,
        unjustifiedMinutes: 0,
        expectedHours: null,
        diffHours: null,
        outcome: "none",
        byRepo: [],
        repos: new Map(),
        fallbackName: null,
      };
      rows.set(key, row);
    }
    if (!row.userIds.includes(userId)) row.userIds.push(userId);
    row.fallbackName ??= identity?.userName || identity?.email || userName || null;
    return row;
  };
  const repoOf = (row: Acc, repo: string | null | undefined): PayrollHoursRepoLine => {
    const key = String(repo ?? "");
    let line = row.repos.get(key);
    if (!line) {
      line = { repo: key, justifiedMinutes: 0, entryMinutes: 0, estimatedMinutes: 0, pendingMinutes: 0, unjustifiedMinutes: 0 };
      row.repos.set(key, line);
    }
    return line;
  };

  for (const e of input.entries ?? []) {
    if (!e || e.voided || !(Number(e.minutes) > 0) || !inRange(e.date)) continue;
    const minutes = Number(e.minutes);
    const row = rowOf(e.userId);
    const line = repoOf(row, e.repo);
    row.registeredMinutes += minutes;
    if (isPendingEntry(e)) {
      row.pendingMinutes += minutes;
      line.pendingMinutes += minutes;
    } else if (isRejectedEntry(e)) {
      row.unjustifiedMinutes += minutes;
      line.unjustifiedMinutes += minutes;
    } else {
      row.entryMinutes += minutes;
      line.entryMinutes += minutes;
      line.justifiedMinutes += minutes;
    }
  }
  // Los días estimados salen ya resueltos: aquí solo se reparten por repo.
  for (const d of input.days ?? []) {
    if (!d || !d.estimated || !(d.minutes > 0) || !inRange(d.date)) continue;
    const row = rowOf(d.userId, d.userName);
    row.estimatedMinutes += d.minutes;
    const byRepo = Object.entries(d.byRepo ?? {}).filter(([, m]) => m > 0);
    let spread = 0;
    for (const [repo, m] of byRepo) {
      const line = repoOf(row, repo === NO_REPO_KEY ? "" : repo);
      line.estimatedMinutes += m;
      line.justifiedMinutes += m;
      spread += m;
    }
    // Lo que el estimado no atribuye a un repo no se pierde: va a «sin repositorio».
    if (d.minutes > spread) {
      const line = repoOf(row, "");
      line.estimatedMinutes += d.minutes - spread;
      line.justifiedMinutes += d.minutes - spread;
    }
  }
  // Quien tiene esperado sale aunque no haya registrado nada: es justo a quien hay que ver.
  for (const p of input.expected?.byPerson ?? []) {
    const row = rowOf(p.userId, p.name);
    row.expectedHours = round2((row.expectedHours ?? 0) + p.expectedHours);
  }

  const out: PayrollHoursReviewRow[] = [];
  for (const { repos, fallbackName, ...row } of rows.values()) {
    row.name = row.name || fallbackName || row.userIds[0] || row.key;
    row.justifiedMinutes = row.entryMinutes + row.estimatedMinutes;
    if (row.expectedHours !== null) {
      row.diffHours = round2(minutesToHours(row.justifiedMinutes) - row.expectedHours);
      row.outcome = row.diffHours < 0 ? "loss" : row.diffHours > 0 ? "gain" : "even";
    }
    row.byRepo = [...repos.values()].sort(
      (a, b) => b.justifiedMinutes + b.pendingMinutes - (a.justifiedMinutes + a.pendingMinutes) || a.repo.localeCompare(b.repo),
    );
    row.userIds.sort();
    out.push(row);
  }
  // Primero la mayor pérdida; quien no tiene esperado, al final.
  out.sort((a, b) => (a.diffHours ?? Infinity) - (b.diffHours ?? Infinity) || a.name.localeCompare(b.name));
  return { rows: out, totals: payrollHoursReviewTotals(out) };
}

/**
 * Totales de unas filas de la revisión. Aparte porque el servidor recorta las
 * filas al alcance de quien consulta (su squad, o solo él) y los totales deben
 * ser de lo que SÍ ve, no del equipo entero.
 */
export function payrollHoursReviewTotals(rows: PayrollHoursReviewRow[]): PayrollHoursReview["totals"] {
  const totals: PayrollHoursReview["totals"] = { registeredMinutes: 0, entryMinutes: 0, estimatedMinutes: 0, justifiedMinutes: 0, pendingMinutes: 0, unjustifiedMinutes: 0, expectedHours: 0, lossHours: 0, gainHours: 0 };
  for (const row of rows ?? []) {
    totals.registeredMinutes += row.registeredMinutes;
    totals.entryMinutes += row.entryMinutes;
    totals.estimatedMinutes += row.estimatedMinutes;
    totals.justifiedMinutes += row.justifiedMinutes;
    totals.pendingMinutes += row.pendingMinutes;
    totals.unjustifiedMinutes += row.unjustifiedMinutes;
    if (row.expectedHours === null || row.diffHours === null) continue;
    totals.expectedHours = round2(totals.expectedHours + row.expectedHours);
    if (row.diffHours < 0) totals.lossHours = round2(totals.lossHours - row.diffHours);
    else totals.gainHours = round2(totals.gainHours + row.diffHours);
  }
  return totals;
}

/** Importes de una fila de la revisión. Llevan sueldos: SOLO para los dueños. */
export interface PayrollHoursReviewCostLine {
  key: string;
  hourlyCost: number;
  currency: string;
  basis: HourlyCostBasis;
  currencyAssumed: boolean;
  /** Lo que valen las horas justificadas. */
  justifiedCost: number;
  /** Lo que valdrían las horas por aprobar. */
  pendingCost: number;
  /** Lo que valen las rechazadas (registradas que no cuentan). */
  unjustifiedCost: number;
  /** Horas faltantes × costo (≥ 0); 0 sin esperado. */
  lossCost: number;
  /** Horas de más × costo (≥ 0); 0 sin esperado. */
  gainCost: number;
}

export interface PayrollHoursReviewCost {
  byRow: Record<string, PayrollHoursReviewCostLine>;
  /** Totales por moneda; nunca se suman entre monedas (#741). */
  byCurrency: Record<string, { justifiedCost: number; pendingCost: number; unjustifiedCost: number; lossCost: number; gainCost: number }>;
  /** Filas sin sueldo registrado (o sin ficha): no suman importe. */
  unpriced: { key: string; name: string }[];
}

/**
 * Dinero de la revisión (#1346): mismas tarifas que el tablero (`hourlyCostOf`).
 * La pérdida es horas faltantes × costo por hora de la persona, igual que
 * `computeExpectedCost`. Función pura; quién la recibe lo decide el servidor.
 */
export function computePayrollHoursReviewCost(rows: PayrollHoursReviewRow[], members: ExpectedMemberInput[]): PayrollHoursReviewCost {
  const membersById = new Map((members ?? []).filter((m) => m && m.id).map((m) => [m.id, m]));
  const byRow: PayrollHoursReviewCost["byRow"] = {};
  const byCurrency: PayrollHoursReviewCost["byCurrency"] = {};
  const unpriced: PayrollHoursReviewCost["unpriced"] = [];
  for (const row of rows ?? []) {
    const rate = row.teamMemberId ? hourlyCostOf(membersById.get(row.teamMemberId)) : null;
    if (!rate) {
      unpriced.push({ key: row.key, name: row.name });
      continue;
    }
    const cost = (minutes: number) => round2((minutes / 60) * rate.hourlyCost);
    const diff = row.diffHours ?? 0;
    const line: PayrollHoursReviewCostLine = {
      key: row.key,
      ...rate,
      hourlyCost: round2(rate.hourlyCost),
      justifiedCost: cost(row.justifiedMinutes),
      pendingCost: cost(row.pendingMinutes),
      unjustifiedCost: cost(row.unjustifiedMinutes),
      lossCost: diff < 0 ? round2(-diff * rate.hourlyCost) : 0,
      gainCost: diff > 0 ? round2(diff * rate.hourlyCost) : 0,
    };
    byRow[row.key] = line;
    const acc = (byCurrency[rate.currency] ??= { justifiedCost: 0, pendingCost: 0, unjustifiedCost: 0, lossCost: 0, gainCost: 0 });
    acc.justifiedCost = round2(acc.justifiedCost + line.justifiedCost);
    acc.pendingCost = round2(acc.pendingCost + line.pendingCost);
    acc.unjustifiedCost = round2(acc.unjustifiedCost + line.unjustifiedCost);
    acc.lossCost = round2(acc.lossCost + line.lossCost);
    acc.gainCost = round2(acc.gainCost + line.gainCost);
  }
  return { byRow, byCurrency, unpriced };
}

export const WORK_HOURS_ENDPOINTS = {
  commits: "/workHoursCommits",
  summary: "/workHoursSummary",
  // #1344: las líneas detrás de cada cifra del tablero.
  detail: "/workHoursDetail",
  entries: "/workHoursEntries",
  me: "/workHoursMe",
  identity: "/workHoursIdentity",
  identityList: "/workHoursIdentityList",
  identityAdmin: "/workHoursIdentityAdmin",
  unmatched: "/workHoursUnmatched",
  clientList: "/workHoursClientList",
  client: "/workHoursClient",
  // Tiempo, costo, ingreso y ganancia por cliente (#1064): solo dueños.
  clientEconomics: "/workHoursClientEconomics",
  run: "/workHoursRun",
  runList: "/workHoursRunList",
  reportDaily: "/workHoursReportDaily",
  reportWeekly: "/workHoursReportWeekly",
  allocations: "/workHoursAllocations",
  deliveryList: "/workHoursDeliveryList",
  // Entradas de tiempo en minutos (#782)
  log: "/workHoursLog",
  logBatch: "/workHoursLogBatch",
  timeEntries: "/workHoursTimeEntries",
  projects: "/workHoursProjects",
  evidence: "/workHoursEvidence",
  entryVoid: "/workHoursEntryVoid",
  /** #1281: aprobación del tech lead de las entradas manuales de más de 1 h. */
  approvalAccess: "/workHoursApprovalAccess",
  approvals: "/workHoursApprovals",
  entryApprove: "/workHoursEntryApprove",
  entryReject: "/workHoursEntryReject",
  // Ligar identidades (#841 / #842)
  identityPrelink: "/workHoursIdentityPrelink",
  identityClaim: "/workHoursIdentityClaim",
  identityTracking: "/workHoursIdentityTracking",
  githubConnect: "/workHoursGithubConnect",
  githubCallback: "/workHoursGithubCallback",
  githubComplete: "/workHoursGithubComplete",
  // Actividades (épica #1299): catálogo por puesto, tickets, sprints, GitHub.
  /** GET (catálogo efectivo + lo permitido para mi puesto) · POST (alta/edición, dueños) · DELETE /:key */
  activityCatalog: "/workHoursActivityCatalog",
  /** GET lista (filtros) · POST alta (idempotente por `key`/`githubRef`) */
  activities: "/workHoursActivities",
  /** PATCH /:id edición · DELETE /:id archivar (dueños) */
  activity: "/workHoursActivity",
  /** POST { id, status } (Kanban) */
  activityStatus: "/workHoursActivityStatus",
  /** POST sincronizar GitHub (dueños) · GET estado de la última sincronización */
  activitiesSync: "/workHoursActivitiesSync",
  /** GET insights (por estado/tipo/persona, parados, vencidos, sprint) */
  activitiesInsights: "/workHoursActivitiesInsights",
  /** GET roadmap (épicas con avance) */
  activitiesRoadmap: "/workHoursActivitiesRoadmap",
  /** GET lista · POST alta */
  sprints: "/workHoursSprints",
  /** PATCH /:id (nombre, meta, fechas, status: planned|active|closed) */
  sprint: "/workHoursSprint",
  /** #1404 · GET gente del equipo para elegir asistentes de una reunión (id, nombre, puesto; sin sueldos) */
  people: "/workHoursPeople",
} as const;

export type WorkHoursEndpoint = (typeof WORK_HOURS_ENDPOINTS)[keyof typeof WORK_HOURS_ENDPOINTS];

export const TORRE_WORK_HOURS_VERSION = "0.1.0";
